import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import moment from 'moment';
import { ResponsiveBar } from '@nivo/bar';
import { ChartTooltip } from './ChartTooltip';
import DeduplicatedTagsList from './DeduplicatedTagsList';
import {
    BaseDashboardProps,
    HistogramViewOption,
    CallDataRecord,
    TransferDataRecord,
    SortConfig,
    calculateTransferTotals,
    getUnsupportedTransferCounts,
    transformEngagementsToCallRecords,
    sortDataRecords,
    toggleSort,
    callRecordsToTranscripts,
    transferRecordsToTranscripts,
    LocationSummary,
    aggregateCallsByLocation,
    sortLocationSummaries,
    isSpanishEngagement
} from './dashboardTypes';
import { isNonContainedContainableCall } from './callContainment';
import {
    OutboundLocationSummary,
    aggregateOutboundLocationSummaries,
    filterRecordsToLiveApps,
    sortOutboundLocationSummaries
} from './dashboardOutboundLocationUtils';

// Styled components
import {
    DashboardGrid,
    ChartTile,
    TallChartTile,
    SmallNumberTile,
    WideChartTile,
    TileHeader,
    TileTitle,
    ChartContainer,
    TallChartContainer,
    ChartLoadingOverlay,
    EmptyStateMessage,
    NumberLoadingWrapper,
    NumberDisplay,
    NoSubtextNumberDisplay,
    BigNumber,
    NumberSubtext,
    CallDataModalContent,
    CallDataModalHeader,
    CallDataModalTitleSection,
    CallDataModalTitle,
    CallDataDateRange,
    CallDataModalCloseButton,
    CallDataScrollContainer,
    CallDataTableEmptyOverlay,
    CallDataTable,
    CallDataTh,
    CallDataRow,
    CallDataTd,
    TranscriptLink,
    TagsContainer,
    CallTag,
    CallDataModalFooter,
    ExportInfoText,
    ExportButton,
    ButtonSpinner,
    SearchInputWrapper,
    FilterSearchInput,
    ClearSearchButton,
    ClearFilterLink,
    FilterActionsContainer,
    ResultsInfo,
    FilterableColumnHeader,
    ColumnHeaderContent,
    ColumnHeaderText,
    ColumnFilterIcon,
    SortIcon,
    ColumnFilterDropdown,
    ColumnFilterHeader,
    ColumnFilterContent,
    ColumnFilterItem,
    ColumnFilterCheckbox,
    PhoneFilterSection,
    PhoneFilterModeToggle,
    PhoneFilterModeButton,
    PhoneFilterList,
    PhoneFilterTag,
    PhoneFilterTagRemove,
    SimpleFilterControlsContainer,
    SimpleFilterRow,
    CallSearchFilters,
    ContainmentFilterPill,
    TruncatedLocationText,
    HideOnMobile,
    ViewDetailsButton,
    TileHeaderButtonWrapper,
    ChartOverlayContainer,
    ChartOverlayStatBlock,
    AppointmentSummaryLoadingText,
    AppointmentSummaryProgressRow,
    AppointmentSummaryProgressTrack,
    AppointmentSummaryProgressFill,
    AppointmentSummaryProgressValue,
    ChartOverlayNumber,
    ChartOverlayLabel,
    TileHeaderStatsSection,
    TileHeaderNumber,
    TileHeaderLabel,
    ModalTabNavigation,
    ModalTabButton,
    ModalTabContent,
    StickyLocationHeader,
    StickyLocationCell,
    ChartLegend,
    ChartLegendItem,
    ChartLegendSwatch,
    TagFilterModeToggle,
    TagFilterModeButton
} from './ApplicationDashboard.styles';

// Shared imports
import { color_shades_dark, color_shades_darker, color_shades_darkest, chart_planned_fill, chart_planned_fill_light, chart_planned_solid, chart_unplanned_fill, chart_unplanned_fill_light, chart_unplanned_solid, text_dark, voicify_spark_darker, voicify_spark_light, voicify_spark_dark } from '../../../../../shared/constants/colors';
import SecondaryLoader from '../../../../../shared/components/general/SecondaryLoader';

import HistogramContainer from '../../../../../shared/components/charts/ConvSuccessHistogramContainer';
import HistogramChartCard from '../../../../../shared/components/charts/HistogramChartCard';
import OrgAnalyticsContainer from '../../../../../shared/hooks/OrgAnalyticsContainer';
import EngagementHistogramContainer, { EngagementHistogramView, getVolumeInterval } from '../../../../../shared/hooks/EngagementHistogramContainer';
import { EngagementAnalyticsFilter } from '../../../../../shared/api/controllers/engagementAnalyticsReporting';
import {
    transformToHealthcareMetrics,
    transformSeparateTransferReasonsToChartData,
    transformTopLevelMetricsToSchedulingEvents,
    DashboardHealthcareMetrics,
    SchedulingEventData,
    aggregateTopLevelMetricsFiltered,
    flattenGroupedHistogramDataFiltered,
    flattenConcurrentCallHistogramDataFiltered,
    trimHistogramToSelectedHourRange,
    filterEngagementsByAppIds,
    filterEngagementsByOrganizationIds,
    filterTransferReasonsByAppIds,
    filterTransferReasonsByOrganizationIds,
    parseAnalyticsDate
} from '../../../../../shared/utils/engagementAnalyticsTransformers';
import TransfersChart, { TransferReason, TRANSFER_REASON_LABELS, TRANSFER_REASON_IS_PLANNED, TransferCategoryData, TransferReasonData } from '../../../../../shared/components/charts/TransfersChart';
import ModalLayout from '../../../../../shared/components/structure/ModalLayout';
import ChartDetailModal, { ChartDetailData, TranscriptRecord } from './ChartDetailModal';
import CallDataModal from './CallDataModal';
import TransferDataModal from './TransferDataModal';
import useCSVHandler from '../../../../../shared/hooks/UseCSVHandler';
import { getCategoryName, TAGS } from '../../../../../shared/constants/analyticsConstants/generativeCallReview';
import { useTagConfig } from '../../../../../shared/hooks/TagConfigContainer';
import { MetricInfoTooltip } from './MetricInfoTooltip';
import { nivoChartTheme, barChartDefaults, chartSequentialBlues } from '../../../../../shared/constants/chartTheme';
import { getSparkBaseUrl } from '../../../../../shared/constants/Urls';
import { getAppointmentSummary, AppointmentSummaryResponse } from '../../../../../shared/api/controllers/medicalIntegrationConfiguration';
import {
    calculateOutboundAppointmentSuccess,
    countOutboundScheduledAppointments,
    getDenticonAppointmentCountsBySession,
    getDenticonMatchedAppointmentSessions,
    getOutboundAppointmentChartMetrics,
    hasAppointmentSummaryData
} from '../../../../../shared/utils/appointmentSummary';
import AppointmentSummaryModal from './AppointmentSummaryModal';
import AppointmentSummaryMetricButton from './AppointmentSummaryMetricButton';
import { canViewInternalVoicifyTags, filterInternalVoicifyTags, isInternalVoicifyTag } from '../../../../../shared/utils/tagVisibility';
import { getAnalyticsRequestConcurrency, runAnalyticsRequests } from '../../../../../shared/utils/analyticsRequestQueue';

const filterIcon = require('../../../../../shared/content/images/spark/filter.svg');

// Helper to format hours as "Xh Ym" for Labor Time Saved display
const formatHoursAndMinutes = (hours: number): string => {
    if (hours === undefined || hours === null) return '0h 0m';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (wholeHours === 0) {
        return `${minutes}m`;
    }
    return `${wholeHours}h ${minutes}m`;
};

const getSelectedDateRangeMoments = (startDate: string, endDate: string) => {
    const start = parseAnalyticsDate(startDate, moment.invalid());
    const end = parseAnalyticsDate(endDate, moment.invalid());
    const isStartDateOnly = moment(startDate, 'YYYY-MM-DD', true).isValid();
    const isEndDateOnly = moment(endDate, 'YYYY-MM-DD', true).isValid();

    return {
        rangeStart: isStartDateOnly ? start.startOf('day') : start,
        rangeEnd: isEndDateOnly ? end.endOf('day') : end
    };
};

interface DentalSchedulingDashboardProps extends BaseDashboardProps { }

const APPOINTMENT_SUMMARY_BATCH_SIZE = 10;

const createEmptyAppointmentSummary = (): AppointmentSummaryResponse => ({
    totalAppointments: 0,
    eligibleAppointments: 0,
    successfulOutcomes: 0,
    totalConfirmed: 0,
    totalCancelled: 0,
    totalReschedules: 0,
    totalCheckedOut: 0,
    totalLeftMessage: 0,
    totalMissed: 0,
    totalOther: 0,
    confirmedByVoicify: 0,
    confirmedByVoicifyAndCheckedOut: 0,
    leftMessageByVoicify: 0,
    confirmedByVoicifyAfterLeftMessage: 0,
    confirmedAfterVoicifyLeftMessage: 0,
    missedAfterVoicifyLeftMessage: 0,
    messageOutcomeConfirmed: 0,
    messageOutcomeCheckedOut: 0,
    messageOutcomeRescheduled: 0,
    messageOutcomeCancelled: 0,
    messageOutcomeLeftMessage: 0,
    messageOutcomeMissed: 0,
    messageOutcomeOther: 0,
    missedWithSubsequentBooking: 0,
    missedWithBookingAfterVoicifyCall: 0,
    missedWithoutSubsequentBooking: 0,
    missedSubsequentBookingUnknown: 0,
    missedPatientsCalled: 0,
    missedPatientsWithBooking: 0,
    missedPatientsWithVoicifyBooking: 0,
    totalReactivationPatients: 0,
    reactivationPatientsWithBooking: 0,
    reactivationPatientsWithVoicifyBooking: 0,
    reactivationPatientsWithoutBooking: 0,
    reactivationPatientBookingUnknown: 0,
    appointmentSessionMatches: [],
    failedToConnect: []
});

const mergeAppointmentSummaries = (
    current: AppointmentSummaryResponse,
    next: AppointmentSummaryResponse
): AppointmentSummaryResponse => ({
    totalAppointments: current.totalAppointments + (next.totalAppointments ?? 0),
    eligibleAppointments: (current.eligibleAppointments ?? 0) + (next.eligibleAppointments ?? 0),
    successfulOutcomes: (current.successfulOutcomes ?? 0) + (next.successfulOutcomes ?? 0),
    totalConfirmed: current.totalConfirmed + (next.totalConfirmed ?? 0),
    totalCancelled: current.totalCancelled + (next.totalCancelled ?? 0),
    totalReschedules: current.totalReschedules + (next.totalReschedules ?? 0),
    totalCheckedOut: current.totalCheckedOut + (next.totalCheckedOut ?? 0),
    totalLeftMessage: current.totalLeftMessage + (next.totalLeftMessage ?? 0),
    totalMissed: current.totalMissed + (next.totalMissed ?? 0),
    totalOther: (current.totalOther ?? 0) + (next.totalOther ?? 0),
    confirmedByVoicify: current.confirmedByVoicify + (next.confirmedByVoicify ?? 0),
    confirmedByVoicifyAndCheckedOut: (current.confirmedByVoicifyAndCheckedOut ?? 0) + (next.confirmedByVoicifyAndCheckedOut ?? 0),
    leftMessageByVoicify: current.leftMessageByVoicify + (next.leftMessageByVoicify ?? 0),
    confirmedByVoicifyAfterLeftMessage: (current.confirmedByVoicifyAfterLeftMessage ?? 0) + (next.confirmedByVoicifyAfterLeftMessage ?? 0),
    confirmedAfterVoicifyLeftMessage: current.confirmedAfterVoicifyLeftMessage + (next.confirmedAfterVoicifyLeftMessage ?? 0),
    missedAfterVoicifyLeftMessage: current.missedAfterVoicifyLeftMessage + (next.missedAfterVoicifyLeftMessage ?? 0),
    messageOutcomeConfirmed: (current.messageOutcomeConfirmed ?? 0) + (next.messageOutcomeConfirmed ?? 0),
    messageOutcomeCheckedOut: (current.messageOutcomeCheckedOut ?? 0) + (next.messageOutcomeCheckedOut ?? 0),
    messageOutcomeRescheduled: (current.messageOutcomeRescheduled ?? 0) + (next.messageOutcomeRescheduled ?? 0),
    messageOutcomeCancelled: (current.messageOutcomeCancelled ?? 0) + (next.messageOutcomeCancelled ?? 0),
    messageOutcomeLeftMessage: (current.messageOutcomeLeftMessage ?? 0) + (next.messageOutcomeLeftMessage ?? 0),
    messageOutcomeMissed: (current.messageOutcomeMissed ?? 0) + (next.messageOutcomeMissed ?? 0),
    messageOutcomeOther: (current.messageOutcomeOther ?? 0) + (next.messageOutcomeOther ?? 0),
    missedWithSubsequentBooking: current.missedWithSubsequentBooking + (next.missedWithSubsequentBooking ?? 0),
    missedWithBookingAfterVoicifyCall: current.missedWithBookingAfterVoicifyCall + (next.missedWithBookingAfterVoicifyCall ?? 0),
    missedWithoutSubsequentBooking: current.missedWithoutSubsequentBooking + (next.missedWithoutSubsequentBooking ?? 0),
    missedSubsequentBookingUnknown: current.missedSubsequentBookingUnknown + (next.missedSubsequentBookingUnknown ?? 0),
    missedPatientsCalled: (current.missedPatientsCalled ?? 0) + (next.missedPatientsCalled ?? 0),
    missedPatientsWithBooking: (current.missedPatientsWithBooking ?? 0) + (next.missedPatientsWithBooking ?? 0),
    missedPatientsWithVoicifyBooking: (current.missedPatientsWithVoicifyBooking ?? 0) + (next.missedPatientsWithVoicifyBooking ?? 0),
    totalReactivationPatients: (current.totalReactivationPatients ?? 0) + (next.totalReactivationPatients ?? 0),
    reactivationPatientsWithBooking: (current.reactivationPatientsWithBooking ?? 0) + (next.reactivationPatientsWithBooking ?? 0),
    reactivationPatientsWithVoicifyBooking: (current.reactivationPatientsWithVoicifyBooking ?? 0) + (next.reactivationPatientsWithVoicifyBooking ?? 0),
    reactivationPatientsWithoutBooking: (current.reactivationPatientsWithoutBooking ?? 0) + (next.reactivationPatientsWithoutBooking ?? 0),
    reactivationPatientBookingUnknown: (current.reactivationPatientBookingUnknown ?? 0) + (next.reactivationPatientBookingUnknown ?? 0),
    appointmentSessionMatches: [
        ...(current.appointmentSessionMatches ?? []),
        ...(next.appointmentSessionMatches ?? [])
    ],
    failedToConnect: [
        ...(current.failedToConnect ?? []),
        ...(next.failedToConnect ?? [])
    ]
});

const createAppointmentSummaryBatches = (applicationIds: string[]): string[][] => {
    const batches: string[][] = [];
    for (let index = 0; index < applicationIds.length; index += APPOINTMENT_SUMMARY_BATCH_SIZE) {
        batches.push(applicationIds.slice(index, index + APPOINTMENT_SUMMARY_BATCH_SIZE));
    }
    return batches;
};

interface PlatformSchedulingBarChartDatum {
    organizationId: string;
    organizationLabel: string;
    organizationName: string;
    containmentRate: number;
    appointmentScheduledVolume: number;
    totalBillableMinutes: number;
    totalOutboundCalls: number;
    totalOutboundAppointmentsConfirmed: number;
    totalOutboundLeftMessage: number;
    outboundResponseRate: number;
    outboundResponseRateDenominator: number;
    containableCallsCount: number;
    containedCallsCount: number;
}

type PlatformSchedulingBarChartView = 'containmentRate' | 'appointmentScheduledVolume' | 'totalBillableMinutes' | 'totalOutboundCalls' | 'totalOutboundAppointmentsConfirmed' | 'totalOutboundLeftMessage' | 'outboundResponseRate';

const DentalSchedulingDashboard: React.FC<DentalSchedulingDashboardProps> = ({
    organizationId,
    organizationIds,
    dateRange,
    startDate,
    endDate,
    shouldLoadData = true,
    selectedAppIds,
    selectedEntityIds,
    isChartsLoading,
    totalAppCount,
    appNameMap,
    selectedAppLiveStatusMap,
    entityNameMap,
    isMultiLingual,
    isSpanishOnlyFilter,
    isOutboundCallApp = false,
    analyticsMode = 'organization',
    representativeAppId,
    platformAppIdsByOrganizationId,
    onLoadingChange,
    refreshKey
}) => {
    const { getTagName, healthcareTags, loadTagConfig } = useTagConfig();
    // Setup engagement histogram container for API-driven histogram data
    const engagementHistogramContainer = EngagementHistogramContainer.useContainer();

    // Setup org analytics container for API data
    const orgAnalyticsContainer = OrgAnalyticsContainer.useContainer();
    const [pendingAnalyticsRequests, setPendingAnalyticsRequests] = useState<Set<string>>(new Set());
    const [activeAnalyticsRequestScopeKey, setActiveAnalyticsRequestScopeKey] = useState('');
    const entityIds = selectedEntityIds ?? selectedAppIds;
    const isPlatformMode = analyticsMode === 'platform';
    const analyticsRequestScopeKey = `${isPlatformMode ? organizationIds.join(',') : organizationId}|${startDate}|${endDate}|${refreshKey}`;
    const locationNameMap = isPlatformMode ? (entityNameMap || {}) : appNameMap;
    const groupBy = isPlatformMode ? 'organizationId' as const : 'applicationId' as const;
    const selectedEntityCount = entityIds.size;
    const [platformBarChartView, setPlatformBarChartView] = useState<PlatformSchedulingBarChartView>('containmentRate');

    // Load tag config from the first selected application
    useEffect(() => {
        const firstAppId = representativeAppId || selectedAppIds.values().next().value;
        if (firstAppId) {
            loadTagConfig(firstAppId);
        }
    }, [selectedAppIds, representativeAppId]);

    // Sync engagement histogram container with FILTERED org analytics data
    // This replaces useSyncEngagementHistogram to apply selectedAppIds filtering
    useEffect(() => {
        // Filter and flatten histogram data based on selectedAppIds
        const filteredCallVolume = trimHistogramToSelectedHourRange(flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationCallVolumeHistogramPerApp,
            entityIds
        ), startDate, endDate);
        const filteredContainable = trimHistogramToSelectedHourRange(flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformContainableCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationContainableCallVolumeHistogramPerApp,
            entityIds
        ), startDate, endDate);
        const filteredContained = trimHistogramToSelectedHourRange(flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformContainedCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationContainedCallVolumeHistogramPerApp,
            entityIds
        ), startDate, endDate);
        const filteredSpanishCallVolume = trimHistogramToSelectedHourRange(flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformSpanishCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationSpanishCallVolumeHistogramPerApp,
            entityIds
        ), startDate, endDate);
        const filteredSpanishContainable = trimHistogramToSelectedHourRange(flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformSpanishContainableCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationSpanishContainableCallVolumeHistogramPerApp,
            entityIds
        ), startDate, endDate);
        const filteredSpanishContained = trimHistogramToSelectedHourRange(flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformSpanishContainedCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationSpanishContainedCallVolumeHistogramPerApp,
            entityIds
        ), startDate, endDate);
        const filteredAverageByDay = flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformAverageCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationAverageCallVolumeHistogramPerApp,
            entityIds
        );
        const filteredAverageByHour = trimHistogramToSelectedHourRange(flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformAverageCallVolumeByHourHistogramPerOrg : orgAnalyticsContainer.organizationAverageCallVolumeByHourHistogramPerApp,
            entityIds
        ), startDate, endDate);
        // Use max-based aggregation for concurrent calls to avoid inflated values
        const filteredConcurrent = trimHistogramToSelectedHourRange(flattenConcurrentCallHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformConcurrentCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationConcurrentCallVolumeHistogramPerApp,
            entityIds
        ), startDate, endDate);

        engagementHistogramContainer.updateHistogramData({
            callVolumeHistogram: filteredCallVolume,
            containableCallVolumeHistogram: filteredContainable,
            containedCallVolumeHistogram: filteredContained,
            spanishCallVolumeHistogram: filteredSpanishCallVolume,
            spanishContainableCallVolumeHistogram: filteredSpanishContainable,
            spanishContainedCallVolumeHistogram: filteredSpanishContained,
            averageCallVolumeByHourHistogram: filteredAverageByHour,
            averageCallVolumeByDayHistogram: filteredAverageByDay,
            concurrentCallVolumeHistogram: filteredConcurrent
        });
    }, [
        orgAnalyticsContainer.organizationCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationContainableCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationContainedCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationSpanishCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationSpanishContainableCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationSpanishContainedCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationAverageCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationAverageCallVolumeByHourHistogramPerApp,
        orgAnalyticsContainer.organizationConcurrentCallVolumeHistogramPerApp,
        orgAnalyticsContainer.platformCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformContainableCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformContainedCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformSpanishCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformSpanishContainableCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformSpanishContainedCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformAverageCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformAverageCallVolumeByHourHistogramPerOrg,
        orgAnalyticsContainer.platformConcurrentCallVolumeHistogramPerOrg,
        entityIds,
        startDate,
        endDate,
        isPlatformMode
    ]);

    // Sync loading state from org analytics (not filtered)
    useEffect(() => {
        engagementHistogramContainer.updateLoadingState({
            loadingCallVolume: isPlatformMode ? orgAnalyticsContainer.loadingPlatformCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationCallVolumeHistogram,
            loadingContainable: isPlatformMode ? orgAnalyticsContainer.loadingPlatformContainableCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationContainableCallVolumeHistogram,
            loadingContained: isPlatformMode ? orgAnalyticsContainer.loadingPlatformContainedCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationContainedCallVolumeHistogram,
            loadingSpanishCallVolume: isPlatformMode ? orgAnalyticsContainer.loadingPlatformSpanishCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationSpanishCallVolumeHistogram,
            loadingSpanishContainable: isPlatformMode ? orgAnalyticsContainer.loadingPlatformSpanishContainableCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationSpanishContainableCallVolumeHistogram,
            loadingSpanishContained: isPlatformMode ? orgAnalyticsContainer.loadingPlatformSpanishContainedCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationSpanishContainedCallVolumeHistogram,
            loadingAverageCallVolumeByHour: isPlatformMode ? orgAnalyticsContainer.loadingPlatformAverageCallVolumeByHourHistogram : orgAnalyticsContainer.loadingOrganizationAverageCallVolumeByHourHistogram,
            loadingAverageCallVolumeByDay: isPlatformMode ? orgAnalyticsContainer.loadingPlatformAverageCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationAverageCallVolumeHistogram,
            loadingConcurrentCallVolume: isPlatformMode ? orgAnalyticsContainer.loadingPlatformConcurrentCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationConcurrentCallVolumeHistogram
        });
    }, [
        orgAnalyticsContainer.loadingOrganizationCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationContainableCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationContainedCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationSpanishCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationSpanishContainableCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationSpanishContainedCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationAverageCallVolumeByHourHistogram,
        orgAnalyticsContainer.loadingOrganizationAverageCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationConcurrentCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformContainableCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformContainedCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformSpanishCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformSpanishContainableCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformSpanishContainedCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformAverageCallVolumeByHourHistogram,
        orgAnalyticsContainer.loadingPlatformAverageCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformConcurrentCallVolumeHistogram,
        isPlatformMode
    ]);

    // CSV handler for export functionality
    const csvHandler = useCSVHandler();

    // Load engagement analytics data when date range changes (not on selectedAppIds change)
    // Data filtering by selectedAppIds happens client-side using the per-app data
    useEffect(() => {
        if (!shouldLoadData) return;
        if ((!organizationId && !organizationIds?.length) || (isPlatformMode && !organizationIds?.length)) return;

        const controller = new AbortController();
        const signal = controller.signal;

        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const baseFilter: EngagementAnalyticsFilter = {
            startDate,
            endDate,
            timezone: browserTimezone,
            signal
        };

        const volumeInterval = getVolumeInterval(startDate, endDate);
        const volumeFilter: EngagementAnalyticsFilter = { ...baseFilter, interval: volumeInterval };
        const averageByDayFilter: EngagementAnalyticsFilter = { ...baseFilter, interval: 1 };
        const averageByHourFilter: EngagementAnalyticsFilter = { ...baseFilter, interval: 0 };

        const queuedRequests: Array<{ key: string; load: () => Promise<unknown> }> = isPlatformMode ? [
            { key: 'topLevelMetrics', load: () => orgAnalyticsContainer.loadPlatformTopLevelMetrics(organizationIds, baseFilter) },
            { key: 'transfers', load: () => orgAnalyticsContainer.loadPlatformTransferReasons(organizationIds, baseFilter) },
            { key: 'callVolume', load: () => orgAnalyticsContainer.loadPlatformCallVolumeHistogram(organizationIds, volumeFilter) },
            { key: 'containable', load: () => orgAnalyticsContainer.loadPlatformContainableCallVolumeHistogram(organizationIds, volumeFilter) },
            { key: 'contained', load: () => orgAnalyticsContainer.loadPlatformContainedCallVolumeHistogram(organizationIds, volumeFilter) },
            { key: 'averageByDay', load: () => orgAnalyticsContainer.loadPlatformAverageCallVolumeHistogram(organizationIds, averageByDayFilter) },
            { key: 'averageByHour', load: () => orgAnalyticsContainer.loadPlatformAverageCallVolumeByHourHistogram(organizationIds, averageByHourFilter) },
            { key: 'spanishCallVolume', load: () => orgAnalyticsContainer.loadPlatformSpanishCallVolumeHistogram(organizationIds, volumeFilter) },
            { key: 'spanishContainable', load: () => orgAnalyticsContainer.loadPlatformSpanishContainableCallVolumeHistogram(organizationIds, volumeFilter) },
            { key: 'spanishContained', load: () => orgAnalyticsContainer.loadPlatformSpanishContainedCallVolumeHistogram(organizationIds, volumeFilter) },
            { key: 'concurrentCalls', load: () => orgAnalyticsContainer.loadPlatformConcurrentCallVolumeHistogram(organizationIds, volumeFilter) },
            { key: 'engagements', load: () => orgAnalyticsContainer.loadPlatformEngagements(organizationIds, baseFilter) }
        ] : [
            { key: 'topLevelMetrics', load: () => orgAnalyticsContainer.loadOrganizationTopLevelMetrics(organizationId, baseFilter) },
            { key: 'transfers', load: () => orgAnalyticsContainer.loadOrganizationTransferReasons(organizationId, baseFilter) },
            { key: 'callVolume', load: () => orgAnalyticsContainer.loadOrganizationCallVolumeHistogram(organizationId, volumeFilter) },
            { key: 'offHours', load: () => orgAnalyticsContainer.loadOffHoursEngagements(organizationId, baseFilter) },
            { key: 'spanishOffHours', load: () => orgAnalyticsContainer.loadSpanishOffHoursEngagements(organizationId, baseFilter) },
            { key: 'containable', load: () => orgAnalyticsContainer.loadOrganizationContainableCallVolumeHistogram(organizationId, volumeFilter) },
            { key: 'contained', load: () => orgAnalyticsContainer.loadOrganizationContainedCallVolumeHistogram(organizationId, volumeFilter) },
            { key: 'transferredEngagements', load: () => orgAnalyticsContainer.loadTransferredEngagements(organizationId, baseFilter) },
            { key: 'averageByDay', load: () => orgAnalyticsContainer.loadOrganizationAverageCallVolumeHistogram(organizationId, averageByDayFilter) },
            { key: 'averageByHour', load: () => orgAnalyticsContainer.loadOrganizationAverageCallVolumeByHourHistogram(organizationId, averageByHourFilter) },
            { key: 'spanishCallVolume', load: () => orgAnalyticsContainer.loadOrganizationSpanishCallVolumeHistogram(organizationId, volumeFilter) },
            { key: 'spanishContainable', load: () => orgAnalyticsContainer.loadOrganizationSpanishContainableCallVolumeHistogram(organizationId, volumeFilter) },
            { key: 'spanishContained', load: () => orgAnalyticsContainer.loadOrganizationSpanishContainedCallVolumeHistogram(organizationId, volumeFilter) },
            { key: 'concurrentCalls', load: () => orgAnalyticsContainer.loadOrganizationConcurrentCallVolumeHistogram(organizationId, volumeFilter) },
            { key: 'engagements', load: () => orgAnalyticsContainer.loadOrganizationEngagements(organizationId, baseFilter) }
        ];

        const concurrency = getAnalyticsRequestConcurrency(isPlatformMode ? organizationIds.length : 1, startDate, endDate);
        setActiveAnalyticsRequestScopeKey(analyticsRequestScopeKey);
        setPendingAnalyticsRequests(new Set(queuedRequests.map(request => request.key)));
        const requests = queuedRequests.map(request => async () => {
            try {
                await request.load();
            } finally {
                if (!signal.aborted) {
                    setPendingAnalyticsRequests(current => {
                        const next = new Set(current);
                        next.delete(request.key);
                        return next;
                    });
                }
            }
        });

        runAnalyticsRequests(requests, concurrency, signal).then(() => {
            if (signal.aborted) return;
        });

        return () => {
            controller.abort();
            setPendingAnalyticsRequests(new Set());
        };
    }, [organizationId, organizationIds, startDate, endDate, refreshKey, isPlatformMode, shouldLoadData]);

    // Force histogram view to Spanish Calls when Spanish Only filter is active
    useEffect(() => {
        if (isSpanishOnlyFilter) {
            engagementHistogramContainer.setSelectedView('Spanish Calls');
        }
    }, [isSpanishOnlyFilter]);

    // ==================== GRANULAR LOADING STATES ====================
    // Individual loading states for each chart type - allows charts to render independently
    // as their data becomes available, improving perceived performance for large orgs
    const isAnalyticsRequestPending = (key: string) => shouldLoadData && (
        activeAnalyticsRequestScopeKey !== analyticsRequestScopeKey || pendingAnalyticsRequests.has(key)
    );

    // Appointments Managed still uses raw engagements while its summary contract is finalized.
    const isSchedulingEventsLoading = isChartsLoading || isAnalyticsRequestPending('engagements') ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements);

    // Transfers chart - depends on transfer reasons data and engagement data for modal
    const isTransfersLoading = isChartsLoading || isAnalyticsRequestPending('transfers') || isAnalyticsRequestPending('transferredEngagements') ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformTransferReasons : orgAnalyticsContainer.loadingOrganizationTransferReasons) ||
        (!isPlatformMode && orgAnalyticsContainer.loadingTransferredEngagements);

    // Small tiles (Staff Hours, Off Hours, New Patient) - depend on top-level metrics, off hours data, and engagement data for modals
    const isMetricTilesLoading = isChartsLoading || isAnalyticsRequestPending('topLevelMetrics') || isAnalyticsRequestPending('engagements') || isAnalyticsRequestPending('offHours') || isAnalyticsRequestPending('spanishOffHours') ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformTopLevelMetrics : orgAnalyticsContainer.loadingOrganizationTopLevelMetrics) ||
        (!isPlatformMode && orgAnalyticsContainer.loadingOffHoursEngagements) ||
        (!isPlatformMode && orgAnalyticsContainer.loadingSpanishOffHoursEngagements) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements);

    // Histogram - depends on histogram data from container
    const isHistogramLoading = engagementHistogramContainer.isLoading ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationCallVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformContainableCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationContainableCallVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformContainedCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationContainedCallVolumeHistogram);
    const pendingHistogramKeysByView: Record<string, string[]> = {
        'All Conversations': ['callVolume', 'containable', 'contained'],
        'Containable Conversations': ['containable', 'contained'],
        'Spanish Calls': ['spanishCallVolume', 'spanishContainable', 'spanishContained'],
        'Average Calls by Hour': ['averageByHour'],
        'Average Calls by Day of Week': ['averageByDay'],
        'Concurrent Calls': ['concurrentCalls']
    };
    const isQueuedHistogramLoading = (pendingHistogramKeysByView[engagementHistogramContainer.selectedView] || [])
        .some(key => isAnalyticsRequestPending(key));
    const isHistogramChartLoading = isChartsLoading || isQueuedHistogramLoading || isHistogramLoading;

    // Engagement data loading - needed for modals and export (heavier computation)
    const isEngagementDataLoading = isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements;

    // Report visible chart loading separately from the lower-priority detail hydration.
    const isChartDataLoading =
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformTopLevelMetrics : orgAnalyticsContainer.loadingOrganizationTopLevelMetrics) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationCallVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformContainableCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationContainableCallVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformContainedCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationContainedCallVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformTransferReasons : orgAnalyticsContainer.loadingOrganizationTransferReasons) ||
        (!isPlatformMode && orgAnalyticsContainer.loadingOffHoursEngagements) ||
        (!isPlatformMode && orgAnalyticsContainer.loadingSpanishOffHoursEngagements) ||
        (!isPlatformMode && orgAnalyticsContainer.loadingTransferredEngagements);

    // Primary loading state for parent component reporting
    const isChartsLoadingState = isChartsLoading || isChartDataLoading;

    // Track if interactive features are ready (after engagement data loads and transforms)
    const [isInteractiveFeaturesReady, setIsInteractiveFeaturesReady] = useState(false);

    // When engagement data finishes loading, enable interactive features after a micro-delay
    // IMPORTANT: Buttons should be disabled while ANY loading is happening
    useEffect(() => {
        const isAnyLoading = isEngagementDataLoading || isChartDataLoading || isChartsLoading;

        if (!isAnyLoading) {
            const enableInteractive = () => setIsInteractiveFeaturesReady(true);
            if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(enableInteractive, { timeout: 100 });
            } else {
                setTimeout(enableInteractive, 50);
            }
        } else {
            setIsInteractiveFeaturesReady(false);
        }
    }, [isEngagementDataLoading, isChartDataLoading, isChartsLoading]);

    // Also disable interactive features when charts are loading (for immediate visual feedback)
    const isButtonsDisabled = !isInteractiveFeaturesReady || isChartsLoadingState;

    // Detect if "today" date range is selected - data may still be loading
    const isTodayDateRange = moment(startDate).isSame(moment(), 'day') && moment(endDate).isSame(moment(), 'day');

    // Report only chart loading state to parent
    useEffect(() => {
        onLoadingChange?.(isChartsLoadingState);
    }, [isChartsLoadingState, onLoadingChange]);

    // UI State
    const [hoveredTile, setHoveredTile] = useState<'laborTime' | 'offHours' | 'newPatient' | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    // Detect mobile viewport
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 640);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    const [isChartDetailModalOpen, setIsChartDetailModalOpen] = useState<boolean>(false);
    const [selectedFunnelData, setSelectedFunnelData] = useState<ChartDetailData[] | null>(null);
    const [chartDetailTitle, setChartDetailTitle] = useState<string>('');
    const [chartDetailValueLabel, setChartDetailValueLabel] = useState<string>('conversations');
    const [chartDetailCountPrefix, setChartDetailCountPrefix] = useState<string>('');
    const [chartDetailSummaryLabel, setChartDetailSummaryLabel] = useState<string | undefined>(undefined);
    const [chartDetailShowIndividualTotals, setChartDetailShowIndividualTotals] = useState<boolean>(false);
    const [chartDetailTranscripts, setChartDetailTranscripts] = useState<TranscriptRecord[]>([]);
    const [showTranscriptsTab, setShowTranscriptsTab] = useState<boolean>(false);
    const [chartDetailEventLabel, setChartDetailEventLabel] = useState<string | undefined>(undefined);
    const [chartDetailEventCountTags, setChartDetailEventCountTags] = useState<string[] | undefined>(undefined);
    const [chartDetailExportAllCategories, setChartDetailExportAllCategories] = useState<boolean>(false);
    const [chartDetailSuccessCategoryDisplayName, setChartDetailSuccessCategoryDisplayName] = useState<string | undefined>(undefined);
    const [chartDetailHeaderInfo, setChartDetailHeaderInfo] = useState<string | undefined>(undefined);
    const [isCallDataModalOpen, setIsCallDataModalOpen] = useState<boolean>(false);
    const [allCallsActiveTab, setAllCallsActiveTab] = useState<'calls' | 'byLocation' | 'outbound'>('calls');
    const [isTransfersModalOpen, setIsTransfersModalOpen] = useState<boolean>(false);
    const [isSchedulingEventsModalOpen, setIsSchedulingEventsModalOpen] = useState<boolean>(false);
    const [isContainableCallsModalOpen, setIsContainableCallsModalOpen] = useState<boolean>(false);

    // Call data filter state
    const [callSearchQuery, setCallSearchQuery] = useState<string>('');
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [excludedTags, setExcludedTags] = useState<Set<string>>(new Set());
    // Default to all categories except NO_INTERACTION so no-interaction calls are filtered out on initial load
    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
    const [showNonContainedContainableOnly, setShowNonContainedContainableOnly] = useState(false);
    const [tagFilterMode, setTagFilterMode] = useState<'any' | 'all'>('all');
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
    const [openColumnFilter, setOpenColumnFilter] = useState<string | null>(null);
    const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
    const [phoneFilterMode, setPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());
    const wasCallDataModalOpen = useRef(false);

    // Transfer filter state
    const [transferSearchQuery, setTransferSearchQuery] = useState<string>('');
    const [selectedTransferPhones, setSelectedTransferPhones] = useState<Set<string>>(new Set());
    const [transferPhoneFilterMode, setTransferPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedTransferLocations, setSelectedTransferLocations] = useState<Set<string>>(new Set());
    const [selectedTransferType, setSelectedTransferType] = useState<'all' | 'Planned' | 'Unplanned' | 'Undetermined'>('all');
    const [selectedTransferReasons, setSelectedTransferReasons] = useState<Set<TransferReason>>(new Set());
    const [transferSortConfig, setTransferSortConfig] = useState<SortConfig | null>(null);
    const [openTransferColumnFilter, setOpenTransferColumnFilter] = useState<string | null>(null);

    // Appointments managed filter state (reuses containable calls data with same columns as Order Conversion modal)
    const [schedulingSearchQuery, setSchedulingSearchQuery] = useState<string>('');
    const [selectedSchedulingTags, setSelectedSchedulingTags] = useState<Set<string>>(new Set());
    const [selectedSchedulingCategories, setSelectedSchedulingCategories] = useState<Set<string>>(new Set());
    const [schedulingSortConfig, setSchedulingSortConfig] = useState<SortConfig | null>(null);
    const [openSchedulingColumnFilter, setOpenSchedulingColumnFilter] = useState<string | null>(null);
    const [selectedSchedulingPhones, setSelectedSchedulingPhones] = useState<Set<string>>(new Set());
    const [schedulingPhoneFilterMode, setSchedulingPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedSchedulingLocations, setSelectedSchedulingLocations] = useState<Set<string>>(new Set());

    // Containable calls filter state (for the Call Volume histogram modal)
    const [containableSearchQuery, setContainableSearchQuery] = useState<string>('');
    const [selectedContainableTags, setSelectedContainableTags] = useState<Set<string>>(new Set());
    const [selectedContainableCategories, setSelectedContainableCategories] = useState<Set<string>>(new Set());
    const [containableSortConfig, setContainableSortConfig] = useState<SortConfig | null>(null);
    const [openContainableColumnFilter, setOpenContainableColumnFilter] = useState<string | null>(null);
    const [selectedContainablePhones, setSelectedContainablePhones] = useState<Set<string>>(new Set());
    const [containablePhoneFilterMode, setContainablePhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedContainableLocations, setSelectedContainableLocations] = useState<Set<string>>(new Set());

    // Spanish calls modal state (for Spanish Only filter mode)
    const [isSpanishCallsModalOpen, setIsSpanishCallsModalOpen] = useState<boolean>(false);
    const [spanishSearchQuery, setSpanishSearchQuery] = useState<string>('');
    const [selectedSpanishTags, setSelectedSpanishTags] = useState<Set<string>>(new Set());
    const [selectedSpanishCategories, setSelectedSpanishCategories] = useState<Set<string>>(new Set());
    const [spanishSortConfig, setSpanishSortConfig] = useState<SortConfig | null>(null);
    const [openSpanishColumnFilter, setOpenSpanishColumnFilter] = useState<string | null>(null);
    const [selectedSpanishPhones, setSelectedSpanishPhones] = useState<Set<string>>(new Set());
    const [spanishPhoneFilterMode, setSpanishPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedSpanishLocations, setSelectedSpanishLocations] = useState<Set<string>>(new Set());

    // Pagination state for modals - prevents rendering thousands of rows at once
    const PAGE_SIZE = 100;
    const [callDataPage, setCallDataPage] = useState<number>(0);
    const [transferDataPage, setTransferDataPage] = useState<number>(0);
    const [schedulingDataPage, setSchedulingDataPage] = useState<number>(0);
    const [containableDataPage, setContainableDataPage] = useState<number>(0);
    const [spanishDataPage, setSpanishDataPage] = useState<number>(0);

    // Export loading states
    const [isExportingCallData, setIsExportingCallData] = useState<boolean>(false);
    const [isExportingTransfers, setIsExportingTransfers] = useState<boolean>(false);
    const [isExportingSchedulingEvents, setIsExportingSchedulingEvents] = useState<boolean>(false);
    const [isExportingContainableCalls, setIsExportingContainableCalls] = useState<boolean>(false);
    const [isExportingSpanishCalls, setIsExportingSpanishCalls] = useState<boolean>(false);
    const [isExportingByLocation, setIsExportingByLocation] = useState<boolean>(false);
    const [isExportingOutboundByLocation, setIsExportingOutboundByLocation] = useState<boolean>(false);

    // By Location tab state
    const [byLocationSortConfig, setByLocationSortConfig] = useState<SortConfig | null>(null);
    const [outboundByLocationSortConfig, setOutboundByLocationSortConfig] = useState<SortConfig | null>(null);

    // ==================== DEFERRED DATA TRANSFORMATIONS ====================
    // Heavy computations for modals/export - only computed when interactive features are ready

    const engagements = isPlatformMode ? orgAnalyticsContainer.platformEngagements : orgAnalyticsContainer.organizationEngagements;
    const offHoursEngagements = isPlatformMode ? [] : orgAnalyticsContainer.offHoursEngagements;
    const spanishOffHoursEngagements = isPlatformMode ? [] : orgAnalyticsContainer.spanishOffHoursEngagements;
    const transferredEngagements = isPlatformMode ? [] : orgAnalyticsContainer.transferredEngagements;

    // Filter engagements by selectedAppIds and Spanish-only filter
    const filteredEngagements = useMemo(() => {
        let filtered = isPlatformMode
            ? filterEngagementsByOrganizationIds(engagements, entityIds)
            : filterEngagementsByAppIds(engagements, selectedAppIds);
        if (isSpanishOnlyFilter && filtered) {
            filtered = filtered.filter(isSpanishEngagement);
        }
        return filtered;
    }, [engagements, selectedAppIds, entityIds, isSpanishOnlyFilter, isPlatformMode]);

    const isOutboundAppointmentBookedTag = useCallback(
        (tag: string) => tag === 'OUTBOUND_APPOINTMENT_BOOKED' || tag === 'OUTBOUND-APPOINTMENT-BOOKED',
        []
    );

    // Filter off hours engagements by selectedAppIds (loaded via getEngagementsForTagCombo with CALL_DURING_OFF_HOURS tag)
    const filteredOffHoursEngagements = useMemo(() => {
        let filtered = isPlatformMode
            ? filteredEngagements.filter(eng => eng.sessionTags?.includes('CALL_DURING_OFF_HOURS'))
            : filterEngagementsByAppIds(offHoursEngagements, selectedAppIds);
        if (isSpanishOnlyFilter && filtered) {
            filtered = filtered.filter(isSpanishEngagement);
        }
        return filtered;
    }, [offHoursEngagements, selectedAppIds, filteredEngagements, isSpanishOnlyFilter, isPlatformMode]);

    // Filter Spanish off hours engagements by selectedAppIds (loaded via getEngagementsForTagCombo with CALL_DURING_OFF_HOURS + SPANISH tags)
    const filteredSpanishOffHoursEngagements = useMemo(() => {
        return isPlatformMode
            ? filteredOffHoursEngagements.filter(isSpanishEngagement)
            : filterEngagementsByAppIds(spanishOffHoursEngagements, selectedAppIds);
    }, [spanishOffHoursEngagements, selectedAppIds, filteredOffHoursEngagements, isPlatformMode]);

    // Create a Set of Spanish off hours session IDs for efficient lookup in language breakdown
    const spanishOffHoursSessionIds = useMemo(() => {
        if (!filteredSpanishOffHoursEngagements || filteredSpanishOffHoursEngagements.length === 0) {
            return new Set<string>();
        }
        return new Set(filteredSpanishOffHoursEngagements.map(eng => eng.sessionId).filter(Boolean));
    }, [filteredSpanishOffHoursEngagements]);

    // Filter transferred engagements by selectedAppIds (loaded via getEngagementsForTagCombo with TRANSFERRED tag)
    // This gives us detailed per-engagement data including Spanish status for transfers
    const filteredTransferredEngagements = useMemo(() => {
        let filtered = isPlatformMode
            ? filteredEngagements.filter(eng => eng.sessionTags?.includes('TRANSFERRED'))
            : filterEngagementsByAppIds(transferredEngagements, selectedAppIds);
        if (isSpanishOnlyFilter && filtered) {
            filtered = filtered.filter(isSpanishEngagement);
        }
        return filtered;
    }, [transferredEngagements, selectedAppIds, filteredEngagements, isSpanishOnlyFilter, isPlatformMode]);

    // Compute Spanish transfer breakdown from transferred engagements
    // Groups by application and transfer reason to provide English/Spanish counts
    const transferSpanishBreakdown = useMemo(() => {
        if (!transferredEngagements || transferredEngagements.length === 0) {
            return { byApp: {}, byReason: {}, totals: { planned: { english: 0, spanish: 0 }, unplanned: { english: 0, spanish: 0 } } };
        }

        // Filter by selected apps
        const appFiltered = filterEngagementsByAppIds(transferredEngagements, selectedAppIds);
        if (!appFiltered || appFiltered.length === 0) {
            return { byApp: {}, byReason: {}, totals: { planned: { english: 0, spanish: 0 }, unplanned: { english: 0, spanish: 0 } } };
        }

        // Group by app and reason with English/Spanish breakdown
        const byApp: Record<string, { english: number; spanish: number }> = {};
        const byReason: Record<string, Record<string, { english: number; spanish: number }>> = {
            planned: {},
            unplanned: {}
        };
        const totals = {
            planned: { english: 0, spanish: 0 },
            unplanned: { english: 0, spanish: 0 }
        };

        appFiltered.forEach(eng => {
            const appId = eng.applicationId || 'unknown';
            const isSpanish = isSpanishEngagement(eng);

            // Determine if planned based on available fields (handling empty strings)
            const hasPlannedReason = eng.plannedTransferReason && eng.plannedTransferReason.length > 0;
            const hasUnplannedReason = eng.unplannedTransferReason && eng.unplannedTransferReason.length > 0;
            const isPlanned = eng.isPlannedTransfer === true || (hasPlannedReason && !hasUnplannedReason);

            // Get reason from appropriate field (use non-empty value)
            let reason = 'UNKNOWN';
            if (hasPlannedReason) {
                reason = eng.plannedTransferReason;
            } else if (hasUnplannedReason) {
                reason = eng.unplannedTransferReason;
            }
            const category = isPlanned ? 'planned' : 'unplanned';

            // Skip immediate transfers (they are not containable)
            if (eng.sessionCategory === 'IMMEDIATE_TRANSFER' || reason === 'IMMEDIATE_TRANSFER') return;

            // Track by app
            if (!byApp[appId]) {
                byApp[appId] = { english: 0, spanish: 0 };
            }
            if (isSpanish) {
                byApp[appId].spanish++;
            } else {
                byApp[appId].english++;
            }

            // Track by reason
            if (!byReason[category][reason]) {
                byReason[category][reason] = { english: 0, spanish: 0 };
            }
            if (isSpanish) {
                byReason[category][reason].spanish++;
                totals[category].spanish++;
            } else {
                byReason[category][reason].english++;
                totals[category].english++;
            }
        });

        return { byApp, byReason, totals };
    }, [transferredEngagements, selectedAppIds]);

    // Compute off hours calls count and per-app breakdown from engagements loaded via CALL_DURING_OFF_HOURS tag
    // Compute off hours scheduling attempts - only ACTION_ATTEMPTED calls during off hours
    const offHoursCallsFromEngagements = useMemo(() => {
        if (!filteredOffHoursEngagements || filteredOffHoursEngagements.length === 0) {
            return { count: 0, apps: [] };
        }

        // Filter to only ACTION_ATTEMPTED engagements that do NOT also have a scheduling success tag
        // (those with success tags are counted under "appointments managed" instead)
        const schedulingSuccessTags = ['ACTION_COMPLETED', 'APPOINTMENT_RESCHEDULED', 'APPOINTMENT_CANCELLED', 'CHECK_NEXT_APPOINTMENT_COMPLETE'];
        const actionAttemptedEngagements = filteredOffHoursEngagements.filter(eng => {
            if (eng.sessionCategory !== 'ACTION_ATTEMPTED') return false;
            const tags = eng.sessionTags || [];
            return !tags.some(tag => schedulingSuccessTags.includes(tag));
        });

        // Group by applicationId to get per-app breakdown
        const appCounts: Record<string, { count: number; english: number; spanish: number }> = {};
        actionAttemptedEngagements.forEach(eng => {
            const appId = eng.applicationId || 'unknown';
            if (!appCounts[appId]) {
                appCounts[appId] = { count: 0, english: 0, spanish: 0 };
            }
            appCounts[appId].count++;
            // Check if this session is in the Spanish off hours set (loaded via CALL_DURING_OFF_HOURS + SPANISH tags)
            const isSpanish = eng.sessionId && spanishOffHoursSessionIds.has(eng.sessionId);
            if (isSpanish) {
                appCounts[appId].spanish++;
            } else {
                appCounts[appId].english++;
            }
        });

        const apps = Object.entries(appCounts).map(([appId, data]) => ({
            appName: (appNameMap && appNameMap[appId]) || appId,
            count: data.count,
            english: data.english,
            spanish: data.spanish
        }));

        return {
            count: actionAttemptedEngagements.length,
            apps
        };
    }, [filteredOffHoursEngagements, appNameMap, spanishOffHoursSessionIds]);

    // Tags that indicate a appointment managed was completed during off hours
    const SCHEDULING_EVENT_TAGS = ['ACTION_COMPLETED', 'APPOINTMENT_RESCHEDULED', 'APPOINTMENT_CANCELLED', 'CHECK_NEXT_APPOINTMENT_COMPLETE'];

    // Compute off hours appointments managed - engagements with CALL_DURING_OFF_HOURS that also have a appointment managed tag
    // NOTE: Count tag occurrences (not engagements) to handle multipatient bookings where
    // multiple scheduling actions can occur in a single call
    const offHoursSchedulingEventsFromEngagements = useMemo(() => {
        if (!filteredOffHoursEngagements || filteredOffHoursEngagements.length === 0) {
            return { count: 0, apps: [] };
        }

        // Filter to only engagements that have at least one appointment managed tag
        const schedulingEventEngagements = filteredOffHoursEngagements.filter(eng => {
            const tags = eng.sessionTags || [];
            return tags.some(tag => SCHEDULING_EVENT_TAGS.includes(tag));
        });

        // Group by applicationId and count TAG OCCURRENCES (not engagements) to handle multipatient bookings
        const appCounts: Record<string, { count: number; english: number; spanish: number }> = {};
        let totalTagCount = 0;
        schedulingEventEngagements.forEach(eng => {
            const appId = eng.applicationId || 'unknown';
            const tags = eng.sessionTags || [];
            // Count how many appointment managed tags are in this engagement
            const schedulingTagCount = tags.filter(tag => SCHEDULING_EVENT_TAGS.includes(tag)).length;
            if (schedulingTagCount === 0) return;

            if (!appCounts[appId]) {
                appCounts[appId] = { count: 0, english: 0, spanish: 0 };
            }
            appCounts[appId].count += schedulingTagCount;
            totalTagCount += schedulingTagCount;
            // Check if this session is in the Spanish off hours set (loaded via CALL_DURING_OFF_HOURS + SPANISH tags)
            const isSpanish = eng.sessionId && spanishOffHoursSessionIds.has(eng.sessionId);
            if (isSpanish) {
                appCounts[appId].spanish += schedulingTagCount;
            } else {
                appCounts[appId].english += schedulingTagCount;
            }
        });

        const apps = Object.entries(appCounts).map(([appId, data]) => ({
            appName: (appNameMap && appNameMap[appId]) || appId,
            count: data.count,
            english: data.english,
            spanish: data.spanish
        }));

        return {
            count: totalTagCount,
            apps
        };
    }, [filteredOffHoursEngagements, appNameMap, spanishOffHoursSessionIds]);

    // Filter for appointments managed (both attempts and completions) for the Appointments Managed modal
    // These are calls where a scheduling action was attempted or completed (Schedule, Reschedule, Cancel, Check Next Appointment)
    // Includes: SCHEDULE_ATTEMPTED, RESCHEDULE_ATTEMPTED, CANCELLATION_ATTEMPTED, CHECK_NEXT_APPOINTMENT
    // And: ACTION_COMPLETED, APPOINTMENT_RESCHEDULED, APPOINTMENT_CANCELLED, CHECK_NEXT_APPOINTMENT_COMPLETE
    const SCHEDULING_ATTEMPT_TAGS = ['SCHEDULE_ATTEMPTED', 'RESCHEDULE_ATTEMPTED', 'CANCELLATION_ATTEMPTED', 'CHECK_NEXT_APPOINTMENT'];
    const appointmentActionEngagements = useMemo(() => {
        // If no apps are selected, return empty (show empty state)
        if (!selectedAppIds || selectedAppIds.size === 0) {
            return [];
        }

        // Filter from filteredEngagements by appointment managed or attempt tags
        return filteredEngagements.filter(e => {
            const tags = e.sessionTags || [];
            return tags.some(tag => SCHEDULING_EVENT_TAGS.includes(tag) || SCHEDULING_ATTEMPT_TAGS.includes(tag));
        });
    }, [
        filteredEngagements,
        selectedAppIds
    ]);

    // Filter for containable engagements only (for the Containable Conversations modal)
    // Containable calls use the isContainable property from engagement data
    // Filter outbound calls from engagement metrics
    const containableEngagements = useMemo(() => {
        if (!filteredEngagements || filteredEngagements.length === 0) return [];
        return filteredEngagements.filter(e => e.isContainable && !e.sessionTags?.includes("OUTBOUND_CALL"));
    }, [filteredEngagements]);

    // Filter for contained engagements (successfully handled calls)
    // Contained calls use the isContained property from engagement data
    const containedEngagements = useMemo(() => {
        if (!filteredEngagements || filteredEngagements.length === 0) return [];
        return filteredEngagements.filter(e => e.isContained && !e.sessionTags?.includes("OUTBOUND_CALL"));
    }, [filteredEngagements]);

    // Computed containment counts based on engagement isContainable/isContained properties
    const containableCount = containableEngagements.length;
    const containedCount = containedEngagements.length;

    // Count of all conversations for the histogram legend.
    // Platform mode shows all loaded engagements; organization mode excludes NO_INTERACTION.
    const allConversationsCount = useMemo(() => {
        if (!filteredEngagements || filteredEngagements.length === 0) return 0;
        return isPlatformMode
            ? filteredEngagements.length
            : filteredEngagements.filter(e => e.sessionCategory !== 'NO_INTERACTION').length;
    }, [filteredEngagements, isPlatformMode]);

    // Transform ALL engagements for the Call Data modal (shows all calls)
    const callDataRecords = useMemo(() => {
        if (!isInteractiveFeaturesReady || !filteredEngagements || filteredEngagements.length === 0) return [];
        return transformEngagementsToCallRecords(filteredEngagements, appNameMap, locationNameMap, groupBy);
    }, [filteredEngagements, appNameMap, locationNameMap, groupBy, isInteractiveFeaturesReady]);

    // Transform Appointment Action Attempts only for the Appointments Managed modal
    const schedulingEventsCallDataRecords = useMemo(() => {
        if (!isInteractiveFeaturesReady || !appointmentActionEngagements || appointmentActionEngagements.length === 0) return [];
        return transformEngagementsToCallRecords(appointmentActionEngagements, appNameMap, locationNameMap, groupBy);
    }, [appointmentActionEngagements, appNameMap, locationNameMap, groupBy, isInteractiveFeaturesReady]);

    // Transform outbound engagement analytics events for the Outbound Call Results modal
    const outboundCallDataRecords = useMemo((): CallDataRecord[] => {
        if (!isInteractiveFeaturesReady) return [];
        const allEvents = filteredEngagements || [];
        if (!entityIds || entityIds.size === 0) return [];
        if (!startDate || !endDate) return [];

        const { rangeStart, rangeEnd } = getSelectedDateRangeMoments(startDate, endDate);

        const outboundTags: string[] = [
            TAGS.OUTBOUND_APPOINTMENT_CONFIRMED,
            TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED,
            TAGS.OUTBOUND_APPOINTMENT_CANCELLED,
            TAGS.OUTBOUND_LEFT_MESSAGE,
            TAGS.OUTBOUND_CALL_TRANSFERRED,
            'OUTBOUND_REACTIVATION',
            'OUTBOUND_MISSED_APPOINTMENT_CHECK',
            'OUTBOUND-APPOINTMENT-BOOKED'
        ];

        const filtered = allEvents.filter(e =>
            e.sessionTags?.some(tag => outboundTags.includes(tag) || isOutboundAppointmentBookedTag(tag))
        );

        return transformEngagementsToCallRecords(filtered, appNameMap, locationNameMap, groupBy);
    }, [
        filteredEngagements,
        entityIds,
        selectedAppIds,
        appNameMap,
        locationNameMap,
        groupBy,
        isInteractiveFeaturesReady,
        isOutboundAppointmentBookedTag,
        isPlatformMode,
        isSpanishOnlyFilter,
        startDate,
        endDate
    ]);

    const schedulingEventsModalRecords = useMemo(() => {
        if (!isOutboundCallApp) {
            return schedulingEventsCallDataRecords;
        }

        const uniqueRecords = new Map<string, CallDataRecord>();
        [...schedulingEventsCallDataRecords, ...outboundCallDataRecords].forEach(record => {
            const key = `${record.applicationId || 'app'}::${record.sessionId || record.id}`;
            if (!uniqueRecords.has(key)) {
                uniqueRecords.set(key, record);
            }
        });
        return Array.from(uniqueRecords.values());
    }, [isOutboundCallApp, schedulingEventsCallDataRecords, outboundCallDataRecords]);

    // Left Message is a call-session metric. A patient can receive multiple
    // confirmation calls, while Denticon represents the appointment only once.
    const outboundLeftMessageSessionRecords = useMemo(() => {
        const uniqueRecords = new Map<string, CallDataRecord>();
        outboundCallDataRecords
            .filter(record => record.tags.includes(TAGS.OUTBOUND_LEFT_MESSAGE))
            .forEach(record => {
                const key = `${record.applicationId || 'app'}::${record.sessionId || record.id}`;
                if (!uniqueRecords.has(key)) {
                    uniqueRecords.set(key, record);
                }
            });
        return Array.from(uniqueRecords.values());
    }, [outboundCallDataRecords]);
    const outboundLeftMessageSessionCount = outboundLeftMessageSessionRecords.length;

    // Use the exact deduplicated records rendered in View Details as the Scheduled
    // source of truth, so a visible outbound booking tag always maps to the outbound
    // Schedule bar and click-through modal.
    const outboundScheduledFromDetailRecords = useMemo(
        () => countOutboundScheduledAppointments(schedulingEventsModalRecords),
        [schedulingEventsModalRecords]
    );

    // Transform CONTAINABLE engagements only for the Containable Conversations modal
    const containableCallDataRecords = useMemo(() => {
        if (!isInteractiveFeaturesReady || !containableEngagements || containableEngagements.length === 0) return [];
        return transformEngagementsToCallRecords(containableEngagements, appNameMap, locationNameMap, groupBy);
    }, [containableEngagements, appNameMap, locationNameMap, groupBy, isInteractiveFeaturesReady]);

    const transferDataRecords = useMemo(() => {
        if (!isInteractiveFeaturesReady) return [];
        // Always use filtered transferred engagements for call-level detail to match chart data source
        // Apply tag-to-reason mapping similar to transfersData chart
        if (!filteredTransferredEngagements || filteredTransferredEngagements.length === 0) return [];

        // Map sessionTags to TransferReason enum values (same as chart data)
        const tagToReasonMap: Record<string, TransferReason> = {
            'UNSUPPORTED_APPOINTMENT': TransferReason.UNSUPPORTED_APPOINTMENT_TYPE,
            'UNSUPPORTED_APPOINTMENT_TYPE': TransferReason.UNSUPPORTED_APPOINTMENT_TYPE,
            'POS_CONNECTION_ISSUE': TransferReason.POS_CONNECTION_ISSUE,
            'POS_ISSUE': TransferReason.POS_CONNECTION_ISSUE,
            'VFY_POS_API_FAILURE': TransferReason.POS_CONNECTION_ISSUE,
            'PAYMENT_LINK_REFUSED': TransferReason.PAYMENT_LINK_REFUSED,
            'PRICING_ISSUE': TransferReason.PRICING_ISSUE,
            'END_OF_CALL_TRANSFER': TransferReason.END_OF_CALL_TRANSFER,
            'END_OF_CALL': TransferReason.END_OF_CALL_TRANSFER,
            'VFY_CALL_END_TRANSFER': TransferReason.END_OF_CALL_TRANSFER,
            'FAQ_CONFIGURED_TRANSFER': TransferReason.FAQ_CONFIGURED_TRANSFER,
            'FAQ_TRANSFER': TransferReason.FAQ_CONFIGURED_TRANSFER,
            'FAQ': TransferReason.FAQ_CONFIGURED_TRANSFER,
            'UNSUPPORTED_REQUEST': TransferReason.UNSUPPORTED_REQUEST,
            'UNSUPPORTED_CUSTOMER_REQUEST': TransferReason.UNSUPPORTED_REQUEST,
            'AI_ISSUE': TransferReason.AI_ISSUE,
            'OFF_HOURS_TRANSFER': TransferReason.OFF_HOURS_TRANSFER,
            'OFF_HOURS': TransferReason.OFF_HOURS_TRANSFER,
            'CALL_DURING_OFF_HOURS': TransferReason.OFF_HOURS_TRANSFER,
            'CUSTOMER_FRUSTRATED': TransferReason.CUSTOMER_FRUSTRATED,
            'CUSTOMER_FRUSTRATION': TransferReason.CUSTOMER_FRUSTRATED,
            'FRUSTRATED': TransferReason.CUSTOMER_FRUSTRATED,
            'CALLER_REQUESTED_TRANSFER': TransferReason.CALLER_REQUESTED_TRANSFER,
            'CALLER_REQUESTED': TransferReason.CALLER_REQUESTED_TRANSFER,
            'IMMEDIATE_TRANSFER': TransferReason.IMMEDIATE_TRANSFER
        };

        const result: TransferDataRecord[] = [];

        for (const eng of filteredTransferredEngagements) {
            // Skip immediate transfers
            if (eng.sessionCategory === 'IMMEDIATE_TRANSFER' ||
                eng.sessionTags?.includes('IMMEDIATE_TRANSFER')) continue;

            // Format date/time
            const eventMoment = eng.eventDate ? moment(eng.eventDate) : moment();
            const date = eventMoment.format('YYYY-MM-DD');
            const time = eventMoment.format('h:mm A');

            // Determine transfer reason from sessionTags
            let reason: TransferReason = TransferReason.UNKNOWN;
            if (eng.sessionTags && Array.isArray(eng.sessionTags)) {
                for (const tag of eng.sessionTags) {
                    if (tagToReasonMap[tag]) {
                        reason = tagToReasonMap[tag];
                        break;
                    }
                }
            }

            // If no tag match, check explicit reason fields
            if (reason === TransferReason.UNKNOWN) {
                if (eng.plannedTransferReason && eng.plannedTransferReason.trim().length > 0) {
                    const mappedReason = tagToReasonMap[eng.plannedTransferReason.trim()];
                    reason = mappedReason || (eng.plannedTransferReason.trim() as TransferReason);
                } else if (eng.unplannedTransferReason && eng.unplannedTransferReason.trim().length > 0) {
                    const mappedReason = tagToReasonMap[eng.unplannedTransferReason.trim()];
                    reason = mappedReason || (eng.unplannedTransferReason.trim() as TransferReason);
                }
            }

            // Determine planned/unplanned using TRANSFER_REASON_IS_PLANNED mapping
            let isPlanned: boolean | null = null;
            if (eng.isPlannedTransfer === true) {
                isPlanned = true;
            } else if (eng.isUnplannedTransfer === true) {
                isPlanned = false;
            } else if (reason !== TransferReason.UNKNOWN && TRANSFER_REASON_IS_PLANNED[reason] !== undefined) {
                isPlanned = TRANSFER_REASON_IS_PLANNED[reason];
            }

            result.push({
                id: eng.id,
                date,
                time,
                phoneLastFour: eng.nativeUserId?.slice(-4) || '****',
                transcriptLink: `/v/apps/${eng.applicationId}/analytics/sessions?sessionId=${eng.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`,
                transferType: isPlanned === true ? 'Planned' : isPlanned === false ? 'Unplanned' : 'Undetermined',
                transferReason: reason,
                location: locationNameMap[groupBy === 'organizationId' ? eng.organizationId : eng.applicationId] || appNameMap[eng.applicationId] || eng.applicationId || eng.organizationId,
                applicationId: eng.applicationId,
                sessionId: eng.sessionId
            });
        }

        return result;
    }, [filteredTransferredEngagements, appNameMap, locationNameMap, groupBy, isInteractiveFeaturesReady, startDate, endDate]);

    // Transform off-hours engagements to call records for transcript detail
    const offHoursCallDataRecords = useMemo(() => {
        if (!isInteractiveFeaturesReady || !filteredOffHoursEngagements || filteredOffHoursEngagements.length === 0) return [];
        return transformEngagementsToCallRecords(filteredOffHoursEngagements, appNameMap, locationNameMap, groupBy);
    }, [filteredOffHoursEngagements, appNameMap, locationNameMap, groupBy, isInteractiveFeaturesReady]);

    // Transform new patient booking attempts - includes:
    // 1. Successful new patient bookings (NEW_PATIENT + ACTION_COMPLETED)
    // 2. All scheduling attempts (SCHEDULE_ATTEMPTED without ACTION_COMPLETED) - these may include new patient attempts
    const newPatientCallDataRecords = useMemo(() => {
        if (!isInteractiveFeaturesReady || !filteredEngagements || filteredEngagements.length === 0) return [];
        const newPatientAttemptEngagements = filteredEngagements.filter(e => e.sessionTags?.includes('NEW_PATIENT'));
        return transformEngagementsToCallRecords(newPatientAttemptEngagements, appNameMap, locationNameMap, groupBy);
    }, [filteredEngagements, appNameMap, locationNameMap, groupBy, isInteractiveFeaturesReady]);

    // Transform off-hours scheduling activity from filteredEngagements (which includes sessionSummaries)
    // Includes both scheduling attempts (ACTION_ATTEMPTED) and completed events
    // This provides expandable summaries in the modal, similar to New Patient Bookings
    const offHoursSchedulingCallDataRecords = useMemo(() => {
        if (!isInteractiveFeaturesReady || !filteredEngagements || filteredEngagements.length === 0) return [];
        const schedulingEventTags = ['ACTION_COMPLETED', 'APPOINTMENT_RESCHEDULED', 'APPOINTMENT_CANCELLED', 'CHECK_NEXT_APPOINTMENT_COMPLETE'];
        const offHoursSchedulingEngagements = filteredEngagements.filter(e => {
            const tags = e.sessionTags || [];
            const hasOffHoursTag = tags.includes('CALL_DURING_OFF_HOURS');
            // Include if: has off hours tag AND (has ACTION_ATTEMPTED category OR has appointment managed tag)
            const isAttempt = e.sessionCategory === 'ACTION_ATTEMPTED';
            const hasSchedulingEventTag = tags.some(tag => schedulingEventTags.includes(tag));
            return hasOffHoursTag && (isAttempt || hasSchedulingEventTag);
        });
        return transformEngagementsToCallRecords(offHoursSchedulingEngagements, appNameMap, locationNameMap, groupBy);
    }, [filteredEngagements, appNameMap, locationNameMap, groupBy, isInteractiveFeaturesReady]);

    // Unique values for filters - computed lazily (for Call Data modal - all calls)
    const uniquePhones = useMemo(() =>
        callDataRecords.length > 0 ? [...new Set(callDataRecords.map(c => c.phoneLastFour))].sort() : [],
        [callDataRecords]
    );
    const uniqueLocations = useMemo(() =>
        callDataRecords.length > 0 ? [...new Set(callDataRecords.map(c => c.location))].sort() : [],
        [callDataRecords]
    );
    const uniqueCallCategories = useMemo(() =>
        callDataRecords.length > 0 ? [...new Set(callDataRecords.map(c => c.category).filter(Boolean))].sort() : [],
        [callDataRecords]
    );
    const callModalTagSet = useMemo(() => {
        const showInternalTags = canViewInternalVoicifyTags();
        const configTags = filterInternalVoicifyTags(healthcareTags, showInternalTags);
        const configTagSet = new Set(configTags);
        const extraTags = new Set<string>();

        callDataRecords.forEach(record => {
            record.tags?.forEach(tag => {
                if ((showInternalTags || !isInternalVoicifyTag(tag)) && !configTagSet.has(tag)) {
                    extraTags.add(tag);
                }
            });
        });

        return [...configTags, ...[...extraTags].sort()];
    }, [healthcareTags, callDataRecords]);
    const defaultSelectedCallCategories = useMemo(() =>
        new Set(uniqueCallCategories.filter(category => category !== 'NO_INTERACTION')),
        [uniqueCallCategories]
    );
    const uniqueTransferPhones = useMemo(() =>
        transferDataRecords.length > 0 ? [...new Set(transferDataRecords.map(t => t.phoneLastFour))].sort() : [],
        [transferDataRecords]
    );
    const uniqueTransferLocations = useMemo(() =>
        transferDataRecords.length > 0 ? [...new Set(transferDataRecords.map(t => t.location))].sort() : [],
        [transferDataRecords]
    );
    const uniqueTransferReasons = useMemo(() =>
        transferDataRecords.length > 0 ? [...new Set(transferDataRecords.map(t => t.transferReason))] : [],
        [transferDataRecords]
    );

    // Unique values for containable calls modal filters
    const uniqueContainablePhones = useMemo(() =>
        containableCallDataRecords.length > 0 ? [...new Set(containableCallDataRecords.map(c => c.phoneLastFour))].sort() : [],
        [containableCallDataRecords]
    );
    const uniqueContainableLocations = useMemo(() =>
        containableCallDataRecords.length > 0 ? [...new Set(containableCallDataRecords.map(c => c.location))].sort() : [],
        [containableCallDataRecords]
    );

    // Filtered call data - computed only when modal is open to avoid blocking UI
    const filteredCallData = useMemo(() => {
        // Skip filtering when no data or modal isn't visible
        if (callDataRecords.length === 0 || !isCallDataModalOpen) return [];

        let filtered = callDataRecords.filter(call => {
            const searchLower = callSearchQuery.toLowerCase();
            const matchesSearch = !callSearchQuery ||
                call.location.toLowerCase().includes(searchLower) ||
                call.phoneLastFour.toLowerCase().includes(searchLower) ||
                call.date.toLowerCase().includes(searchLower) ||
                call.time.toLowerCase().includes(searchLower) ||
                call.category.toLowerCase().includes(searchLower) ||
                call.tags.some(tag => getTagName(tag, 'HEALTHCARE').toLowerCase().includes(searchLower));

            let matchesPhone = true;
            if (selectedPhones.size > 0) {
                matchesPhone = phoneFilterMode === 'include'
                    ? selectedPhones.has(call.phoneLastFour)
                    : !selectedPhones.has(call.phoneLastFour);
            }

            const matchesLocation = selectedLocations.size === 0 || selectedLocations.has(call.location);
            const matchesTags = selectedTags.size === 0 || (
                tagFilterMode === 'any'
                    ? call.tags.some(tag => selectedTags.has(tag))
                    : [...selectedTags].every(tag => call.tags.includes(tag))
            );
            const notExcluded = excludedTags.size === 0 || !call.tags.some(tag => excludedTags.has(tag));
            const matchesCategory = selectedCategories.size === 0 || selectedCategories.has(call.category);
            const matchesContainment = !showNonContainedContainableOnly || isNonContainedContainableCall(call);

            return matchesSearch && matchesPhone && matchesLocation && matchesTags && notExcluded && matchesCategory && matchesContainment;
        });

        return sortDataRecords(filtered, sortConfig);
    }, [callDataRecords, callSearchQuery, selectedPhones, phoneFilterMode, selectedLocations, selectedTags, excludedTags, tagFilterMode, selectedCategories, showNonContainedContainableOnly, sortConfig, isCallDataModalOpen]);

    // Location summary data for By Location tab
    const locationSummaryData = useMemo(() => {
        if (callDataRecords.length === 0 || !isCallDataModalOpen) return [];
        return aggregateCallsByLocation(
            filterRecordsToLiveApps(callDataRecords, selectedAppLiveStatusMap)
                .filter(call => !call.tags.includes(TAGS.OUTBOUND_CALL))
        );
    }, [callDataRecords, isCallDataModalOpen, selectedAppLiveStatusMap]);

    const sortedLocationSummaryData = useMemo(() => {
        return sortLocationSummaries(locationSummaryData, byLocationSortConfig);
    }, [locationSummaryData, byLocationSortConfig]);

    const outboundLocationSummaryData = useMemo((): OutboundLocationSummary[] => {
        if (!isOutboundCallApp || !isCallDataModalOpen || !engagements || engagements.length === 0) return [];

        const { rangeStart, rangeEnd } = getSelectedDateRangeMoments(startDate, endDate);
        const selectedIds = isPlatformMode ? entityIds : selectedAppIds;

        if (!selectedIds || selectedIds.size === 0) return [];

        const filteredOutboundEngagements = (engagements || []).filter(e => {
            const tags = e.sessionTags || [];
            const matchesSelection = isPlatformMode ? entityIds.has(e.organizationId) : selectedAppIds.has(e.applicationId);
            if (!matchesSelection) return false;
            if (selectedAppLiveStatusMap && selectedAppLiveStatusMap[e.applicationId] === false) return false;
            if (isSpanishOnlyFilter && !isSpanishEngagement(e)) return false;

            const eventDate = moment(e.eventDate);
            if (!eventDate.isSameOrAfter(rangeStart) || !eventDate.isSameOrBefore(rangeEnd)) return false;

            return tags.includes(TAGS.OUTBOUND_CALL) ||
                tags.includes(TAGS.OUTBOUND_APPOINTMENT_CONFIRMED) ||
                tags.includes(TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED) ||
                tags.includes(TAGS.OUTBOUND_APPOINTMENT_CANCELLED) ||
                tags.includes(TAGS.OUTBOUND_LEFT_MESSAGE) ||
                tags.includes(TAGS.OUTBOUND_CALL_TRANSFERRED) ||
                tags.includes('OUTBOUND_REACTIVATION') ||
                tags.includes('OUTBOUND_MISSED_APPOINTMENT_CHECK') ||
                tags.includes('OUTBOUND-APPOINTMENT-BOOKED');
        });

        return aggregateOutboundLocationSummaries(filteredOutboundEngagements, appNameMap);
    }, [
        appNameMap,
        engagements,
        entityIds,
        isCallDataModalOpen,
        isOutboundCallApp,
        isPlatformMode,
        isSpanishOnlyFilter,
        selectedAppIds,
        selectedAppLiveStatusMap,
        startDate,
        endDate
    ]);

    const sortedOutboundLocationSummaryData = useMemo(() => {
        return sortOutboundLocationSummaries(outboundLocationSummaryData, outboundByLocationSortConfig);
    }, [outboundLocationSummaryData, outboundByLocationSortConfig]);

    const shouldShowOutboundMetricsWarning = useMemo(() => {
        return moment(startDate).isBefore(moment('2026-07-14', 'YYYY-MM-DD'), 'day');
    }, [startDate]);

    // Filtered transfer data - computed only when modal is open
    const filteredTransferData = useMemo(() => {
        // Skip filtering when no data or modal isn't visible
        if (transferDataRecords.length === 0 || !isTransfersModalOpen) return [];

        let filtered = transferDataRecords.filter(transfer => {
            const searchLower = transferSearchQuery.toLowerCase();
            const matchesSearch = !transferSearchQuery ||
                transfer.location.toLowerCase().includes(searchLower) ||
                transfer.phoneLastFour.toLowerCase().includes(searchLower) ||
                transfer.date.toLowerCase().includes(searchLower) ||
                transfer.time.toLowerCase().includes(searchLower) ||
                transfer.transferType.toLowerCase().includes(searchLower) ||
                (TRANSFER_REASON_LABELS[transfer.transferReason] || transfer.transferReason).toLowerCase().includes(searchLower);

            let matchesPhone = true;
            if (selectedTransferPhones.size > 0) {
                matchesPhone = transferPhoneFilterMode === 'include'
                    ? selectedTransferPhones.has(transfer.phoneLastFour)
                    : !selectedTransferPhones.has(transfer.phoneLastFour);
            }

            const matchesLocation = selectedTransferLocations.size === 0 || selectedTransferLocations.has(transfer.location);
            const matchesType = selectedTransferType === 'all' || transfer.transferType === selectedTransferType;
            const matchesReason = selectedTransferReasons.size === 0 || selectedTransferReasons.has(transfer.transferReason);

            return matchesSearch && matchesPhone && matchesLocation && matchesType && matchesReason;
        });

        return sortDataRecords(filtered, transferSortConfig, true);
    }, [transferDataRecords, transferSearchQuery, selectedTransferPhones, transferPhoneFilterMode, selectedTransferLocations, selectedTransferType, selectedTransferReasons, transferSortConfig, isTransfersModalOpen]);

    // Filtered appointments managed data - uses Appointment Action Attempts only (Appointments Managed chart)
    const filteredSchedulingData = useMemo(() => {
        // Skip filtering when no data or modal isn't visible
        if (schedulingEventsCallDataRecords.length === 0 || !isSchedulingEventsModalOpen) return [];

        let filtered = schedulingEventsCallDataRecords.filter(call => {
            const searchLower = schedulingSearchQuery.toLowerCase();
            const matchesSearch = !schedulingSearchQuery ||
                call.location.toLowerCase().includes(searchLower) ||
                call.phoneLastFour.toLowerCase().includes(searchLower) ||
                call.date.toLowerCase().includes(searchLower) ||
                call.time.toLowerCase().includes(searchLower) ||
                call.category.toLowerCase().includes(searchLower) ||
                call.tags.some(tag => getTagName(tag, 'HEALTHCARE').toLowerCase().includes(searchLower));

            let matchesPhone = true;
            if (selectedSchedulingPhones.size > 0) {
                matchesPhone = schedulingPhoneFilterMode === 'include'
                    ? selectedSchedulingPhones.has(call.phoneLastFour)
                    : !selectedSchedulingPhones.has(call.phoneLastFour);
            }

            const matchesLocation = selectedSchedulingLocations.size === 0 || selectedSchedulingLocations.has(call.location);
            const matchesTags = selectedSchedulingTags.size === 0 || call.tags.some(tag => selectedSchedulingTags.has(tag));
            const matchesCategory = selectedSchedulingCategories.size === 0 || selectedSchedulingCategories.has(call.category);

            return matchesSearch && matchesPhone && matchesLocation && matchesTags && matchesCategory;
        });

        return sortDataRecords(filtered, schedulingSortConfig);
    }, [schedulingEventsCallDataRecords, schedulingSearchQuery, selectedSchedulingPhones, schedulingPhoneFilterMode, selectedSchedulingLocations, selectedSchedulingTags, selectedSchedulingCategories, schedulingSortConfig, isSchedulingEventsModalOpen]);

    // Filtered containable call data - uses containable calls only (Call Volume histogram modal)
    const filteredContainableCallData = useMemo(() => {
        // Skip filtering when no data or modal isn't visible
        if (containableCallDataRecords.length === 0 || !isContainableCallsModalOpen) return [];

        let filtered = containableCallDataRecords.filter(call => {
            const searchLower = containableSearchQuery.toLowerCase();
            const matchesSearch = !containableSearchQuery ||
                call.location.toLowerCase().includes(searchLower) ||
                call.phoneLastFour.toLowerCase().includes(searchLower) ||
                call.date.toLowerCase().includes(searchLower) ||
                call.time.toLowerCase().includes(searchLower) ||
                call.category.toLowerCase().includes(searchLower) ||
                call.tags.some(tag => getTagName(tag, 'HEALTHCARE').toLowerCase().includes(searchLower));

            let matchesPhone = true;
            if (selectedContainablePhones.size > 0) {
                matchesPhone = containablePhoneFilterMode === 'include'
                    ? selectedContainablePhones.has(call.phoneLastFour)
                    : !selectedContainablePhones.has(call.phoneLastFour);
            }

            const matchesLocation = selectedContainableLocations.size === 0 || selectedContainableLocations.has(call.location);
            const matchesTags = selectedContainableTags.size === 0 || call.tags.some(tag => selectedContainableTags.has(tag));
            const matchesCategory = selectedContainableCategories.size === 0 || selectedContainableCategories.has(call.category);

            return matchesSearch && matchesPhone && matchesLocation && matchesTags && matchesCategory;
        });

        return sortDataRecords(filtered, containableSortConfig);
    }, [containableCallDataRecords, containableSearchQuery, selectedContainablePhones, containablePhoneFilterMode, selectedContainableLocations, selectedContainableTags, selectedContainableCategories, containableSortConfig, isContainableCallsModalOpen]);

    // Spanish calls data - uses ALL call records (already filtered for Spanish when isSpanishOnlyFilter is true)
    // This ensures the modal matches the "Spanish Calls" histogram which shows all Spanish calls, not just containable ones
    const spanishCallDataRecords = useMemo(() => {
        // When Spanish Only filter is active, callDataRecords already contains Spanish-only data from filteredEngagements
        return callDataRecords;
    }, [callDataRecords]);

    // Unique values for Spanish calls modal filters
    const uniqueSpanishPhones = useMemo(() =>
        spanishCallDataRecords.length > 0 ? [...new Set(spanishCallDataRecords.map(c => c.phoneLastFour))].sort() : [],
        [spanishCallDataRecords]
    );
    const uniqueSpanishLocations = useMemo(() =>
        spanishCallDataRecords.length > 0 ? [...new Set(spanishCallDataRecords.map(c => c.location))].sort() : [],
        [spanishCallDataRecords]
    );

    // Filtered Spanish call data
    const filteredSpanishCallData = useMemo(() => {
        if (spanishCallDataRecords.length === 0 || !isSpanishCallsModalOpen) return [];

        let filtered = spanishCallDataRecords.filter(call => {
            const searchLower = spanishSearchQuery.toLowerCase();
            const matchesSearch = !spanishSearchQuery ||
                call.location.toLowerCase().includes(searchLower) ||
                call.phoneLastFour.toLowerCase().includes(searchLower) ||
                call.date.toLowerCase().includes(searchLower) ||
                call.time.toLowerCase().includes(searchLower) ||
                call.category.toLowerCase().includes(searchLower) ||
                call.tags.some(tag => getTagName(tag, 'HEALTHCARE').toLowerCase().includes(searchLower));

            let matchesPhone = true;
            if (selectedSpanishPhones.size > 0) {
                matchesPhone = spanishPhoneFilterMode === 'include'
                    ? selectedSpanishPhones.has(call.phoneLastFour)
                    : !selectedSpanishPhones.has(call.phoneLastFour);
            }

            const matchesLocation = selectedSpanishLocations.size === 0 || selectedSpanishLocations.has(call.location);
            const matchesTags = selectedSpanishTags.size === 0 || call.tags.some(tag => selectedSpanishTags.has(tag));
            const matchesCategory = selectedSpanishCategories.size === 0 || selectedSpanishCategories.has(call.category);

            return matchesSearch && matchesPhone && matchesLocation && matchesTags && matchesCategory;
        });

        return sortDataRecords(filtered, spanishSortConfig);
    }, [spanishCallDataRecords, spanishSearchQuery, selectedSpanishPhones, spanishPhoneFilterMode, selectedSpanishLocations, selectedSpanishTags, selectedSpanishCategories, spanishSortConfig, isSpanishCallsModalOpen]);

    // Paginated data for rendering - limits DOM nodes to PAGE_SIZE at a time
    const paginatedCallData = useMemo(() => {
        const start = callDataPage * PAGE_SIZE;
        return filteredCallData.slice(start, start + PAGE_SIZE);
    }, [filteredCallData, callDataPage]);

    const paginatedTransferData = useMemo(() => {
        const start = transferDataPage * PAGE_SIZE;
        return filteredTransferData.slice(start, start + PAGE_SIZE);
    }, [filteredTransferData, transferDataPage]);

    const paginatedSchedulingData = useMemo(() => {
        const start = schedulingDataPage * PAGE_SIZE;
        return filteredSchedulingData.slice(start, start + PAGE_SIZE);
    }, [filteredSchedulingData, schedulingDataPage]);

    const paginatedContainableCallData = useMemo(() => {
        const start = containableDataPage * PAGE_SIZE;
        return filteredContainableCallData.slice(start, start + PAGE_SIZE);
    }, [filteredContainableCallData, containableDataPage]);

    const paginatedSpanishCallData = useMemo(() => {
        const start = spanishDataPage * PAGE_SIZE;
        return filteredSpanishCallData.slice(start, start + PAGE_SIZE);
    }, [filteredSpanishCallData, spanishDataPage]);

    // Pagination helpers
    const totalCallPages = Math.ceil(filteredCallData.length / PAGE_SIZE);
    const totalTransferPages = Math.ceil(filteredTransferData.length / PAGE_SIZE);
    const totalSchedulingPages = Math.ceil(filteredSchedulingData.length / PAGE_SIZE);
    const totalContainableCallPages = Math.ceil(filteredContainableCallData.length / PAGE_SIZE);
    const totalSpanishCallPages = Math.ceil(filteredSpanishCallData.length / PAGE_SIZE);

    // Reset pagination when filters change
    useEffect(() => { setCallDataPage(0); }, [callSearchQuery, selectedPhones, phoneFilterMode, selectedLocations, selectedTags, excludedTags, tagFilterMode, selectedCategories, showNonContainedContainableOnly, sortConfig]);

    useEffect(() => {
        if (!wasCallDataModalOpen.current && isCallDataModalOpen) {
            setSelectedCategories(new Set(defaultSelectedCallCategories));
            setSelectedTags(prev => new Set([...prev].filter(tag => callModalTagSet.includes(tag))));
            setExcludedTags(prev => new Set([...prev].filter(tag => callModalTagSet.includes(tag))));
            setTagFilterMode('all');
        }

        wasCallDataModalOpen.current = isCallDataModalOpen;
    }, [isCallDataModalOpen, defaultSelectedCallCategories, callModalTagSet]);
    useEffect(() => { setTransferDataPage(0); }, [transferSearchQuery, selectedTransferPhones, transferPhoneFilterMode, selectedTransferLocations, selectedTransferType, selectedTransferReasons, transferSortConfig]);
    useEffect(() => { setSchedulingDataPage(0); }, [schedulingSearchQuery, selectedSchedulingPhones, schedulingPhoneFilterMode, selectedSchedulingLocations, selectedSchedulingTags, selectedSchedulingCategories, schedulingSortConfig]);
    useEffect(() => { setContainableDataPage(0); }, [containableSearchQuery, selectedContainablePhones, containablePhoneFilterMode, selectedContainableLocations, selectedContainableTags, selectedContainableCategories, containableSortConfig]);
    useEffect(() => { setSpanishDataPage(0); }, [spanishSearchQuery, selectedSpanishPhones, spanishPhoneFilterMode, selectedSpanishLocations, selectedSpanishTags, selectedSpanishCategories, spanishSortConfig]);

    // Filter helpers
    const handleSort = (key: string) => setSortConfig(prev => toggleSort(key, prev));
    const handleTransferSort = (key: string) => setTransferSortConfig(prev => toggleSort(key, prev));
    const handleSchedulingSort = (key: string) => setSchedulingSortConfig(prev => toggleSort(key, prev));
    const handleByLocationSort = (key: string) => setByLocationSortConfig(prev => toggleSort(key, prev));
    const handleOutboundByLocationSort = (key: string) => setOutboundByLocationSortConfig(prev => toggleSort(key, prev));

    const handleRemovePhoneFilter = (phone: string) => {
        const newSet = new Set(selectedPhones);
        newSet.delete(phone);
        setSelectedPhones(newSet);
    };

    const handleAddTransferPhoneFilter = (phone: string) => {
        const newSet = new Set(selectedTransferPhones);
        if (newSet.has(phone)) {
            newSet.delete(phone);
        } else {
            newSet.add(phone);
        }
        setSelectedTransferPhones(newSet);
    };

    const clearAllFilters = () => {
        setCallSearchQuery('');
        setSelectedPhones(new Set());
        setPhoneFilterMode('include');
        setSelectedLocations(new Set());
        setSelectedTags(new Set());
        setExcludedTags(new Set());
        setTagFilterMode('all');
        setSelectedCategories(new Set(defaultSelectedCallCategories));
        setShowNonContainedContainableOnly(false);
        setSortConfig(null);
        setOpenColumnFilter(null);
    };

    const clearAllTransferFilters = () => {
        setTransferSearchQuery('');
        setSelectedTransferPhones(new Set());
        setTransferPhoneFilterMode('include');
        setSelectedTransferLocations(new Set());
        setSelectedTransferType('all');
        setSelectedTransferReasons(new Set());
        setTransferSortConfig(null);
        setOpenTransferColumnFilter(null);
    };

    const clearAllSchedulingFilters = () => {
        setSchedulingSearchQuery('');
        setSelectedSchedulingPhones(new Set());
        setSchedulingPhoneFilterMode('include');
        setSelectedSchedulingLocations(new Set());
        setSelectedSchedulingTags(new Set());
        setSelectedSchedulingCategories(new Set());
        setSchedulingSortConfig(null);
        setOpenSchedulingColumnFilter(null);
    };

    const handleAddSchedulingPhoneFilter = (phone: string) => {
        const newSet = new Set(selectedSchedulingPhones);
        if (newSet.has(phone)) {
            newSet.delete(phone);
        } else {
            newSet.add(phone);
        }
        setSelectedSchedulingPhones(newSet);
    };

    const clearAllContainableFilters = () => {
        setContainableSearchQuery('');
        setSelectedContainablePhones(new Set());
        setContainablePhoneFilterMode('include');
        setSelectedContainableLocations(new Set());
        setSelectedContainableTags(new Set());
        setSelectedContainableCategories(new Set());
        setContainableSortConfig(null);
        setOpenContainableColumnFilter(null);
    };

    const handleAddContainablePhoneFilter = (phone: string) => {
        const newSet = new Set(selectedContainablePhones);
        if (newSet.has(phone)) {
            newSet.delete(phone);
        } else {
            newSet.add(phone);
        }
        setSelectedContainablePhones(newSet);
    };

    const handleContainableSort = (key: string) => setContainableSortConfig(prev => toggleSort(key, prev));

    const clearAllSpanishFilters = () => {
        setSpanishSearchQuery('');
        setSelectedSpanishPhones(new Set());
        setSpanishPhoneFilterMode('include');
        setSelectedSpanishLocations(new Set());
        setSelectedSpanishTags(new Set());
        setSelectedSpanishCategories(new Set());
        setSpanishSortConfig(null);
        setOpenSpanishColumnFilter(null);
    };

    const handleAddSpanishPhoneFilter = (phone: string) => {
        const newSet = new Set(selectedSpanishPhones);
        if (newSet.has(phone)) {
            newSet.delete(phone);
        } else {
            newSet.add(phone);
        }
        setSelectedSpanishPhones(newSet);
    };

    const handleSpanishSort = (key: string) => setSpanishSortConfig(prev => toggleSort(key, prev));

    const hasCustomCategoryFilter = selectedCategories.size !== defaultSelectedCallCategories.size || [...selectedCategories].some(category => !defaultSelectedCallCategories.has(category));
    const hasActiveFilters = !!(callSearchQuery || selectedPhones.size > 0 || selectedLocations.size > 0 || selectedTags.size > 0 || excludedTags.size > 0 || hasCustomCategoryFilter || showNonContainedContainableOnly);
    // isExportFiltered reflects whether the actual exported rows are a filtered subset of callDataRecords.
    // Unlike hasActiveFilters, this treats the baseline category exclusion (NO_INTERACTION) as a filter
    // because dataToExport always applies selectedCategories, so any deviation from the full visible category set produces
    // fewer exported rows than callDataRecords.length.
    const isExportFiltered = !!(callSearchQuery || selectedPhones.size > 0 || selectedLocations.size > 0 || selectedTags.size > 0 || excludedTags.size > 0 || showNonContainedContainableOnly || selectedCategories.size !== uniqueCallCategories.length || uniqueCallCategories.some(category => !selectedCategories.has(category)));
    const hasActiveTransferFilters = transferSearchQuery || selectedTransferPhones.size > 0 || selectedTransferLocations.size > 0 || selectedTransferType !== 'all' || selectedTransferReasons.size > 0;
    const hasActiveSchedulingFilters = schedulingSearchQuery || selectedSchedulingPhones.size > 0 || selectedSchedulingLocations.size > 0 || selectedSchedulingTags.size > 0 || selectedSchedulingCategories.size > 0;
    const hasActiveContainableFilters = containableSearchQuery || selectedContainablePhones.size > 0 || selectedContainableLocations.size > 0 || selectedContainableTags.size > 0 || selectedContainableCategories.size > 0;
    const hasActiveSpanishFilters = spanishSearchQuery || selectedSpanishPhones.size > 0 || selectedSpanishLocations.size > 0 || selectedSpanishTags.size > 0 || selectedSpanishCategories.size > 0;

    // Determine if only one app is selected (for Concurrent Calls option visibility)
    const isSingleAppSelected = selectedEntityCount === 1;

    // Reset to All Conversations if Concurrent Calls is selected but multiple apps are now selected
    useEffect(() => {
        if (!isSingleAppSelected && engagementHistogramContainer.selectedView === 'Concurrent Calls') {
            engagementHistogramContainer.setSelectedView('All Conversations');
        }
    }, [isSingleAppSelected, engagementHistogramContainer.selectedView]);

    // Get histogram data
    // When Spanish Only filter is active, force the view to "Spanish Calls"
    const selectedView = isSpanishOnlyFilter
        ? "Spanish Calls" as HistogramViewOption
        : (engagementHistogramContainer.selectedView || "All Conversations") as HistogramViewOption;
    const currentHistogramData = engagementHistogramContainer.currentHistogramData;

    // Compute appointments managed by filtering from organizationEngagements by tags
    // Uses tags: ACTION_COMPLETED, APPOINTMENT_RESCHEDULED, APPOINTMENT_CANCELLED, CHECK_NEXT_APPOINTMENT_COMPLETE
    // NOTE: Count tag occurrences (not engagements) to handle multipatient bookings where
    // multiple scheduling actions can occur in a single call
    const schedulingEventsData = React.useMemo((): SchedulingEventData[] | null => {
        if (isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements) return null;

        const allEngagements = engagements || [];

        // Get engagements that have a specific tag (for iteration purposes)
        const filterByTag = (tag: string) => {
            return allEngagements.filter(e => {
                const tags = e.sessionTags || [];
                return tags.includes(tag);
            });
        };

        // Get engagements for each appointment managed type by tag
        // Exclude genuine outbound reactivation bookings from inbound — those are counted in the Outbound stack.
        // Outbound calls that also have OUTBOUND_APPOINTMENT_CONFIRMED are confirmation calls that
        // re-booked; their ACTION_COMPLETED still counts as inbound since the booking was inbound-initiated.
        const scheduled = filterByTag(TAGS.ACTION_COMPLETED).filter(e => {
            const tags = e.sessionTags || [];
            if (!tags.includes(TAGS.OUTBOUND_CALL)) return true;                       // inbound call — include
            if (tags.includes(TAGS.OUTBOUND_APPOINTMENT_CONFIRMED)) return true;       // outbound confirmation re-book — include in inbound
            return false;                                                               // genuine outbound reactivation booking — exclude
        });
        const rescheduled = filterByTag(TAGS.APPOINTMENT_RESCHEDULED).filter(e => !(e.sessionTags || []).includes(TAGS.OUTBOUND_CALL));
        const cancelled = filterByTag(TAGS.APPOINTMENT_CANCELLED).filter(e => !(e.sessionTags || []).includes(TAGS.OUTBOUND_CALL));
        const checkNextAppt = filterByTag(TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE);

        // Helper to filter engagements by selectedAppIds and Spanish-only filter
        const filterEngagements = (engagements: any[]) => {
            // If no apps are selected, return empty (show empty state)
            if (!entityIds || entityIds.size === 0) {
                return [];
            }
            let filtered = engagements.filter(e => isPlatformMode ? entityIds.has(e.organizationId) : selectedAppIds.has(e.applicationId));
            // Filter by Spanish-only if active
            if (isSpanishOnlyFilter) {
                filtered = filtered.filter(isSpanishEngagement);
            }
            return filtered;
        };

        // Helper to count total tag occurrences across engagements (handles multipatient bookings)
        const countTagOccurrences = (engagements: any[], tag: string) => {
            return engagements.reduce((total, e) => {
                const tags = e.sessionTags || [];
                const tagCount = tags.filter(t => t === tag).length;
                return total + tagCount;
            }, 0);
        };

        // Helper to group engagements by app and count TAG OCCURRENCES with language breakdown
        // This handles multipatient bookings where one call may have multiple occurrences of the same tag
        const groupByAppWithTagCounts = (engagements: any[], tag: string) => {
            const appCounts: Record<string, { count: number; english: number; spanish: number }> = {};
            engagements.forEach(e => {
                const appId = isPlatformMode ? (e.organizationId || 'unknown') : (e.applicationId || 'unknown');
                const tags = e.sessionTags || [];
                const tagCount = tags.filter(t => t === tag).length;
                if (tagCount === 0) return;
                if (!appCounts[appId]) {
                    appCounts[appId] = { count: 0, english: 0, spanish: 0 };
                }
                appCounts[appId].count += tagCount;
                if (isSpanishEngagement(e)) {
                    appCounts[appId].spanish += tagCount;
                } else {
                    appCounts[appId].english += tagCount;
                }
            });
            return Object.entries(appCounts).map(([appId, data]) => ({
                appName: locationNameMap[appId] || appId,
                count: data.count,
                english: data.english,
                spanish: data.spanish
            }));
        };

        const filteredScheduled = filterEngagements(scheduled);
        const filteredRescheduled = filterEngagements(rescheduled);
        const filteredCancelled = filterEngagements(cancelled);
        const filteredCheckNextAppt = filterEngagements(checkNextAppt);

        const result: SchedulingEventData[] = [
            { type: 'Schedule', count: countTagOccurrences(filteredScheduled, TAGS.ACTION_COMPLETED), color: chartSequentialBlues[0], apps: groupByAppWithTagCounts(filteredScheduled, TAGS.ACTION_COMPLETED) },
            { type: 'Reschedule', count: countTagOccurrences(filteredRescheduled, TAGS.APPOINTMENT_RESCHEDULED), color: chartSequentialBlues[0], apps: groupByAppWithTagCounts(filteredRescheduled, TAGS.APPOINTMENT_RESCHEDULED) },
            { type: 'Cancel', count: countTagOccurrences(filteredCancelled, TAGS.APPOINTMENT_CANCELLED), color: chartSequentialBlues[0], apps: groupByAppWithTagCounts(filteredCancelled, TAGS.APPOINTMENT_CANCELLED) },
            { type: 'Check Appt', count: countTagOccurrences(filteredCheckNextAppt, TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE), color: chartSequentialBlues[0], apps: groupByAppWithTagCounts(filteredCheckNextAppt, TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE) }
        ];

        // Check if there's any actual scheduling data
        const hasData = result.some(item => item.count > 0);

        return hasData ? result : [];
    }, [
        engagements,
        orgAnalyticsContainer.loadingOrganizationEngagements,
        orgAnalyticsContainer.loadingPlatformEngagements,
        selectedAppIds,
        entityIds,
        isSpanishOnlyFilter,
        locationNameMap,
        isPlatformMode
    ]);

    // Compute new patient bookings by counting ACTION_COMPLETED tag occurrences in NEW_PATIENT engagements
    // NOTE: Count tag occurrences (not engagements) to handle multipatient bookings where
    // multiple scheduling actions can occur in a single call - same approach as schedulingEventsData
    const newPatientBookingsData = React.useMemo((): { count: number; apps: { appName: string; count: number; english: number; spanish: number }[] } => {
        if (isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements) {
            return { count: 0, apps: [] };
        }

        const allEngagements = engagements || [];

        // Filter to engagements that have NEW_PATIENT tag
        const newPatientEngagements = allEngagements.filter(e => {
            const tags = e.sessionTags || [];
            return tags.includes('NEW_PATIENT');
        });

        // Apply selectedAppIds filter
        let filtered = newPatientEngagements;
        if (entityIds && entityIds.size > 0) {
            filtered = filtered.filter(e => isPlatformMode ? entityIds.has(e.organizationId) : selectedAppIds.has(e.applicationId));
        } else {
            // No apps selected = empty state
            return { count: 0, apps: [] };
        }

        // Apply Spanish-only filter if active
        if (isSpanishOnlyFilter) {
            filtered = filtered.filter(isSpanishEngagement);
        }

        // Count ACTION_COMPLETED tag occurrences (handles multipatient bookings)
        let totalCount = 0;
        const appCounts: Record<string, { count: number; english: number; spanish: number }> = {};

        filtered.forEach(e => {
            const tags = e.sessionTags || [];
            const tagCount = tags.filter(t => t === 'ACTION_COMPLETED').length;
            if (tagCount === 0) return;

            totalCount += tagCount;
            const appId = isPlatformMode ? (e.organizationId || 'unknown') : (e.applicationId || 'unknown');
            if (!appCounts[appId]) {
                appCounts[appId] = { count: 0, english: 0, spanish: 0 };
            }
            appCounts[appId].count += tagCount;
            if (isSpanishEngagement(e)) {
                appCounts[appId].spanish += tagCount;
            } else {
                appCounts[appId].english += tagCount;
            }
        });

        const apps = Object.entries(appCounts).map(([appId, data]) => ({
            appName: locationNameMap[appId] || appId,
            count: data.count,
            english: data.english,
            spanish: data.spanish
        }));

        return { count: totalCount, apps };
    }, [
        engagements,
        orgAnalyticsContainer.loadingOrganizationEngagements,
        orgAnalyticsContainer.loadingPlatformEngagements,
        selectedAppIds,
        entityIds,
        isSpanishOnlyFilter,
        locationNameMap,
        isPlatformMode
    ]);

    // Compute outbound call metrics from engagement analytics events.
    const outboundMetrics = React.useMemo((): { confirmed: number; rescheduled: number; cancelled: number; leftMessage: number; transferred: number; scheduled: number; notCaptured: number } => {
        if (isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements) {
            return { confirmed: 0, rescheduled: 0, cancelled: 0, leftMessage: 0, transferred: 0, scheduled: 0, notCaptured: 0 };
        }

        if (!entityIds || entityIds.size === 0) {
            return { confirmed: 0, rescheduled: 0, cancelled: 0, leftMessage: 0, transferred: 0, scheduled: 0, notCaptured: 0 };
        }
        const filtered = filteredEngagements || [];

        const countTag = (tag: string) =>
            filtered.reduce((total, e) => {
                const tags = e.sessionTags || [];
                const tagCount = tags.filter(t => t === tag).length;
                return total + tagCount;
            }, 0);

        const outboundOutcomeTags = [
            TAGS.OUTBOUND_APPOINTMENT_CONFIRMED,
            TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED,
            TAGS.OUTBOUND_APPOINTMENT_CANCELLED,
            TAGS.OUTBOUND_LEFT_MESSAGE,
            TAGS.OUTBOUND_CALL_TRANSFERRED,
            'OUTBOUND_APPOINTMENT_BOOKED',
            'OUTBOUND-APPOINTMENT-BOOKED',
        ];

        // Count calls where OUTBOUND_CALL was fired but no outcome tag was captured
        const notCaptured = filtered.filter(e => {
            const tags = e.sessionTags || [];
            return tags.includes(TAGS.OUTBOUND_CALL) &&
                !outboundOutcomeTags.some(t => tags.includes(t)) &&
                !tags.some(isOutboundAppointmentBookedTag);
        }).length;

        return {
            confirmed: countTag(TAGS.OUTBOUND_APPOINTMENT_CONFIRMED),
            rescheduled: countTag(TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED),
            cancelled: countTag(TAGS.OUTBOUND_APPOINTMENT_CANCELLED),
            leftMessage: countTag(TAGS.OUTBOUND_LEFT_MESSAGE),
            transferred: countTag(TAGS.OUTBOUND_CALL_TRANSFERRED),
            scheduled: outboundScheduledFromDetailRecords,
            notCaptured,
        };
    }, [
        filteredEngagements,
        orgAnalyticsContainer.loadingOrganizationEngagements,
        orgAnalyticsContainer.loadingPlatformEngagements,
        entityIds,
        isPlatformMode,
        isOutboundAppointmentBookedTag,
        outboundScheduledFromDetailRecords,
    ]);

    const [appointmentSummary, setAppointmentSummary] = useState<AppointmentSummaryResponse | null>(null);
    const [isLoadingAppointmentSummary, setIsLoadingAppointmentSummary] = useState(false);
    const [appointmentSummaryLoadCompleted, setAppointmentSummaryLoadCompleted] = useState(false);
    const [appointmentSummaryByOrganization, setAppointmentSummaryByOrganization] = useState<Record<string, AppointmentSummaryResponse>>({});
    const [appointmentSummaryLoadingAppNames, setAppointmentSummaryLoadingAppNames] = useState<string[]>([]);
    const [appointmentSummaryHasFailedBatches, setAppointmentSummaryHasFailedBatches] = useState(false);
    const [appointmentSummaryProgress, setAppointmentSummaryProgress] = useState({
        total: 0,
        loadedOfficeNames: [] as string[],
        failedOfficeNames: [] as string[]
    });
    const [isAppointmentSummaryModalOpen, setIsAppointmentSummaryModalOpen] = useState(false);

    // Stable string key so the effect only re-runs when the actual IDs change,
    // not on every new Set reference created by progressive locationApps updates.
    const selectedAppIdsKey = React.useMemo(
        () => Array.from(selectedAppIds ?? []).sort().join(','),
        [selectedAppIds]
    );

    const selectedOrganizationIdsKey = React.useMemo(
        () => Array.from(entityIds ?? []).sort().join(','),
        [entityIds]
    );

    // Primitive key based on the IDs actually sent to the appointment-summary API.
    // Equivalent app/status map objects therefore do not retrigger the request.
    const appointmentSummaryScopeKey = React.useMemo(() => {
        if (isPlatformMode) {
            return Array.from(entityIds ?? []).sort().map(selectedOrgId => {
                const liveAppIds = (platformAppIdsByOrganizationId?.[selectedOrgId] || [])
                    .filter(appId => selectedAppLiveStatusMap?.[appId] === true)
                    .sort();
                return `${selectedOrgId}:${liveAppIds.join(',')}`;
            }).join('|');
        }

        const liveAppIds = Array.from(selectedAppIds ?? [])
            .filter(appId => selectedAppLiveStatusMap?.[appId] === true)
            .sort();
        return `${organizationId ?? ''}:${liveAppIds.join(',')}`;
    }, [
        organizationId,
        isPlatformMode,
        selectedAppIdsKey,
        selectedOrganizationIdsKey,
        selectedAppLiveStatusMap,
        platformAppIdsByOrganizationId
    ]);

    useEffect(() => {
        if (!shouldLoadData || !isOutboundCallApp || !startDate || !endDate || !appointmentSummaryScopeKey) return;
        let cancelled = false;
        const load = async () => {
            setIsLoadingAppointmentSummary(true);
            setAppointmentSummaryLoadCompleted(false);
            setAppointmentSummary(null);
            setAppointmentSummaryByOrganization({});
            setAppointmentSummaryLoadingAppNames([]);
            setAppointmentSummaryHasFailedBatches(false);
            setAppointmentSummaryProgress({
                total: 0,
                loadedOfficeNames: [],
                failedOfficeNames: []
            });
            try {
                const { rangeStart, rangeEnd } = getSelectedDateRangeMoments(startDate, endDate);
                const requestStartDate = rangeStart.format("YYYY-MM-DDTHH:mm:ss.SSS");
                const requestEndDate = rangeEnd.format("YYYY-MM-DDTHH:mm:ss.SSS");
                const batchRequests: { organizationId: string; applicationIds: string[] }[] = [];
                if (isPlatformMode) {
                    Array.from(entityIds ?? []).sort().forEach(selectedOrgId => {
                        const liveAppIds = (platformAppIdsByOrganizationId?.[selectedOrgId] || [])
                            .filter(appId => selectedAppLiveStatusMap?.[appId] === true)
                            .sort();
                        createAppointmentSummaryBatches(liveAppIds).forEach(applicationIds => {
                            batchRequests.push({ organizationId: selectedOrgId, applicationIds });
                        });
                    });
                } else if (organizationId) {
                    const liveAppIds = Array.from(selectedAppIds ?? [])
                        .filter(appId => selectedAppLiveStatusMap?.[appId] === true)
                        .sort();
                    createAppointmentSummaryBatches(liveAppIds).forEach(applicationIds => {
                        batchRequests.push({ organizationId, applicationIds });
                    });
                }

                const totalOfficeCount = batchRequests.reduce((total, batch) => total + batch.applicationIds.length, 0);
                const loadedOfficeNames: string[] = [];
                const failedOfficeNames: string[] = [];
                setAppointmentSummaryProgress({
                    total: totalOfficeCount,
                    loadedOfficeNames: [],
                    failedOfficeNames: []
                });

                let aggregated = createEmptyAppointmentSummary();
                const perOrganization: Record<string, AppointmentSummaryResponse> = {};
                for (const batch of batchRequests) {
                    if (cancelled) return;
                    const batchOfficeNames = batch.applicationIds.map(appId => appNameMap?.[appId] || appId);
                    let batchSucceeded = false;
                    setAppointmentSummaryLoadingAppNames(batchOfficeNames);

                    try {
                        const result = await getAppointmentSummary(batch.organizationId, {
                            applicationIds: batch.applicationIds,
                            dateFrom: requestStartDate,
                            dateTo: requestEndDate,
                        });
                        if (cancelled) return;
                        if (result.resultType !== 'Ok') {
                            setAppointmentSummaryHasFailedBatches(true);
                            continue;
                        }

                        aggregated = mergeAppointmentSummaries(aggregated, result.data);
                        perOrganization[batch.organizationId] = mergeAppointmentSummaries(
                            perOrganization[batch.organizationId] ?? createEmptyAppointmentSummary(),
                            result.data
                        );

                        setAppointmentSummary(hasAppointmentSummaryData(aggregated) ? aggregated : null);
                        setAppointmentSummaryByOrganization(isPlatformMode ? { ...perOrganization } : {});
                        batchSucceeded = true;
                    } catch {
                        // Keep completed batches visible and continue loading the remaining applications.
                        if (!cancelled) {
                            setAppointmentSummaryHasFailedBatches(true);
                        }
                    } finally {
                        if (!cancelled) {
                            (batchSucceeded ? loadedOfficeNames : failedOfficeNames).push(...batchOfficeNames);
                            setAppointmentSummaryProgress({
                                total: totalOfficeCount,
                                loadedOfficeNames: [...loadedOfficeNames],
                                failedOfficeNames: [...failedOfficeNames]
                            });
                        }
                    }
                }
            } catch {
                if (!cancelled) {
                    setAppointmentSummary(null);
                    setAppointmentSummaryByOrganization({});
                    setAppointmentSummaryHasFailedBatches(true);
                }
            } finally {
                if (!cancelled) {
                    setAppointmentSummaryLoadingAppNames([]);
                    setIsLoadingAppointmentSummary(false);
                    setAppointmentSummaryLoadCompleted(true);
                }
            }
        };
        load();
        return () => { cancelled = true; };
    }, [appointmentSummaryScopeKey, startDate, endDate, isOutboundCallApp, refreshKey, isPlatformMode, shouldLoadData]);

    const appointmentResponseMetrics = React.useMemo(
        () => calculateOutboundAppointmentSuccess(appointmentSummary),
        [appointmentSummary]
    );
    const appointmentResponseRate = appointmentResponseMetrics.rate;
    const appointmentSummaryHasData = hasAppointmentSummaryData(appointmentSummary);
    const displayedAppointmentResponseRate = appointmentSummaryHasData
        ? appointmentResponseRate ?? 0
        : null;
    const appointmentSummaryStatusLabel = 'Partial results';
    const appointmentSummaryStatusTitle = 'Some appointment data could not be loaded. Totals shown are incomplete.';
    const appointmentSummaryProgressPercent = appointmentSummaryProgress.total > 0
        ? Math.round((appointmentSummaryProgress.loadedOfficeNames.length / appointmentSummaryProgress.total) * 100)
        : 0;
    const appointmentSummaryProgressTitle = [
        appointmentSummaryProgress.loadedOfficeNames.length > 0
            ? `Loaded ${appointmentSummaryProgress.loadedOfficeNames.length} of ${appointmentSummaryProgress.total} offices: ${appointmentSummaryProgress.loadedOfficeNames.join(', ')}`
            : `Loaded 0 of ${appointmentSummaryProgress.total} offices`,
        appointmentSummaryProgress.failedOfficeNames.length > 0
            ? `Could not load: ${appointmentSummaryProgress.failedOfficeNames.join(', ')}`
            : null,
        appointmentSummaryLoadingAppNames.length > 0
            ? `Loading now: ${appointmentSummaryLoadingAppNames.join(', ')}`
            : null
    ].filter(Boolean).join('\n');
    const outboundAppointmentChartMetrics = React.useMemo(
        () => getOutboundAppointmentChartMetrics(appointmentSummary, {
            confirmed: appointmentSummaryLoadCompleted ? outboundMetrics?.confirmed ?? 0 : 0,
            leftMessage: appointmentSummaryLoadCompleted ? outboundLeftMessageSessionCount : 0
        }),
        [appointmentSummary, appointmentSummaryLoadCompleted, outboundMetrics?.confirmed, outboundLeftMessageSessionCount]
    );
    const outboundScheduledCount = outboundMetrics?.scheduled ?? 0;
    const hasOutboundData = isOutboundCallApp && (
        appointmentSummaryHasData ||
        outboundAppointmentChartMetrics.confirmed > 0 ||
        outboundMetrics.rescheduled > 0 ||
        outboundMetrics.cancelled > 0 ||
        outboundLeftMessageSessionCount > 0 ||
        outboundMetrics.transferred > 0 ||
        outboundScheduledCount > 0 ||
        outboundMetrics.notCaptured > 0
    );
    const outboundRescheduledCount = outboundMetrics?.rescheduled ?? 0;
    const outboundCancelledCount = outboundMetrics?.cancelled ?? 0;
    const appointmentsManagedCount = (schedulingEventsData?.reduce((sum, item) => sum + item.count, 0) ?? 0) +
        (isOutboundCallApp ? outboundScheduledCount + outboundRescheduledCount + outboundCancelledCount : 0);

    // Filter and aggregate metrics based on selectedAppIds (client-side filtering)
    // When isSpanishOnlyFilter is true, only aggregate Spanish-specific metrics
    const healthcareMetrics = React.useMemo((): DashboardHealthcareMetrics | null => {
        const perAppMetrics = isPlatformMode ? orgAnalyticsContainer.platformTopLevelMetricsPerOrg : orgAnalyticsContainer.organizationTopLevelMetricsPerApp;
        const isLoadingMetrics = isPlatformMode ? orgAnalyticsContainer.loadingPlatformTopLevelMetrics : orgAnalyticsContainer.loadingOrganizationTopLevelMetrics;
        const isLoadingOffHours = isPlatformMode ? false : orgAnalyticsContainer.loadingOffHoursEngagements;

        if (isLoadingMetrics || isLoadingOffHours) return null;

        // Filter metrics by selected app IDs and optionally Spanish-only
        const filteredMetrics = aggregateTopLevelMetricsFiltered(perAppMetrics, isPlatformMode ? entityIds : selectedAppIds, isSpanishOnlyFilter);

        if (filteredMetrics && (
            filteredMetrics.laborMinutesSaved !== undefined ||
            filteredMetrics.laborHoursSaved !== undefined ||
            filteredMetrics.containedCalls !== undefined
        )) {
            return transformToHealthcareMetrics(filteredMetrics);
        }

        return {
            laborHoursSaved: 0,
            offHoursCalls: 0,
            offHoursScheduledAppts: 0,
            containedCallsCount: 0,
            containableCallsCount: 0,
            totalEngagements: 0,
            newPatientBookings: 0,
            laborHoursSavedApps: [],
            offHoursApps: [],
            offHoursScheduledApptsApps: [],
            newPatientBookingsApps: []
        };
    }, [orgAnalyticsContainer.organizationTopLevelMetricsPerApp, orgAnalyticsContainer.platformTopLevelMetricsPerOrg, orgAnalyticsContainer.loadingOrganizationTopLevelMetrics, orgAnalyticsContainer.loadingPlatformTopLevelMetrics, orgAnalyticsContainer.loadingOffHoursEngagements, selectedAppIds, entityIds, appNameMap, isSpanishOnlyFilter, isPlatformMode]);

    const platformSchedulingBarChartData = useMemo((): PlatformSchedulingBarChartDatum[] => {
        if (!isPlatformMode) {
            return [];
        }

        const allEngagements = engagements || [];

        const getOrganizationContainmentCounts = (organizationId: string) => allEngagements.reduce((counts, engagement) => {
            if (engagement.organizationId !== organizationId) {
                return counts;
            }

            if (isSpanishOnlyFilter && !isSpanishEngagement(engagement)) {
                return counts;
            }

            const tags = engagement.sessionTags || [];
            if (tags.includes(TAGS.OUTBOUND_CALL)) {
                return counts;
            }

            if (engagement.isContainable) {
                counts.containableCallsCount += 1;
            }

            if (engagement.isContained) {
                counts.containedCallsCount += 1;
            }

            return counts;
        }, { containableCallsCount: 0, containedCallsCount: 0 });

        const getActionCompletedCount = (organizationId: string) => allEngagements.reduce((total, engagement) => {
            if (engagement.organizationId !== organizationId) {
                return total;
            }

            if (isSpanishOnlyFilter && !isSpanishEngagement(engagement)) {
                return total;
            }

            const tags = engagement.sessionTags || [];
            return total + tags.filter(tag => tag === TAGS.ACTION_COMPLETED).length;
        }, 0);

        const getTagOccurrenceCount = (organizationId: string, tagToCount: string) => allEngagements.reduce((total, engagement) => {
            if (engagement.organizationId !== organizationId) {
                return total;
            }

            if (isSpanishOnlyFilter && !isSpanishEngagement(engagement)) {
                return total;
            }

            const tags = engagement.sessionTags || [];
            return total + tags.filter(tag => tag === tagToCount).length;
        }, 0);

        const getTotalBillableMinutes = (organizationId: string) => allEngagements.reduce((total, engagement) => {
            if (engagement.organizationId !== organizationId) {
                return total;
            }

            if (isSpanishOnlyFilter && !isSpanishEngagement(engagement)) {
                return total;
            }

            return total + (engagement.preTransferDurationMinutes || 0);
        }, 0);

        return Array.from(entityIds).map((organizationId) => {
            const { containableCallsCount, containedCallsCount } = getOrganizationContainmentCounts(organizationId);
            const containmentRate = containableCallsCount > 0
                ? Math.round((containedCallsCount / containableCallsCount) * 1000) / 10
                : 0;
            const appointmentSummaryForOrg = appointmentSummaryByOrganization[organizationId];
            const totalOutboundCalls = getTagOccurrenceCount(organizationId, TAGS.OUTBOUND_CALL);
            const outboundAppointmentMetricsForOrg = getOutboundAppointmentChartMetrics(
                appointmentSummaryForOrg,
                {
                    confirmed: getTagOccurrenceCount(organizationId, TAGS.OUTBOUND_APPOINTMENT_CONFIRMED),
                    leftMessage: getTagOccurrenceCount(organizationId, TAGS.OUTBOUND_LEFT_MESSAGE)
                }
            );
            const totalOutboundAppointmentsConfirmed = outboundAppointmentMetricsForOrg.confirmed;
            const totalOutboundLeftMessage = outboundAppointmentMetricsForOrg.leftMessage;
            const outboundSuccessMetrics = calculateOutboundAppointmentSuccess(
                appointmentSummaryForOrg,
                1
            );
            const outboundResponseRateDenominator = outboundSuccessMetrics.denominator;
            const outboundResponseRate = outboundSuccessMetrics.rate ?? 0;

            return {
                organizationId,
                organizationLabel: organizationId,
                organizationName: locationNameMap[organizationId] || organizationId,
                containmentRate,
                appointmentScheduledVolume: getActionCompletedCount(organizationId),
                totalBillableMinutes: getTotalBillableMinutes(organizationId),
                totalOutboundCalls,
                totalOutboundAppointmentsConfirmed,
                totalOutboundLeftMessage,
                outboundResponseRate,
                outboundResponseRateDenominator,
                containableCallsCount,
                containedCallsCount
            };
        });
    }, [engagements, entityIds, isPlatformMode, isSpanishOnlyFilter, locationNameMap, appointmentSummaryByOrganization]);

    const platformSchedulingBarChartConfig = useMemo(() => {
        if (platformBarChartView === 'outboundResponseRate') {
            return {
                title: 'Outbound Success Rate by Organization',
                metricLabel: 'Outbound Success Rate',
                metricKey: 'OUTBOUND_APPOINTMENT_RESPONSE_RATE_HEALTHCARE',
                dataKey: 'outboundResponseRate' as const,
                valueLabel: 'rate',
                axisFormat: (value: number | string) => `${value}%`,
                barLabel: (value: number) => `${Number(value).toFixed(1)}%`,
                emptyState: 'We could not find any outbound appointment outcome data for the selected organizations in this date range.',
                hasData: (data: PlatformSchedulingBarChartDatum[]) => data.some(item => item.outboundResponseRateDenominator > 0),
                tooltipValue: (data: PlatformSchedulingBarChartDatum) => `${data.outboundResponseRate.toFixed(1)}%`,
                tooltipSubtitle: (data: PlatformSchedulingBarChartDatum) => `${data.organizationName} outbound success rate across tracked appointments`,
                valueScale: { type: 'linear' as const, min: 0, max: 100 },
                ariaLabel: 'Outbound success rate by organization'
            };
        }

        if (platformBarChartView === 'totalOutboundLeftMessage') {
            return {
                title: 'Total Outbound Left Message by Organization',
                metricLabel: 'Outbound Left Message',
                metricKey: 'OUTBOUND_LEFT_MESSAGE_HEALTHCARE',
                dataKey: 'totalOutboundLeftMessage' as const,
                valueLabel: 'calls',
                axisFormat: (value: number | string) => Number(value).toLocaleString(),
                barLabel: (value: number) => Number(value).toLocaleString(),
                emptyState: 'We could not find any outbound left-message activity for the selected organizations in this date range.',
                hasData: (data: PlatformSchedulingBarChartDatum[]) => data.some(item => item.totalOutboundLeftMessage > 0),
                tooltipValue: (data: PlatformSchedulingBarChartDatum) => data.totalOutboundLeftMessage.toLocaleString(),
                tooltipSubtitle: (data: PlatformSchedulingBarChartDatum) => `${data.organizationName} outbound calls that resulted in a left message`,
                valueScale: { type: 'linear' as const, min: 0 },
                ariaLabel: 'Total outbound left message by organization'
            };
        }

        if (platformBarChartView === 'totalOutboundAppointmentsConfirmed') {
            return {
                title: 'Total Outbound Appointments Confirmed by Organization',
                metricLabel: 'Outbound Confirmed',
                metricKey: 'OUTBOUND_CONFIRMED_HEALTHCARE',
                dataKey: 'totalOutboundAppointmentsConfirmed' as const,
                valueLabel: 'appointments',
                axisFormat: (value: number | string) => Number(value).toLocaleString(),
                barLabel: (value: number) => Number(value).toLocaleString(),
                emptyState: 'We could not find any outbound confirmed appointments for the selected organizations in this date range.',
                hasData: (data: PlatformSchedulingBarChartDatum[]) => data.some(item => item.totalOutboundAppointmentsConfirmed > 0),
                tooltipValue: (data: PlatformSchedulingBarChartDatum) => data.totalOutboundAppointmentsConfirmed.toLocaleString(),
                tooltipSubtitle: (data: PlatformSchedulingBarChartDatum) => `${data.organizationName} outbound appointments confirmed by Voicify`,
                valueScale: { type: 'linear' as const, min: 0 },
                ariaLabel: 'Total outbound appointments confirmed by organization'
            };
        }

        if (platformBarChartView === 'totalOutboundCalls') {
            return {
                title: 'Total Outbound Calls by Organization',
                metricLabel: 'Outbound Calls',
                metricKey: 'OUTBOUND_CALLS_HEALTHCARE',
                dataKey: 'totalOutboundCalls' as const,
                valueLabel: 'calls',
                axisFormat: (value: number | string) => Number(value).toLocaleString(),
                barLabel: (value: number) => Number(value).toLocaleString(),
                emptyState: 'We could not find any outbound calls for the selected organizations in this date range.',
                hasData: (data: PlatformSchedulingBarChartDatum[]) => data.some(item => item.totalOutboundCalls > 0),
                tooltipValue: (data: PlatformSchedulingBarChartDatum) => data.totalOutboundCalls.toLocaleString(),
                tooltipSubtitle: (data: PlatformSchedulingBarChartDatum) => `${data.organizationName} total outbound calls`,
                valueScale: { type: 'linear' as const, min: 0 },
                ariaLabel: 'Total outbound calls by organization'
            };
        }

        if (platformBarChartView === 'totalBillableMinutes') {
            return {
                title: 'Total Billable Minutes by Organization',
                metricLabel: 'Billable Minutes',
                metricKey: 'BILLABLE_MINUTES_HEALTHCARE',
                dataKey: 'totalBillableMinutes' as const,
                valueLabel: 'minutes',
                axisFormat: (value: number | string) => Number(value).toFixed(1),
                barLabel: (value: number) => Number(value).toFixed(1),
                emptyState: 'We could not find any billable pre-transfer call time for the selected organizations in this date range.',
                hasData: (data: PlatformSchedulingBarChartDatum[]) => data.some(item => item.totalBillableMinutes > 0),
                tooltipValue: (data: PlatformSchedulingBarChartDatum) => `${data.totalBillableMinutes.toFixed(1)} min`,
                tooltipSubtitle: (data: PlatformSchedulingBarChartDatum) => `${data.organizationName} total pre-transfer AI minutes`,
                valueScale: { type: 'linear' as const, min: 0 },
                ariaLabel: 'Total billable minutes by organization'
            };
        }

        if (platformBarChartView === 'appointmentScheduledVolume') {
            return {
                title: 'Appointment Scheduled Volume by Organization',
                metricLabel: 'Appointments Scheduled',
                metricKey: 'SCHEDULING_EVENTS_HEALTHCARE',
                dataKey: 'appointmentScheduledVolume' as const,
                valueLabel: 'appointments',
                axisFormat: (value: number | string) => Number(value).toLocaleString(),
                barLabel: (value: number) => Number(value).toLocaleString(),
                emptyState: 'We could not find any appointment bookings for the selected organizations in this date range.',
                hasData: (data: PlatformSchedulingBarChartDatum[]) => data.some(item => item.appointmentScheduledVolume > 0),
                tooltipValue: (data: PlatformSchedulingBarChartDatum) => data.appointmentScheduledVolume.toLocaleString(),
                tooltipSubtitle: (data: PlatformSchedulingBarChartDatum) => `${data.organizationName} completed appointment bookings`,
                valueScale: { type: 'linear' as const, min: 0 },
                ariaLabel: 'Appointment scheduled volume by organization'
            };
        }

        return {
            title: 'Containment Rate by Organization',
            metricLabel: 'Containment Rate',
            metricKey: 'CONTAINMENT_RATE_HEALTHCARE',
            dataKey: 'containmentRate' as const,
            valueLabel: 'rate',
            axisFormat: (value: number | string) => `${value}%`,
            barLabel: (value: number) => `${Number(value).toFixed(1)}%`,
            emptyState: 'We could not find any containable conversations for the selected organizations in this date range.',
            hasData: (data: PlatformSchedulingBarChartDatum[]) => data.some(item => item.containableCallsCount > 0),
            tooltipValue: (data: PlatformSchedulingBarChartDatum) => `${data.containmentRate.toFixed(1)}%`,
            tooltipSubtitle: (data: PlatformSchedulingBarChartDatum) => `${data.containedCallsCount.toLocaleString()} contained out of ${data.containableCallsCount.toLocaleString()} containable calls`,
            valueScale: { type: 'linear' as const, min: 0, max: 100 },
            ariaLabel: 'Containment rate by organization'
        };
    }, [platformBarChartView]);

    const sortedPlatformSchedulingBarChartData = useMemo(() => {
        const sortedData = [...platformSchedulingBarChartData];
        const dataKey = platformSchedulingBarChartConfig.dataKey;

        sortedData.sort((a, b) => {
            const primaryDifference = Number(a[dataKey] ?? 0) - Number(b[dataKey] ?? 0);
            if (primaryDifference !== 0) {
                return primaryDifference;
            }

            return a.organizationName.localeCompare(b.organizationName);
        });

        return sortedData;
    }, [platformSchedulingBarChartData, platformSchedulingBarChartConfig]);

    const transfersData = React.useMemo((): TransferCategoryData[] | null => {
        const isLoadingTransferredEngagements = isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingTransferredEngagements;

        if (isLoadingTransferredEngagements) return null;

        // Get base engagements - filtered by app and optionally by Spanish
        const engagementsToProcess = filteredTransferredEngagements;

        // If no transfers found, return empty data
        if (!engagementsToProcess || engagementsToProcess.length === 0) {
            return [{ category: 'Planned' }, { category: 'Unplanned' }];
        }

        // Map sessionTags to TransferReason enum values
        // Tags in sessionTags may have slightly different names than the enum
        const tagToReasonMap: Record<string, TransferReason> = {
            // Unsupported appointment variations
            'UNSUPPORTED_APPOINTMENT': TransferReason.UNSUPPORTED_APPOINTMENT_TYPE,
            'UNSUPPORTED_APPOINTMENT_TYPE': TransferReason.UNSUPPORTED_APPOINTMENT_TYPE,

            // POS/System issues
            'POS_CONNECTION_ISSUE': TransferReason.POS_CONNECTION_ISSUE,
            'POS_ISSUE': TransferReason.POS_CONNECTION_ISSUE,
            'VFY_POS_API_FAILURE': TransferReason.POS_CONNECTION_ISSUE,

            // Payment
            'PAYMENT_LINK_REFUSED': TransferReason.PAYMENT_LINK_REFUSED,

            // Pricing
            'PRICING_ISSUE': TransferReason.PRICING_ISSUE,

            // End of call transfer variations
            'END_OF_CALL_TRANSFER': TransferReason.END_OF_CALL_TRANSFER,
            'END_OF_CALL': TransferReason.END_OF_CALL_TRANSFER,
            'VFY_CALL_END_TRANSFER': TransferReason.END_OF_CALL_TRANSFER,

            // FAQ transfer
            'FAQ_CONFIGURED_TRANSFER': TransferReason.FAQ_CONFIGURED_TRANSFER,
            'FAQ_TRANSFER': TransferReason.FAQ_CONFIGURED_TRANSFER,
            'FAQ': TransferReason.FAQ_CONFIGURED_TRANSFER,

            // Unsupported request variations
            'UNSUPPORTED_REQUEST': TransferReason.UNSUPPORTED_REQUEST,
            'UNSUPPORTED_CUSTOMER_REQUEST': TransferReason.UNSUPPORTED_REQUEST,

            // AI issues
            'AI_ISSUE': TransferReason.AI_ISSUE,

            // Off hours
            'OFF_HOURS_TRANSFER': TransferReason.OFF_HOURS_TRANSFER,
            'OFF_HOURS': TransferReason.OFF_HOURS_TRANSFER,
            'CALL_DURING_OFF_HOURS': TransferReason.OFF_HOURS_TRANSFER,

            // Customer frustrated variations (note: tag uses FRUSTRATION, enum uses FRUSTRATED)
            'CUSTOMER_FRUSTRATED': TransferReason.CUSTOMER_FRUSTRATED,
            'CUSTOMER_FRUSTRATION': TransferReason.CUSTOMER_FRUSTRATED,
            'FRUSTRATED': TransferReason.CUSTOMER_FRUSTRATED,

            // Caller requested transfer
            'CALLER_REQUESTED_TRANSFER': TransferReason.CALLER_REQUESTED_TRANSFER,
            'CALLER_REQUESTED': TransferReason.CALLER_REQUESTED_TRANSFER,

            // Immediate transfer
            'IMMEDIATE_TRANSFER': TransferReason.IMMEDIATE_TRANSFER
        };

        // Group transfers by reason and planned/unplanned, tracking English/Spanish counts
        const plannedReasons: Record<string, { count: number; apps: Record<string, { count: number; english: number; spanish: number }> }> = {};
        const unplannedReasons: Record<string, { count: number; apps: Record<string, { count: number; english: number; spanish: number }> }> = {};
        const undeterminedReasons: Record<string, { count: number; apps: Record<string, { count: number; english: number; spanish: number }> }> = {};

        engagementsToProcess.forEach(eng => {
            // Skip immediate transfers
            if (eng.sessionCategory === 'IMMEDIATE_TRANSFER' ||
                eng.sessionTags?.includes('IMMEDIATE_TRANSFER')) return;

            const appId = eng.applicationId || 'unknown';
            const isSpanish = isSpanishEngagement(eng);

            // First, try to find a transfer reason from sessionTags
            let reason: TransferReason = TransferReason.UNKNOWN;

            // Check sessionTags for known transfer reason tags
            if (eng.sessionTags && Array.isArray(eng.sessionTags)) {
                for (const tag of eng.sessionTags) {
                    if (tagToReasonMap[tag]) {
                        reason = tagToReasonMap[tag];
                        break;
                    }
                }
            }

            // If no tag found, check the explicit reason fields
            if (reason === TransferReason.UNKNOWN) {
                if (eng.plannedTransferReason && eng.plannedTransferReason.trim().length > 0) {
                    const mappedReason = tagToReasonMap[eng.plannedTransferReason.trim()];
                    reason = mappedReason || (eng.plannedTransferReason.trim() as TransferReason);
                } else if (eng.unplannedTransferReason && eng.unplannedTransferReason.trim().length > 0) {
                    const mappedReason = tagToReasonMap[eng.unplannedTransferReason.trim()];
                    reason = mappedReason || (eng.unplannedTransferReason.trim() as TransferReason);
                }
            }

            // Determine planned/unplanned using TRANSFER_REASON_IS_PLANNED mapping
            // First check explicit fields, then use reason mapping
            let isPlanned: boolean | null = null;

            if (eng.isPlannedTransfer === true) {
                isPlanned = true;
            } else if (eng.isUnplannedTransfer === true) {
                isPlanned = false;
            } else if (reason !== TransferReason.UNKNOWN && TRANSFER_REASON_IS_PLANNED[reason] !== undefined) {
                isPlanned = TRANSFER_REASON_IS_PLANNED[reason];
            }

            // Select target bucket based on planned status
            let targetReasons: Record<string, { count: number; apps: Record<string, { count: number; english: number; spanish: number }> }>;
            if (isPlanned === true) {
                targetReasons = plannedReasons;
            } else if (isPlanned === false) {
                targetReasons = unplannedReasons;
            } else {
                targetReasons = undeterminedReasons;
            }

            if (!targetReasons[reason]) {
                targetReasons[reason] = { count: 0, apps: {} };
            }
            targetReasons[reason].count++;

            if (!targetReasons[reason].apps[appId]) {
                targetReasons[reason].apps[appId] = { count: 0, english: 0, spanish: 0 };
            }
            targetReasons[reason].apps[appId].count++;
            if (isSpanish) {
                targetReasons[reason].apps[appId].spanish++;
            } else {
                targetReasons[reason].apps[appId].english++;
            }
        });

        // Build chart data structure
        const plannedCategory: TransferCategoryData = { category: 'Planned' };
        const unplannedCategory: TransferCategoryData = { category: 'Unplanned' };
        const undeterminedCategory: TransferCategoryData = { category: 'Undetermined' };

        Object.entries(plannedReasons).forEach(([reason, data]) => {
            plannedCategory[reason as TransferReason] = {
                value: data.count,
                apps: Object.entries(data.apps).map(([appId, appData]) => ({
                    appName: appNameMap?.[appId] ?? appId,
                    count: appData.count,
                    english: appData.english,
                    spanish: appData.spanish
                }))
            };
        });

        Object.entries(unplannedReasons).forEach(([reason, data]) => {
            unplannedCategory[reason as TransferReason] = {
                value: data.count,
                apps: Object.entries(data.apps).map(([appId, appData]) => ({
                    appName: appNameMap?.[appId] ?? appId,
                    count: appData.count,
                    english: appData.english,
                    spanish: appData.spanish
                }))
            };
        });

        // Add undetermined as a separate category (not merged into unplanned)
        Object.entries(undeterminedReasons).forEach(([reason, data]) => {
            undeterminedCategory[reason as TransferReason] = {
                value: data.count,
                apps: Object.entries(data.apps).map(([appId, appData]) => ({
                    appName: appNameMap?.[appId] ?? appId,
                    count: appData.count,
                    english: appData.english,
                    spanish: appData.spanish
                }))
            };
        });

        // Only include Undetermined category if it has data
        const result: TransferCategoryData[] = [plannedCategory, unplannedCategory];
        const hasUndeterminedData = Object.keys(undeterminedReasons).length > 0;
        if (hasUndeterminedData) {
            result.push(undeterminedCategory);
        }
        return result;
    }, [
        orgAnalyticsContainer.loadingTransferredEngagements,
        orgAnalyticsContainer.loadingPlatformEngagements,
        appNameMap,
        selectedAppIds,
        filteredTransferredEngagements,
        isPlatformMode
    ]);

    const transferTotals = transfersData ? calculateTransferTotals(transfersData) : { planned: 0, unplanned: 0, undetermined: 0, total: 0 };

    // Export handlers - compute filtered data directly from base records to ensure all data is exported
    const handleExportCSV = () => {
        setIsExportingCallData(true);
        setTimeout(() => {
            try {
                const baseHeaders = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Category', 'All Tags'];
                const tagHeaders = callModalTagSet.map(tag => getTagName(tag, 'HEALTHCARE'));
                const headers = [...baseHeaders, ...tagHeaders];

                // Compute filtered data directly from base records
                // Always apply category filter (NO_INTERACTION excluded by default)
                let dataToExport = callDataRecords.filter(call => {
                    const searchLower = callSearchQuery.toLowerCase();
                    const matchesSearch = !callSearchQuery ||
                        call.location.toLowerCase().includes(searchLower) ||
                        call.phoneLastFour.toLowerCase().includes(searchLower) ||
                        call.date.toLowerCase().includes(searchLower) ||
                        call.time.toLowerCase().includes(searchLower) ||
                        call.category.toLowerCase().includes(searchLower) ||
                        call.tags.some(tag => getTagName(tag, 'HEALTHCARE').toLowerCase().includes(searchLower));

                    let matchesPhone = true;
                    if (selectedPhones.size > 0) {
                        matchesPhone = phoneFilterMode === 'include'
                            ? selectedPhones.has(call.phoneLastFour)
                            : !selectedPhones.has(call.phoneLastFour);
                    }

                    const matchesLocation = selectedLocations.size === 0 || selectedLocations.has(call.location);
                    const matchesTags = selectedTags.size === 0 || (
                        tagFilterMode === 'any'
                            ? call.tags.some(tag => selectedTags.has(tag))
                            : [...selectedTags].every(tag => call.tags.includes(tag))
                    );
                    const notExcluded = excludedTags.size === 0 || !call.tags.some(tag => excludedTags.has(tag));
                    const matchesCategory = selectedCategories.size === 0 || selectedCategories.has(call.category);
                    const matchesContainment = !showNonContainedContainableOnly || isNonContainedContainableCall(call);

                    return matchesSearch && matchesPhone && matchesLocation && matchesTags && notExcluded && matchesCategory && matchesContainment;
                });
                dataToExport = sortDataRecords(dataToExport, sortConfig);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(call => {
                    const baseData: Record<string, string> = {
                        'Date': call.date,
                        'Time': call.time,
                        'Phone (Last 4)': `***${call.phoneLastFour}`,
                        'Location': call.location,
                        'Transcript Link': `${baseUrl}/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`,
                        'Category': call.category,
                        'All Tags': filterInternalVoicifyTags(call.tags)
                            .map(tag => getTagName(tag, 'HEALTHCARE'))
                            .join('; ')
                    };

                    callModalTagSet.forEach(tag => {
                        const tagDisplayName = getTagName(tag, 'HEALTHCARE');
                        baseData[tagDisplayName] = call.tags.includes(tag) ? 'Yes' : '';
                    });

                    return baseData;
                });

                const filterSuffix = isExportFiltered ? '-filtered' : '';
                const filename = `all-calls-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingCallData(false);
            }
        }, 50);
    };

    const handleExportByLocationCSV = () => {
        setIsExportingByLocation(true);
        setTimeout(() => {
            try {
                const headers = [
                    'Location',
                    'Engagements',
                    'Scheduled',
                    'Rescheduled',
                    'Cancelled',
                    'Checked Next',
                    'Inbound Transfers',
                    'Answered FAQs',
                    'No Interaction',
                    'Assistant Handled Calls',
                    'Total Managed Calls',
                    '% Supported Calls',
                    'Total Manageable Calls',
                    'Attempts',
                    'Raw Attempts',
                    'Non-Containable'
                ];

                const exportData = sortedLocationSummaryData.map(loc => ({
                    'Location': loc.location,
                    'Engagements': loc.engagements.toString(),
                    'Scheduled': loc.scheduled.toString(),
                    'Rescheduled': loc.rescheduled.toString(),
                    'Cancelled': loc.cancelled.toString(),
                    'Checked Next': loc.checkedNext.toString(),
                    'Inbound Transfers': loc.inboundTransfers.toString(),
                    'Answered FAQs': loc.answeredFaqs.toString(),
                    'No Interaction': loc.noInteraction.toString(),
                    'Assistant Handled Calls': loc.assistantHandledCalls.toString(),
                    'Total Managed Calls': loc.totalManagedCalls.toString(),
                    '% Supported Calls': `${loc.supportedCallsPercent.toFixed(1)}%`,
                    'Total Manageable Calls': loc.totalManageableCalls.toString(),
                    'Attempts': loc.attempts.toString(),
                    'Raw Attempts': loc.rawAttempts.toString(),
                    'Non-Containable': loc.nonContainable.toString()
                }));

                const filename = `calls-by-location-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingByLocation(false);
            }
        }, 50);
    };

    const handleExportOutboundByLocationCSV = () => {
        setIsExportingOutboundByLocation(true);
        setTimeout(() => {
            try {
                const headers = [
                    'Practice (Location)',
                    'Outbound Calls',
                    'Outbound Appt Confirmed',
                    'Outbound Appt Rescheduled',
                    'Outbound Appt Cancelled',
                    'Outbound Messages Left',
                    'Outbound Transferred Calls',
                    'Outbound Recalls',
                    'Outbound Recalls Booked',
                    'Outbound Missed Appointment Calls',
                    'Outbound Missed Appointment Bookings',
                    'Total Outbound Actions'
                ];

                const exportData = sortedOutboundLocationSummaryData.map(loc => ({
                    'Practice (Location)': loc.practiceLocation,
                    'Outbound Calls': loc.outboundCalls.toString(),
                    'Outbound Appt Confirmed': loc.outboundApptConfirmed.toString(),
                    'Outbound Appt Rescheduled': loc.outboundApptRescheduled.toString(),
                    'Outbound Appt Cancelled': loc.outboundApptCancelled.toString(),
                    'Outbound Messages Left': loc.outboundMessagesLeft.toString(),
                    'Outbound Transferred Calls': loc.outboundTransferredCalls.toString(),
                    'Outbound Recalls': loc.outboundRecalls.toString(),
                    'Outbound Recalls Booked': loc.outboundRecallsBooked.toString(),
                    'Outbound Missed Appointment Calls': loc.outboundMissedAppointmentCalls.toString(),
                    'Outbound Missed Appointment Bookings': loc.outboundMissedAppointmentBookings.toString(),
                    'Total Outbound Actions': loc.totalOutboundActions.toString()
                }));

                const filename = `outbound-by-location-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingOutboundByLocation(false);
            }
        }, 50);
    };

    const handleExportTransfersCSV = () => {
        setIsExportingTransfers(true);
        setTimeout(() => {
            try {
                const headers = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Transfer Type', 'Transfer Reason'];

                // Compute filtered data directly from base records
                let dataToExport = transferDataRecords;
                if (hasActiveTransferFilters) {
                    dataToExport = transferDataRecords.filter(transfer => {
                        const searchLower = transferSearchQuery.toLowerCase();
                        const matchesSearch = !transferSearchQuery ||
                            transfer.location.toLowerCase().includes(searchLower) ||
                            transfer.phoneLastFour.toLowerCase().includes(searchLower) ||
                            transfer.date.toLowerCase().includes(searchLower) ||
                            transfer.time.toLowerCase().includes(searchLower) ||
                            transfer.transferType.toLowerCase().includes(searchLower) ||
                            (TRANSFER_REASON_LABELS[transfer.transferReason] || transfer.transferReason).toLowerCase().includes(searchLower);

                        let matchesPhone = true;
                        if (selectedTransferPhones.size > 0) {
                            matchesPhone = transferPhoneFilterMode === 'include'
                                ? selectedTransferPhones.has(transfer.phoneLastFour)
                                : !selectedTransferPhones.has(transfer.phoneLastFour);
                        }

                        const matchesLocation = selectedTransferLocations.size === 0 || selectedTransferLocations.has(transfer.location);
                        const matchesType = selectedTransferType === 'all' || transfer.transferType === selectedTransferType;
                        const matchesReason = selectedTransferReasons.size === 0 || selectedTransferReasons.has(transfer.transferReason);

                        return matchesSearch && matchesPhone && matchesLocation && matchesType && matchesReason;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, transferSortConfig, true);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(transfer => ({
                    'Date': transfer.date,
                    'Time': transfer.time,
                    'Phone (Last 4)': `***${transfer.phoneLastFour}`,
                    'Location': transfer.location,
                    'Transcript Link': `${baseUrl}/v/apps/${transfer.applicationId}/analytics/sessions?sessionId=${transfer.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`,
                    'Transfer Type': transfer.transferType,
                    'Transfer Reason': (TRANSFER_REASON_LABELS[transfer.transferReason] ?? transfer.transferReason) || 'Undetermined'
                }));

                const filterSuffix = hasActiveTransferFilters ? '-filtered' : '';
                const filename = `transfers-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingTransfers(false);
            }
        }, 50);
    };

    const handleExportSchedulingEventsCSV = useCallback(() => {
        setIsExportingSchedulingEvents(true);
        setTimeout(() => {
            try {
                const baseHeaders = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Category', 'All Tags'];
                const tagHeaders = healthcareTags.map(tag => getTagName(tag, 'HEALTHCARE'));
                const headers = [...baseHeaders, ...tagHeaders];

                // Compute filtered data directly from base records
                let dataToExport = schedulingEventsCallDataRecords;
                if (hasActiveSchedulingFilters) {
                    dataToExport = schedulingEventsCallDataRecords.filter(call => {
                        const searchLower = schedulingSearchQuery.toLowerCase();
                        const matchesSearch = !schedulingSearchQuery ||
                            call.location.toLowerCase().includes(searchLower) ||
                            call.phoneLastFour.toLowerCase().includes(searchLower) ||
                            call.date.toLowerCase().includes(searchLower) ||
                            call.time.toLowerCase().includes(searchLower) ||
                            call.category.toLowerCase().includes(searchLower) ||
                            call.tags.some(tag => getTagName(tag, 'HEALTHCARE').toLowerCase().includes(searchLower));

                        let matchesPhone = true;
                        if (selectedSchedulingPhones.size > 0) {
                            matchesPhone = schedulingPhoneFilterMode === 'include'
                                ? selectedSchedulingPhones.has(call.phoneLastFour)
                                : !selectedSchedulingPhones.has(call.phoneLastFour);
                        }

                        const matchesLocation = selectedSchedulingLocations.size === 0 || selectedSchedulingLocations.has(call.location);
                        const matchesTags = selectedSchedulingTags.size === 0 || call.tags.some(tag => selectedSchedulingTags.has(tag));
                        const matchesCategory = selectedSchedulingCategories.size === 0 || selectedSchedulingCategories.has(call.category);

                        return matchesSearch && matchesPhone && matchesLocation && matchesTags && matchesCategory;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, schedulingSortConfig);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(call => {
                    const baseData: Record<string, string> = {
                        'Date': call.date,
                        'Time': call.time,
                        'Phone (Last 4)': `***${call.phoneLastFour}`,
                        'Location': call.location,
                        'Transcript Link': `${baseUrl}/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`,
                        'Category': call.category,
                        'All Tags': call.tags.map(tag => getTagName(tag, 'HEALTHCARE')).join('; ')
                    };

                    healthcareTags.forEach(tag => {
                        const tagDisplayName = getTagName(tag, 'HEALTHCARE');
                        baseData[tagDisplayName] = call.tags.includes(tag) ? 'Yes' : '';
                    });

                    return baseData;
                });

                const filterSuffix = hasActiveSchedulingFilters ? '-filtered' : '';
                const filename = `scheduling-events-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingSchedulingEvents(false);
            }
        }, 50);
    }, [schedulingEventsCallDataRecords, hasActiveSchedulingFilters, schedulingSearchQuery, selectedSchedulingPhones, schedulingPhoneFilterMode, selectedSchedulingLocations, selectedSchedulingTags, selectedSchedulingCategories, schedulingSortConfig, startDate, endDate, csvHandler]);

    const handleExportContainableCallsCSV = useCallback(() => {
        setIsExportingContainableCalls(true);
        setTimeout(() => {
            try {
                const baseHeaders = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Category', 'All Tags'];
                const tagHeaders = healthcareTags.map(tag => getTagName(tag, 'HEALTHCARE'));
                const headers = [...baseHeaders, ...tagHeaders];

                // Compute filtered data directly from base records
                let dataToExport = containableCallDataRecords;
                if (hasActiveContainableFilters) {
                    dataToExport = containableCallDataRecords.filter(call => {
                        const searchLower = containableSearchQuery.toLowerCase();
                        const matchesSearch = !containableSearchQuery ||
                            call.location.toLowerCase().includes(searchLower) ||
                            call.phoneLastFour.toLowerCase().includes(searchLower) ||
                            call.date.toLowerCase().includes(searchLower) ||
                            call.time.toLowerCase().includes(searchLower) ||
                            call.category.toLowerCase().includes(searchLower) ||
                            call.tags.some(tag => getTagName(tag, 'HEALTHCARE').toLowerCase().includes(searchLower));

                        let matchesPhone = true;
                        if (selectedContainablePhones.size > 0) {
                            matchesPhone = containablePhoneFilterMode === 'include'
                                ? selectedContainablePhones.has(call.phoneLastFour)
                                : !selectedContainablePhones.has(call.phoneLastFour);
                        }

                        const matchesLocation = selectedContainableLocations.size === 0 || selectedContainableLocations.has(call.location);
                        const matchesTags = selectedContainableTags.size === 0 || call.tags.some(tag => selectedContainableTags.has(tag));
                        const matchesCategory = selectedContainableCategories.size === 0 || selectedContainableCategories.has(call.category);

                        return matchesSearch && matchesPhone && matchesLocation && matchesTags && matchesCategory;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, containableSortConfig);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(call => {
                    const baseData: Record<string, string> = {
                        'Date': call.date,
                        'Time': call.time,
                        'Phone (Last 4)': `***${call.phoneLastFour}`,
                        'Location': call.location,
                        'Transcript Link': `${baseUrl}/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`,
                        'Category': call.category,
                        'All Tags': call.tags.map(tag => getTagName(tag, 'HEALTHCARE')).join('; ')
                    };

                    healthcareTags.forEach(tag => {
                        const tagDisplayName = getTagName(tag, 'HEALTHCARE');
                        baseData[tagDisplayName] = call.tags.includes(tag) ? 'Yes' : '';
                    });

                    return baseData;
                });

                const filterSuffix = hasActiveContainableFilters ? '-filtered' : '';
                const filename = `containable-calls-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingContainableCalls(false);
            }
        }, 50);
    }, [containableCallDataRecords, hasActiveContainableFilters, containableSearchQuery, selectedContainablePhones, containablePhoneFilterMode, selectedContainableLocations, selectedContainableTags, selectedContainableCategories, containableSortConfig, startDate, endDate, csvHandler]);

    const handleExportSpanishCallsCSV = useCallback(() => {
        setIsExportingSpanishCalls(true);
        setTimeout(() => {
            try {
                const baseHeaders = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Category', 'All Tags'];
                const tagHeaders = healthcareTags.map(tag => getTagName(tag, 'HEALTHCARE'));
                const headers = [...baseHeaders, ...tagHeaders];

                // Compute filtered data directly from base records
                let dataToExport = spanishCallDataRecords;
                if (hasActiveSpanishFilters) {
                    dataToExport = spanishCallDataRecords.filter(call => {
                        const searchLower = spanishSearchQuery.toLowerCase();
                        const matchesSearch = !spanishSearchQuery ||
                            call.location.toLowerCase().includes(searchLower) ||
                            call.phoneLastFour.toLowerCase().includes(searchLower) ||
                            call.date.toLowerCase().includes(searchLower) ||
                            call.time.toLowerCase().includes(searchLower) ||
                            call.category.toLowerCase().includes(searchLower) ||
                            call.tags.some(tag => getTagName(tag, 'HEALTHCARE').toLowerCase().includes(searchLower));

                        let matchesPhone = true;
                        if (selectedSpanishPhones.size > 0) {
                            matchesPhone = spanishPhoneFilterMode === 'include'
                                ? selectedSpanishPhones.has(call.phoneLastFour)
                                : !selectedSpanishPhones.has(call.phoneLastFour);
                        }

                        const matchesLocation = selectedSpanishLocations.size === 0 || selectedSpanishLocations.has(call.location);
                        const matchesTags = selectedSpanishTags.size === 0 || call.tags.some(tag => selectedSpanishTags.has(tag));
                        const matchesCategory = selectedSpanishCategories.size === 0 || selectedSpanishCategories.has(call.category);

                        return matchesSearch && matchesPhone && matchesLocation && matchesTags && matchesCategory;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, spanishSortConfig);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(call => {
                    const baseData: Record<string, string> = {
                        'Date': call.date,
                        'Time': call.time,
                        'Phone (Last 4)': `***${call.phoneLastFour}`,
                        'Location': call.location,
                        'Transcript Link': `${baseUrl}/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`,
                        'Category': call.category,
                        'All Tags': call.tags.map(tag => getTagName(tag, 'HEALTHCARE')).join('; ')
                    };

                    healthcareTags.forEach(tag => {
                        const tagDisplayName = getTagName(tag, 'HEALTHCARE');
                        baseData[tagDisplayName] = call.tags.includes(tag) ? 'Yes' : '';
                    });

                    return baseData;
                });

                const filterSuffix = hasActiveSpanishFilters ? '-filtered' : '';
                const filename = `spanish-calls-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingSpanishCalls(false);
            }
        }, 50);
    }, [spanishCallDataRecords, hasActiveSpanishFilters, spanishSearchQuery, selectedSpanishPhones, spanishPhoneFilterMode, selectedSpanishLocations, selectedSpanishTags, selectedSpanishCategories, spanishSortConfig, startDate, endDate, csvHandler]);

    return (
        <DashboardGrid>
            {/* Appointments Managed */}
            <ChartTile>
                <TileHeader style={{ justifyContent: 'flex-start' }}>
                    <TileHeaderButtonWrapper style={{ alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                            <TileTitle>Appointments Managed</TileTitle>
                            {isOutboundCallApp && !isSchedulingEventsLoading && schedulingEventsData !== null && (schedulingEventsData.length > 0 || hasOutboundData) && (
                                <ChartLegend style={{ padding: 0, margin: 0, justifyContent: 'flex-start' }}>
                                    <ChartLegendItem>
                                        <ChartLegendSwatch fill={chart_planned_fill} border={chart_planned_solid} />
                                        Outbound
                                    </ChartLegendItem>
                                    <ChartLegendItem>
                                        <ChartLegendSwatch fill={chart_unplanned_fill} border={chart_unplanned_solid} />
                                        Inbound
                                    </ChartLegendItem>
                                </ChartLegend>
                            )}
                        </div>
                        <ViewDetailsButton
                            data-view-export-button
                            onClick={() => setIsSchedulingEventsModalOpen(true)}
                            disabled={isButtonsDisabled}
                        >
                            View Details
                        </ViewDetailsButton>
                    </TileHeaderButtonWrapper>
                </TileHeader>
                <ChartContainer style={isMobile ? { display: 'flex', flexDirection: 'column', height: 'auto' } : undefined}>
                    {/* Appointments Managed bar chart */}
                    {(isSchedulingEventsLoading || schedulingEventsData === null) && (
                        <ChartLoadingOverlay>
                            <SecondaryLoader className="grey" />
                        </ChartLoadingOverlay>
                    )}
                    {!isSchedulingEventsLoading && schedulingEventsData !== null && schedulingEventsData.length === 0 && !hasOutboundData && (
                        <EmptyStateMessage>
                            {isTodayDateRange
                                ? "Data may still be loading for today. Check back later — tomorrow will have all of today's data."
                                : "We couldn't find any appointment managed data for the selected date range and assistants."}
                        </EmptyStateMessage>
                    )}
                    {schedulingEventsData !== null && (schedulingEventsData.length > 0 || hasOutboundData) && (
                        <>
                            {!isSchedulingEventsLoading && (
                                <ChartOverlayContainer
                                    top="-24px"
                                    compact={hasOutboundData && containableCount > 0 && Math.round((containedCount / containableCount) * 100) > 0 && displayedAppointmentResponseRate !== null}
                                >
                                    <ChartOverlayStatBlock>
                                        <ChartOverlayNumber
                                            compact={hasOutboundData && containableCount > 0 && Math.round((containedCount / containableCount) * 100) > 0 && displayedAppointmentResponseRate !== null}
                                        >
                                            {appointmentsManagedCount.toLocaleString()}
                                        </ChartOverlayNumber>
                                        <ChartOverlayLabel>
                                            Appointments Managed
                                            <MetricInfoTooltip metricKey="SCHEDULING_EVENTS_HEALTHCARE" iconSize={12} showCalculation={false} />
                                        </ChartOverlayLabel>
                                    </ChartOverlayStatBlock>
                                    {containableCount > 0 && Math.round((containedCount / containableCount) * 100) > 0 && (
                                        <ChartOverlayStatBlock>
                                            <ChartOverlayNumber
                                                compact={hasOutboundData && displayedAppointmentResponseRate !== null}
                                            >
                                                {`${Math.round((containedCount / containableCount) * 100)}%`}
                                            </ChartOverlayNumber>
                                            <ChartOverlayLabel>
                                                Containment Rate
                                                <MetricInfoTooltip metricKey="CONTAINMENT_RATE_HEALTHCARE" iconSize={12} showCalculation={true} />
                                            </ChartOverlayLabel>
                                        </ChartOverlayStatBlock>
                                    )}
                                    {hasOutboundData && (() => {
                                        // Success Rate: calls with any outbound outcome tag / (successful + not-captured calls)
                                        // Not-captured = had OUTBOUND_CALL but no outcome tag was recorded
                                        const successfulOutbound =
                                            outboundAppointmentChartMetrics.confirmed +
                                            (outboundMetrics?.rescheduled ?? 0) +
                                            (outboundMetrics?.cancelled ?? 0) +
                                            outboundLeftMessageSessionCount +
                                            (outboundMetrics?.transferred ?? 0) +
                                            (outboundMetrics?.scheduled ?? 0);
                                        const totalOutboundOutcomes = successfulOutbound + (outboundMetrics?.notCaptured ?? 0);
                                        const successRate = totalOutboundOutcomes > 0
                                            ? Math.round(successfulOutbound / totalOutboundOutcomes * 100)
                                            : 0;
                                        return (
                                            <>
                                                {/* <ChartOverlayStatBlock>
                                                    <ChartOverlayNumber
                                                        compact={containableCount > 0 && Math.round((containedCount / containableCount) * 100) > 0 && displayedAppointmentResponseRate !== null}
                                                    >{successRate}%</ChartOverlayNumber>
                                                    <ChartOverlayLabel>
                                                        Outbound Success Rate
                                                        <MetricInfoTooltip metricKey="OUTBOUND_SUCCESS_RATE_HEALTHCARE" iconSize={12} showCalculation={true} />
                                                    </ChartOverlayLabel>
                                                </ChartOverlayStatBlock> */}
                                                {isLoadingAppointmentSummary && (
                                                    <ChartOverlayStatBlock>
                                                        <AppointmentSummaryProgressRow
                                                            role="progressbar"
                                                            aria-label="Loading appointment summary"
                                                            aria-valuemin={0}
                                                            aria-valuemax={100}
                                                            aria-valuenow={appointmentSummaryProgressPercent}
                                                            title={appointmentSummaryProgressTitle}
                                                        >
                                                            <AppointmentSummaryProgressTrack aria-hidden="true">
                                                                <AppointmentSummaryProgressFill progress={appointmentSummaryProgressPercent} />
                                                            </AppointmentSummaryProgressTrack>
                                                            <AppointmentSummaryProgressValue>{appointmentSummaryProgressPercent}%</AppointmentSummaryProgressValue>
                                                        </AppointmentSummaryProgressRow>
                                                        <ChartOverlayLabel>
                                                            Loading offices
                                                        </ChartOverlayLabel>
                                                    </ChartOverlayStatBlock>
                                                )}
                                                {!isLoadingAppointmentSummary && appointmentSummaryHasFailedBatches && displayedAppointmentResponseRate === null && (
                                                    <ChartOverlayStatBlock>
                                                        <ChartOverlayNumber
                                                            compact={containableCount > 0 && Math.round((containedCount / containableCount) * 100) > 0}
                                                        >—</ChartOverlayNumber>
                                                        <ChartOverlayLabel>
                                                            Outbound Success Rate
                                                            <MetricInfoTooltip metricKey="OUTBOUND_APPOINTMENT_RESPONSE_RATE_HEALTHCARE" iconSize={12} showCalculation={true} />
                                                        </ChartOverlayLabel>
                                                        <AppointmentSummaryLoadingText
                                                            active={false}
                                                            title={appointmentSummaryStatusTitle}
                                                        >
                                                            {appointmentSummaryStatusLabel}
                                                        </AppointmentSummaryLoadingText>
                                                    </ChartOverlayStatBlock>
                                                )}
                                                {!isLoadingAppointmentSummary && displayedAppointmentResponseRate !== null && (
                                                    <AppointmentSummaryMetricButton onClick={() => setIsAppointmentSummaryModalOpen(true)}>
                                                        <ChartOverlayNumber
                                                            compact={containableCount > 0 && Math.round((containedCount / containableCount) * 100) > 0}
                                                        >{displayedAppointmentResponseRate}%</ChartOverlayNumber>
                                                        <ChartOverlayLabel>
                                                            Outbound Success Rate
                                                            <MetricInfoTooltip metricKey="OUTBOUND_APPOINTMENT_RESPONSE_RATE_HEALTHCARE" iconSize={12} showCalculation={true} />
                                                        </ChartOverlayLabel>
                                                        {(isLoadingAppointmentSummary || appointmentSummaryHasFailedBatches) && (
                                                            <AppointmentSummaryLoadingText
                                                                active={isLoadingAppointmentSummary}
                                                                title={appointmentSummaryStatusTitle}
                                                            >
                                                                {appointmentSummaryStatusLabel}
                                                            </AppointmentSummaryLoadingText>
                                                        )}
                                                    </AppointmentSummaryMetricButton>
                                                )}
                                            </>
                                        );
                                    })()}

                                </ChartOverlayContainer>
                            )}
                            {(() => {
                                const chartData = (() => {
                                    if (!isOutboundCallApp) {
                                        return schedulingEventsData.map(item => ({
                                            category: item.type,
                                            'Inbound': item.count,
                                            'Inbound_real': item.count
                                        }));
                                    }

                                    // Build the core categories independently of inbound data. Previously
                                    // these rows came only from schedulingEventsData, so outbound-only
                                    // organizations lost Scheduled, Rescheduled, and Cancelled entirely.
                                    const baseCategories = ['Schedule', 'Reschedule', 'Cancel', 'Check Appt'];
                                    const baseData = baseCategories.map(category => {
                                        const inboundItem = schedulingEventsData.find(item => item.type === category);
                                        let outboundCount = 0;
                                        switch (category) {
                                            case 'Schedule': outboundCount = outboundScheduledCount; break;
                                            case 'Reschedule': outboundCount = outboundRescheduledCount; break;
                                            case 'Cancel': outboundCount = outboundCancelledCount; break;
                                        }
                                        return {
                                            category,
                                            'Inbound': inboundItem?.count ?? 0,
                                            'Outbound': outboundCount
                                        };
                                    });

                                    if (hasOutboundData && appointmentSummaryLoadCompleted) {
                                        baseData.push({
                                            category: 'Confirmed',
                                            'Inbound': 0,
                                            'Outbound': outboundAppointmentChartMetrics.confirmed
                                        });

                                        baseData.push({
                                            category: 'Left Message',
                                            'Inbound': 0,
                                            'Outbound': outboundLeftMessageSessionCount
                                        });
                                    }

                                    const maxTotal = baseData.reduce((max, d) => Math.max(max, d.Inbound + d.Outbound), 1);

                                    return baseData.map(d => {
                                        let paddedInbound = d.Inbound;
                                        let paddedOutbound = d.Outbound;

                                        const maxLog = Math.log10(maxTotal + 1);
                                        const minLogHeight = Math.max(0.15, maxLog * 0.12);

                                        if (d.Inbound > 0) {
                                            paddedInbound = Math.max(d.Inbound, Math.pow(10, minLogHeight) - 1);
                                        }

                                        if (d.Outbound > 0) {
                                            const requiredOutbound = (paddedInbound + 1) * Math.pow(10, minLogHeight) - paddedInbound - 1;
                                            paddedOutbound = Math.max(d.Outbound, requiredOutbound);
                                        }

                                        return {
                                            category: d.category,
                                            'Inbound': d.Inbound > 0 ? paddedInbound : 0,
                                            'Outbound': d.Outbound > 0 ? paddedOutbound : 0,
                                            'Inbound_real': d.Inbound,
                                            'Outbound_real': d.Outbound
                                        };
                                    });
                                })();

                                const maxInbound = Math.max(...(schedulingEventsData?.map(s => s.count) || [0]));
                                const maxOutbound = Math.max(
                                    outboundScheduledCount,
                                    outboundAppointmentChartMetrics.confirmed,
                                    outboundLeftMessageSessionCount,
                                    outboundRescheduledCount,
                                    outboundCancelledCount
                                );
                                const maxValue = Math.max(maxInbound, maxOutbound);

                                const getSymlogTicks = () => {
                                    if (maxValue === 0) return 5;
                                    const ticks = [0];
                                    if (maxValue >= 1) ticks.push(1);
                                    if (maxValue >= 10) ticks.push(10);
                                    if (maxValue >= 100) ticks.push(100);
                                    if (maxValue >= 1000) ticks.push(1000);
                                    if (maxValue >= 10000) ticks.push(10000);
                                    if (maxValue >= 100000) ticks.push(100000);

                                    // Provide the next power of 10 for a top bounded grid line
                                    if (maxValue > 1 && maxValue < 10) ticks.push(10);
                                    if (maxValue > 10 && maxValue < 100) ticks.push(100);
                                    if (maxValue > 100 && maxValue < 1000) ticks.push(1000);
                                    if (maxValue > 1000 && maxValue < 10000) ticks.push(10000);
                                    if (maxValue > 10000 && maxValue < 100000) ticks.push(100000);

                                    return Array.from(new Set(ticks)).sort((a, b) => a - b);
                                };

                                const bar = (
                                    <ResponsiveBar
                                        data={chartData}
                                        keys={isOutboundCallApp ? ['Inbound', 'Outbound'] : ['Inbound']}
                                        indexBy="category"
                                        groupMode="stacked"
                                        margin={{ top: 30, right: 16, bottom: 24, left: 50 }}
                                        padding={0.3}
                                        valueScale={isOutboundCallApp ? { type: 'symlog' } : { type: 'linear' }}
                                        indexScale={{ type: 'band', round: true }}
                                        colors={({ id }) => id === 'Outbound' ? chart_planned_fill : (isOutboundCallApp ? chart_unplanned_fill : chart_planned_fill)}
                                        borderRadius={0}
                                        borderWidth={0}
                                        axisTop={null}
                                        axisRight={null}
                                        axisBottom={{
                                            tickSize: 0,
                                            tickPadding: 8,
                                            tickRotation: 0,
                                            tickValues: chartData.map(d => d.category),
                                            renderTick: (tick) => {
                                                const text = tick.value.length > 13 ? tick.value.substring(0, 13) + '...' : tick.value;
                                                return (
                                                    <g transform={`translate(${tick.x},${tick.y + 4})`}>
                                                        <text
                                                            textAnchor="middle"
                                                            dominantBaseline="hanging"
                                                            style={{ fontSize: 9, fill: color_shades_darker, fontWeight: 500 }}
                                                        >
                                                            {text}
                                                        </text>
                                                    </g>
                                                );
                                            }
                                        }}
                                        axisLeft={{
                                            tickSize: 0,
                                            tickPadding: 8,
                                            tickRotation: 0,
                                            legend: 'Count',
                                            legendPosition: 'middle',
                                            legendOffset: -45,
                                            tickValues: isOutboundCallApp ? getSymlogTicks() : 5,
                                            format: e => Math.floor(e) === e ? e : ''
                                        }}
                                        enableLabel={true}
                                        label={(d: any) => {
                                            const realValue = d.data[`${d.id}_real`];
                                            return realValue !== undefined ? (realValue > 0 ? realValue.toLocaleString() : '') : (d.value > 0 ? d.value.toLocaleString() : '');
                                        }}
                                        labelSkipWidth={0}
                                        labelSkipHeight={0}
                                        labelTextColor={(d: any) => {
                                            // Use darkest text for small bars so text remains readable when overflowing
                                            const realValue = d.data[`${d.id}_real`] !== undefined ? d.data[`${d.id}_real`] : d.value;
                                            return realValue < (maxValue * 0.1) ? color_shades_darkest : barChartDefaults.labelTextColor;
                                        }}
                                        animate={barChartDefaults.animate}
                                        theme={nivoChartTheme}
                                        tooltip={({ value, indexValue, id, data }: any) => {
                                            const item = schedulingEventsData?.find(d => d.type === indexValue);
                                            const apps = item?.apps || [];
                                            const isOutboundBar = id === 'Outbound';
                                            const barColor = isOutboundBar ? chart_planned_fill : (isOutboundCallApp ? chart_unplanned_fill : chart_planned_fill);
                                            const barBorderColor = isOutboundBar ? chart_planned_solid : (isOutboundCallApp ? chart_unplanned_solid : chart_planned_solid);
                                            const getSchedulingLabel = (category: string): string => {
                                                switch (category) {
                                                    case 'Schedule': return 'Bookings';
                                                    case 'Reschedule': return 'Reschedules';
                                                    case 'Cancel': return 'Cancellations';
                                                    case 'Check Appt': return 'Appointment Checks';
                                                    case 'Confirmed': return 'Confirmed Appointments';
                                                    case 'Left Message': return 'Left Messages';
                                                    default: return `${category}s`;
                                                }
                                            };
                                            const tooltipTitle = `${isOutboundCallApp ? `${id} ` : ''}${getSchedulingLabel(indexValue as string)}`;
                                            const realValue = data[`${id}_real`] !== undefined ? data[`${id}_real`] : value;
                                            const uniqueDenticonLeftMessages = outboundAppointmentChartMetrics.leftMessage;
                                            const leftMessageSubtitle = isOutboundBar && indexValue === 'Left Message' && appointmentSummaryLoadCompleted
                                                ? `${uniqueDenticonLeftMessages.toLocaleString()} unique ${uniqueDenticonLeftMessages === 1 ? 'appointment is' : 'appointments are'} recorded in Denticon. The bar counts every Voicify call session where a message was left, so one patient may be counted multiple times when Voicify makes repeated confirmation calls.`
                                                : undefined;
                                            return (
                                                <ChartTooltip
                                                    title={tooltipTitle}
                                                    value={realValue as number}
                                                    valueLabel={isOutboundBar && indexValue === 'Left Message' ? 'call sessions' : 'completed'}
                                                    subtitle={leftMessageSubtitle}
                                                    apps={isOutboundBar ? [] : apps}
                                                    showLanguageBreakdown={!isOutboundBar && isMultiLingual}
                                                    spanishOnly={isSpanishOnlyFilter}
                                                    clickHint={true}
                                                    color={barColor}
                                                    borderColor={barBorderColor}
                                                    compact={isMobile}
                                                />
                                            );
                                        }}
                                        onClick={(bar) => {
                                            const isOutboundClick = bar.id === 'Outbound';

                                            if (isOutboundClick) {
                                                let tagToMatch = '';
                                                let modalTitle = '';
                                                let summaryLabel = '';
                                                let eventLabel = '';
                                                let successDisplayName = '';
                                                let detailEventCountTags: string[] = [];

                                                switch (bar.indexValue) {
                                                    case 'Schedule':
                                                        tagToMatch = 'OUTBOUND_APPOINTMENT_BOOKED';
                                                        modalTitle = 'Outbound Scheduled Appointments';
                                                        summaryLabel = 'Scheduled';
                                                        eventLabel = 'scheduled appointment';
                                                        successDisplayName = 'Scheduled';
                                                        detailEventCountTags = ['OUTBOUND_APPOINTMENT_BOOKED', 'OUTBOUND-APPOINTMENT-BOOKED'];
                                                        break;
                                                    case 'Confirmed':
                                                        tagToMatch = TAGS.OUTBOUND_APPOINTMENT_CONFIRMED;
                                                        modalTitle = 'Confirmed Appointments';
                                                        summaryLabel = 'Confirmed';
                                                        eventLabel = 'confirmed appointment';
                                                        successDisplayName = 'Confirmed';
                                                        detailEventCountTags = [TAGS.OUTBOUND_APPOINTMENT_CONFIRMED];
                                                        break;
                                                    case 'Left Message':
                                                        tagToMatch = TAGS.OUTBOUND_LEFT_MESSAGE;
                                                        modalTitle = 'Left Messages';
                                                        summaryLabel = 'Left Message';
                                                        eventLabel = 'left message';
                                                        successDisplayName = 'Left Message';
                                                        detailEventCountTags = [];
                                                        break;
                                                    case 'Reschedule':
                                                        tagToMatch = TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED;
                                                        modalTitle = 'Outbound Reschedules';
                                                        summaryLabel = 'Reschedules';
                                                        eventLabel = 'reschedule';
                                                        successDisplayName = 'Reschedule';
                                                        detailEventCountTags = [TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED];
                                                        break;
                                                    case 'Cancel':
                                                        tagToMatch = TAGS.OUTBOUND_APPOINTMENT_CANCELLED;
                                                        modalTitle = 'Outbound Cancellations';
                                                        summaryLabel = 'Cancellations';
                                                        eventLabel = 'cancellation';
                                                        successDisplayName = 'Cancellation';
                                                        detailEventCountTags = [TAGS.OUTBOUND_APPOINTMENT_CANCELLED];
                                                        break;
                                                    default:
                                                        return; // Nothing to show for other outbound categories
                                                }

                                                let filteredRecords;
                                                if (tagToMatch === 'OUTBOUND_APPOINTMENT_BOOKED') {
                                                    const tagMatchedRecords = schedulingEventsModalRecords.filter(r =>
                                                        r.tags.some(isOutboundAppointmentBookedTag)
                                                    );
                                                    const uniqueRecords = new Map<string, CallDataRecord>();
                                                    tagMatchedRecords.forEach(record => {
                                                        uniqueRecords.set(record.sessionId || record.id, record);
                                                    });
                                                    filteredRecords = Array.from(uniqueRecords.values());
                                                } else {
                                                    filteredRecords = outboundCallDataRecords.filter(r => r.tags.includes(tagToMatch));
                                                }
                                                const usesDenticonSessionMapping =
                                                    appointmentSummary?.appointmentSessionMatches !== undefined &&
                                                    tagToMatch === TAGS.OUTBOUND_APPOINTMENT_CONFIRMED;
                                                let denticonAppointmentCountsBySession = new Map<CallDataRecord, number>();
                                                if (tagToMatch === TAGS.OUTBOUND_APPOINTMENT_CONFIRMED) {
                                                    denticonAppointmentCountsBySession = getDenticonAppointmentCountsBySession(
                                                        filteredRecords,
                                                        appointmentSummary?.appointmentSessionMatches,
                                                        'confirmed'
                                                    );
                                                    filteredRecords = getDenticonMatchedAppointmentSessions(
                                                        filteredRecords,
                                                        appointmentSummary?.appointmentSessionMatches,
                                                        'confirmed'
                                                    );
                                                } else if (tagToMatch === TAGS.OUTBOUND_LEFT_MESSAGE) {
                                                    filteredRecords = outboundLeftMessageSessionRecords;
                                                }
                                                let count = 0;
                                                switch (tagToMatch) {
                                                    case 'OUTBOUND_APPOINTMENT_BOOKED': count = outboundScheduledCount; break;
                                                    case TAGS.OUTBOUND_APPOINTMENT_CONFIRMED: count = outboundAppointmentChartMetrics.confirmed; break;
                                                    case TAGS.OUTBOUND_LEFT_MESSAGE: count = outboundLeftMessageSessionCount; break;
                                                    case TAGS.OUTBOUND_APPOINTMENT_RESCHEDULED: count = outboundMetrics?.rescheduled || 0; break;
                                                    case TAGS.OUTBOUND_APPOINTMENT_CANCELLED: count = outboundMetrics?.cancelled || 0; break;
                                                    default: count = filteredRecords.length;
                                                }

                                                // Group by app counting occurrences to match the total
                                                const appCounts: Record<string, number> = {};
                                                filteredRecords.forEach(r => {
                                                    // Denticon metrics count appointments, while the detail view lists
                                                    // unique conversations. Do not let repeated tags inflate the mapped
                                                    // per-location session totals.
                                                    let tagCount = usesDenticonSessionMapping
                                                        ? denticonAppointmentCountsBySession.get(r) || 1
                                                        : r.tags.filter(t => t === tagToMatch).length;
                                                    if (tagToMatch === 'OUTBOUND_APPOINTMENT_BOOKED') {
                                                        tagCount = r.tags.filter(isOutboundAppointmentBookedTag).length;
                                                    } else if (tagToMatch === TAGS.OUTBOUND_LEFT_MESSAGE) {
                                                        tagCount = 1;
                                                    }
                                                    appCounts[r.location] = (appCounts[r.location] || 0) + (tagCount > 0 ? tagCount : 1);
                                                });

                                                const apps = Object.entries(appCounts).map(([appName, appCount]) => ({
                                                    appName,
                                                    count: appCount
                                                }));
                                                const isLeftMessageDetail = tagToMatch === TAGS.OUTBOUND_LEFT_MESSAGE;

                                                setChartDetailTitle(modalTitle);
                                                setChartDetailValueLabel(isLeftMessageDetail ? 'call sessions' : 'conversations');
                                                setChartDetailCountPrefix('');
                                                setChartDetailSummaryLabel(summaryLabel);
                                                setChartDetailShowIndividualTotals(false);
                                                setSelectedFunnelData([{
                                                    title: summaryLabel,
                                                    value: count,
                                                    subtitle: isLeftMessageDetail
                                                        ? `${count.toLocaleString()} call session${count === 1 ? '' : 's'}`
                                                        : `${count.toLocaleString()} completed`,
                                                    apps: apps
                                                }]);

                                                setChartDetailTranscripts(callRecordsToTranscripts(filteredRecords, () => 'Success'));
                                                setShowTranscriptsTab(true);
                                                setChartDetailEventLabel(eventLabel);
                                                setChartDetailEventCountTags(detailEventCountTags);
                                                setChartDetailExportAllCategories(true);
                                                setChartDetailSuccessCategoryDisplayName(successDisplayName);
                                                setChartDetailHeaderInfo(usesDenticonSessionMapping
                                                    ? `${count.toLocaleString()} ${eventLabel}${count === 1 ? '' : 's'} across ${filteredRecords.length.toLocaleString()} conversation${filteredRecords.length === 1 ? '' : 's'}. One conversation can update multiple appointments.`
                                                    : undefined);
                                                setIsChartDetailModalOpen(true);
                                                return;
                                            }

                                            const item = schedulingEventsData?.find(d => d.type === bar.indexValue);

                                            if (item) {
                                                const apps = item.apps || [];
                                                const count = item.count;
                                                const getModalTitle = (category: string): string => {
                                                    switch (category) {
                                                        case 'Schedule': return 'Attempted Bookings';
                                                        case 'Reschedule': return 'Attempted Reschedules';
                                                        case 'Cancel': return 'Attempted Cancellations';
                                                        case 'Check Appt': return 'Attempted Appointment Checks';
                                                        default: return `Attempted ${category}s`;
                                                    }
                                                };
                                                const getSummaryLabel = (category: string): string => {
                                                    switch (category) {
                                                        case 'Schedule': return 'Bookings';
                                                        case 'Reschedule': return 'Reschedules';
                                                        case 'Cancel': return 'Cancellations';
                                                        case 'Check Appt': return 'Appointment Checks';
                                                        default: return `${category}s`;
                                                    }
                                                };
                                                const getEventLabel = (category: string): string => {
                                                    switch (category) {
                                                        case 'Schedule': return 'booking';
                                                        case 'Reschedule': return 'reschedule';
                                                        case 'Cancel': return 'cancellation';
                                                        case 'Check Appt': return 'appointment check';
                                                        default: return category.toLowerCase();
                                                    }
                                                };
                                                const getSuccessCategoryDisplayName = (category: string): string => {
                                                    switch (category) {
                                                        case 'Schedule': return 'Booking';
                                                        case 'Reschedule': return 'Reschedule';
                                                        case 'Cancel': return 'Cancellation';
                                                        case 'Check Appt': return 'Appointment Check';
                                                        default: return category;
                                                    }
                                                };
                                                const modalTitle = getModalTitle(item.type);
                                                const summaryLabel = getSummaryLabel(item.type);
                                                const eventLabel = getEventLabel(item.type);
                                                const successCategoryDisplayName = getSuccessCategoryDisplayName(item.type);
                                                setChartDetailTitle(modalTitle);
                                                setChartDetailValueLabel('conversations');
                                                setChartDetailCountPrefix('');
                                                setChartDetailSummaryLabel(summaryLabel);
                                                setChartDetailShowIndividualTotals(false);
                                                setSelectedFunnelData([{
                                                    title: summaryLabel,
                                                    value: count,
                                                    subtitle: `${count.toLocaleString()} completed`,
                                                    apps: apps.map(app => ({
                                                        appName: app.appName,
                                                        count: app.count,
                                                        english: app.english,
                                                        spanish: app.spanish
                                                    }))
                                                }]);
                                                // Map event type to both attempt and success tags
                                                const tagMap: Record<string, { attempt: string; success: string }> = {
                                                    'Schedule': { attempt: 'SCHEDULE_ATTEMPTED', success: 'ACTION_COMPLETED' },
                                                    'Reschedule': { attempt: 'RESCHEDULE_ATTEMPTED', success: 'APPOINTMENT_RESCHEDULED' },
                                                    'Cancel': { attempt: 'CANCELLATION_ATTEMPTED', success: 'APPOINTMENT_CANCELLED' },
                                                    'Check Appt': { attempt: 'CHECK_NEXT_APPOINTMENT', success: 'CHECK_NEXT_APPOINTMENT_COMPLETE' }
                                                };
                                                const tags = tagMap[item.type];
                                                // Filter records that have either the attempt OR success tag, excluding outbound calls
                                                const filteredRecords = schedulingEventsCallDataRecords.filter(r =>
                                                    tags && (r.tags.includes(tags.attempt) || r.tags.includes(tags.success)) && !r.tags.includes('OUTBOUND_CALL')
                                                );
                                                // Categorize as "Success" if they have the success tag, otherwise "Attempt"
                                                setChartDetailTranscripts(callRecordsToTranscripts(filteredRecords, (r) =>
                                                    tags && r.tags.includes(tags.success) ? 'Success' : 'Attempt'
                                                ));
                                                setShowTranscriptsTab(true);
                                                setChartDetailEventLabel(eventLabel);
                                                setChartDetailEventCountTags(tags ? [tags.success] : undefined);
                                                setChartDetailExportAllCategories(true);
                                                setChartDetailSuccessCategoryDisplayName(successCategoryDisplayName);
                                                setIsChartDetailModalOpen(true);
                                            }
                                        }}
                                    />
                                );
                                return isMobile
                                    ? <div style={{ flex: 1, minHeight: 200, width: '100%' }}>{bar}</div>
                                    : bar;
                            })()}
                        </>
                    )}
                </ChartContainer>
            </ChartTile>

            {/* Transfers */}
            <TallChartTile>
                {isPlatformMode ? (
                    <>
                        <TileHeader style={{ justifyContent: 'space-between' }}>
                            <TileHeaderButtonWrapper>
                                <TileTitle>{platformSchedulingBarChartConfig.title}</TileTitle>
                                <div style={{ position: 'relative' }}>
                                    <select
                                        value={platformBarChartView}
                                        onChange={(event) => setPlatformBarChartView(event.target.value as PlatformSchedulingBarChartView)}
                                        disabled={isMetricTilesLoading}
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: text_dark,
                                            padding: '4px 22px 4px 8px',
                                            border: '1px solid #E9EAEE',
                                            borderRadius: 6,
                                            background: 'white',
                                            cursor: isMetricTilesLoading ? 'not-allowed' : 'pointer',
                                            appearance: 'none',
                                            fontFamily: 'Muli',
                                            opacity: isMetricTilesLoading ? 0.7 : 1
                                        }}
                                    >
                                        <option value="containmentRate">Containment Rate</option>
                                        <option value="appointmentScheduledVolume">Appointment Scheduled Volume</option>
                                        <option value="totalBillableMinutes">Total Billable Minutes</option>
                                        <option value="totalOutboundCalls">Total Outbound Calls</option>
                                        <option value="totalOutboundAppointmentsConfirmed">Total Outbound Appointments Confirmed</option>
                                        <option value="totalOutboundLeftMessage">Total Outbound Left Message</option>
                                        <option value="outboundResponseRate">Outbound Response Rate</option>
                                    </select>
                                    <span style={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none',
                                        fontSize: 10,
                                        color: color_shades_darker
                                    }}>
                                        ▼
                                    </span>
                                </div>
                            </TileHeaderButtonWrapper>
                            {!isMetricTilesLoading && sortedPlatformSchedulingBarChartData.length > 0 && (
                                <TileHeaderStatsSection>
                                    <TileHeaderNumber>
                                        {sortedPlatformSchedulingBarChartData.length}
                                    </TileHeaderNumber>
                                    <TileHeaderLabel>
                                        Selected Orgs
                                        <MetricInfoTooltip metricKey={platformSchedulingBarChartConfig.metricKey} iconSize={11} />
                                    </TileHeaderLabel>
                                </TileHeaderStatsSection>
                            )}
                        </TileHeader>
                        <TallChartContainer>
                            {isMetricTilesLoading && (
                                <ChartLoadingOverlay>
                                    <SecondaryLoader className="grey" />
                                </ChartLoadingOverlay>
                            )}
                            {!isMetricTilesLoading && sortedPlatformSchedulingBarChartData.length === 0 && (
                                <EmptyStateMessage>
                                    Select at least one organization to see this chart.
                                </EmptyStateMessage>
                            )}
                            {!isMetricTilesLoading && sortedPlatformSchedulingBarChartData.length > 0 && !platformSchedulingBarChartConfig.hasData(sortedPlatformSchedulingBarChartData) && (
                                <EmptyStateMessage>
                                    {platformSchedulingBarChartConfig.emptyState}
                                </EmptyStateMessage>
                            )}
                            {sortedPlatformSchedulingBarChartData.length > 0 && platformSchedulingBarChartConfig.hasData(sortedPlatformSchedulingBarChartData) && (
                                <ResponsiveBar
                                    data={sortedPlatformSchedulingBarChartData}
                                    keys={[platformSchedulingBarChartConfig.dataKey]}
                                    indexBy="organizationLabel"
                                    layout="horizontal"
                                    margin={{ top: 12, right: 24, bottom: 36, left: 128 }}
                                    padding={barChartDefaults.padding}
                                    innerPadding={barChartDefaults.innerPadding}
                                    valueScale={platformSchedulingBarChartConfig.valueScale}
                                    indexScale={{ type: 'band', round: true }}
                                    colors={voicify_spark_dark}
                                    borderColor={voicify_spark_darker}
                                    borderWidth={barChartDefaults.borderWidth}
                                    borderRadius={barChartDefaults.borderRadius}
                                    enableGridX={barChartDefaults.enableGridX}
                                    enableGridY={barChartDefaults.enableGridY}
                                    enableLabel={true}
                                    labelSkipWidth={barChartDefaults.labelSkipWidth}
                                    labelSkipHeight={barChartDefaults.labelSkipHeight}
                                    labelTextColor={color_shades_darkest}
                                    label={(bar) => platformSchedulingBarChartConfig.barLabel(Number(bar.value))}
                                    theme={nivoChartTheme}
                                    animate={barChartDefaults.animate}
                                    motionConfig={barChartDefaults.motionConfig}
                                    axisTop={null}
                                    axisRight={null}
                                    axisBottom={{
                                        tickSize: 0,
                                        tickPadding: 10,
                                        tickRotation: 0,
                                        format: (value) => platformSchedulingBarChartConfig.axisFormat(value),
                                        legend: platformSchedulingBarChartConfig.metricLabel,
                                        legendPosition: 'middle',
                                        legendOffset: 30
                                    }}
                                    axisLeft={{
                                        tickSize: 0,
                                        tickPadding: 12,
                                        tickRotation: 0,
                                        format: (value) => {
                                            const matchingOrg = sortedPlatformSchedulingBarChartData.find(item => item.organizationLabel === value);
                                            const label = matchingOrg?.organizationName || String(value);
                                            return label.length > 20 ? `${label.slice(0, 20)}...` : label;
                                        }
                                    }}
                                    tooltip={({ data, color }) => (
                                        <ChartTooltip
                                            title={data.organizationName as string}
                                            value={platformSchedulingBarChartConfig.tooltipValue(data as PlatformSchedulingBarChartDatum)}
                                            subtitle={platformSchedulingBarChartConfig.tooltipSubtitle(data as PlatformSchedulingBarChartDatum)}
                                            valueLabel={platformSchedulingBarChartConfig.valueLabel}
                                            color={color}
                                            borderColor={voicify_spark_darker}
                                            maxWidth="280px"
                                        />
                                    )}
                                    role="application"
                                    ariaLabel={platformSchedulingBarChartConfig.ariaLabel}
                                />
                            )}
                        </TallChartContainer>
                    </>
                ) : (
                    <>
                        <TileHeader style={{ justifyContent: 'space-between' }}>
                            <TileHeaderButtonWrapper>
                                <TileTitle>Transfers</TileTitle>
                                <ViewDetailsButton
                                    data-view-export-button
                                    onClick={() => setIsTransfersModalOpen(true)}
                                    disabled={isButtonsDisabled}
                                >
                                    View Details
                                </ViewDetailsButton>
                            </TileHeaderButtonWrapper>
                            {!isTransfersLoading && transfersData !== null && (
                                <TileHeaderStatsSection>
                                    <TileHeaderNumber>
                                        {transferTotals.total.toLocaleString()}
                                    </TileHeaderNumber>
                                    <TileHeaderLabel>
                                        Excludes immediate transfers
                                    </TileHeaderLabel>
                                </TileHeaderStatsSection>
                            )}
                        </TileHeader>
                        <TallChartContainer>
                            {(isTransfersLoading || transfersData === null) && (
                                <ChartLoadingOverlay>
                                    <SecondaryLoader className="grey" />
                                </ChartLoadingOverlay>
                            )}
                            {!isTransfersLoading && transfersData !== null && transfersData.length === 0 && (
                                <EmptyStateMessage>
                                    {isSpanishOnlyFilter
                                        ? "No Spanish transfer data found for the selected date range and assistants."
                                        : (isTodayDateRange
                                            ? "Data may still be loading for today. Check back later — tomorrow will have all of today's data."
                                            : "We couldn't find any transfer data for the selected date range and assistants.")}
                                </EmptyStateMessage>
                            )}
                            {transfersData !== null && transfersData.length > 0 && (
                                <TransfersChart
                                    data={transfersData}
                                    height="100%"
                                    margin={{ top: 10, right: 50, bottom: 30, left: 80 }}
                                    showTotals={false}
                                    showLegend={false}
                                    spanishOnly={isSpanishOnlyFilter}
                                    showLanguageBreakdown={isMultiLingual}
                                    renderLegendTooltip={(category) => {
                                        if (category === 'Planned') return <MetricInfoTooltip metricKey="PLANNED_TRANSFERS_HEALTHCARE" iconSize={11} showCalculation={false} />;
                                        if (category === 'Unplanned') return <MetricInfoTooltip metricKey="UNPLANNED_TRANSFERS_HEALTHCARE" iconSize={11} showCalculation={false} />;
                                        if (category === 'Undetermined') return <MetricInfoTooltip metricKey="UNDETERMINED_TRANSFERS_HEALTHCARE" iconSize={11} showCalculation={false} />;
                                        return null;
                                    }}
                                    onBarClick={(data) => {
                                        setChartDetailTitle(`${data.reason} Transfers`);
                                        setChartDetailValueLabel('transfers');
                                        setChartDetailCountPrefix('');
                                        setChartDetailSummaryLabel(data.reason);
                                        setChartDetailShowIndividualTotals(false);
                                        setSelectedFunnelData([{
                                            title: `${data.reason}`,
                                            value: data.value,
                                            subtitle: `${data.category} transfer - ${data.value.toLocaleString()} total`,
                                            apps: data.apps.map(app => ({
                                                appName: app.appName,
                                                count: app.count
                                            }))
                                        }]);
                                        // Filter transfer records by the clicked reason and convert to transcript format
                                        const filteredTransfers = transferDataRecords.filter(t => t.transferReason === data.reasonKey);
                                        setChartDetailTranscripts(transferRecordsToTranscripts(filteredTransfers, () => `${data.category} - ${data.reason}`));
                                        setShowTranscriptsTab(true);
                                        setIsChartDetailModalOpen(true);
                                    }}
                                />
                            )}
                        </TallChartContainer>
                    </>
                )}
            </TallChartTile>

            {/* Staff Hours Saved */}
            <SmallNumberTile
                onMouseEnter={() => setHoveredTile('laborTime')}
                onMouseLeave={() => setHoveredTile(null)}
                onClick={() => {
                    if (!isMetricTilesLoading && healthcareMetrics !== null) {
                        setChartDetailTitle('Staff Hours Saved');
                        setChartDetailValueLabel('hours saved');
                        setChartDetailCountPrefix('');
                        setChartDetailSummaryLabel('Staff Hours Saved');
                        setChartDetailShowIndividualTotals(false);
                        setSelectedFunnelData([{
                            title: 'Staff Hours Saved',
                            value: healthcareMetrics.laborHoursSaved,
                            subtitle: `${formatHoursAndMinutes(healthcareMetrics.laborHoursSaved)} saved across all locations`,
                            apps: healthcareMetrics.laborHoursSavedApps?.map(app => ({
                                appName: (appNameMap && appNameMap[app.appName]) || app.appName,
                                count: Math.round(app.count),
                                english: app.english !== undefined ? Math.round(app.english) : undefined,
                                spanish: app.spanish !== undefined ? Math.round(app.spanish) : undefined
                            })) || []
                        }]);
                        setChartDetailTranscripts([]);
                        setShowTranscriptsTab(false);
                        setIsChartDetailModalOpen(true);
                    }
                }}
                style={{ cursor: isMetricTilesLoading || healthcareMetrics === null ? 'default' : 'pointer' }}
            >
                <TileHeader>
                    <TileTitle small>Staff Hours Saved</TileTitle>
                </TileHeader>
                {isMetricTilesLoading || healthcareMetrics === null ? (
                    <NumberLoadingWrapper>
                        <SecondaryLoader className="grey" />
                    </NumberLoadingWrapper>
                ) : (
                    <NumberDisplay>
                        {(() => {
                            const totalMinutes = Math.round(healthcareMetrics.laborHoursSaved * 60);
                            const hours = Math.floor(totalMinutes / 60);
                            const minutes = totalMinutes % 60;
                            return hours > 0 ? (
                                <>
                                    <BigNumber>
                                        {hours}<span style={{ fontSize: '0.5em', color: color_shades_dark, fontWeight: 400 }}> hrs</span>
                                    </BigNumber>
                                    <NumberSubtext>
                                        and {minutes} min saved
                                    </NumberSubtext>
                                </>
                            ) : (
                                <>
                                    <BigNumber>
                                        {minutes}<span style={{ fontSize: '0.5em', color: color_shades_dark, fontWeight: 400 }}> min</span>
                                    </BigNumber>
                                    <NumberSubtext>saved</NumberSubtext>
                                </>
                            );
                        })()}
                    </NumberDisplay>
                )}
                {hoveredTile === 'laborTime' && !isMetricTilesLoading && healthcareMetrics !== null && isInteractiveFeaturesReady && (
                    <ChartTooltip
                        title="Staff Hours Saved"
                        value={formatHoursAndMinutes(healthcareMetrics.laborHoursSaved)}
                        subtitle="saved"
                        apps={(healthcareMetrics.laborHoursSavedApps || []).map(app => ({
                            ...app,
                            appName: (appNameMap && appNameMap[app.appName]) || app.appName,
                            count: Math.round(app.count),
                            english: app.english !== undefined ? Math.round(app.english) : undefined,
                            spanish: app.spanish !== undefined ? Math.round(app.spanish) : undefined
                        }))}
                        valueLabel="hours"
                        showLanguageBreakdown={isMultiLingual}
                        spanishOnly={isSpanishOnlyFilter}
                        clickHint={true}
                        compact={isMobile}
                        overlay={isMobile}
                    />
                )}
            </SmallNumberTile>

            {/* After-Hours Calls */}
            <SmallNumberTile
                onMouseEnter={() => setHoveredTile('offHours')}
                onMouseLeave={() => setHoveredTile(null)}
                onClick={() => {
                    if (!isMetricTilesLoading && healthcareMetrics !== null) {
                        setChartDetailTitle(isSpanishOnlyFilter ? 'Spanish After-Hours Appointments Managed' : 'After-Hours Appointments Managed');
                        setChartDetailValueLabel('conversations');
                        setChartDetailCountPrefix('');
                        setChartDetailSummaryLabel('Appointments Managed');
                        setChartDetailShowIndividualTotals(false);
                        setSelectedFunnelData([{
                            title: 'Appointments Managed',
                            value: offHoursSchedulingEventsFromEngagements.count,
                            subtitle: `${offHoursSchedulingEventsFromEngagements.count.toLocaleString()} appointments managed completed outside business hours`,
                            apps: offHoursSchedulingEventsFromEngagements.apps.map(app => ({
                                appName: app.appName,
                                count: app.count,
                                english: app.english,
                                spanish: app.spanish
                            }))
                        }]);
                        // Use combined off hours scheduling data (includes sessionSummaries)
                        // Categorize as "Event" if they have scheduling success tags, otherwise "Attempt"
                        const schedulingSuccessTags = ['ACTION_COMPLETED', 'APPOINTMENT_RESCHEDULED', 'APPOINTMENT_CANCELLED', 'CHECK_NEXT_APPOINTMENT_COMPLETE'];
                        setChartDetailTranscripts(callRecordsToTranscripts(offHoursSchedulingCallDataRecords, (r) =>
                            r.tags.some(tag => schedulingSuccessTags.includes(tag)) ? 'Event' : 'Attempt'
                        ));
                        setShowTranscriptsTab(true);
                        setChartDetailEventLabel('appointment managed');
                        setChartDetailEventCountTags(schedulingSuccessTags);
                        setChartDetailExportAllCategories(true);
                        setChartDetailSuccessCategoryDisplayName('Appointment Managed');
                        setIsChartDetailModalOpen(true);
                    }
                }}
                style={{ cursor: isMetricTilesLoading || healthcareMetrics === null ? 'default' : 'pointer' }}
            >
                <TileHeader>
                    <TileTitle small>After-Hours Performance</TileTitle>
                </TileHeader>
                {isMetricTilesLoading || healthcareMetrics === null ? (
                    <NumberLoadingWrapper>
                        <SecondaryLoader className="grey" />
                    </NumberLoadingWrapper>
                ) : offHoursSchedulingEventsFromEngagements.count === 0 && isSpanishOnlyFilter ? (
                    <EmptyStateMessage>
                        No Spanish after-hours data found for the selected date range.
                    </EmptyStateMessage>
                ) : (
                    <NumberDisplay>
                        <BigNumber>{offHoursSchedulingEventsFromEngagements.count.toLocaleString()}</BigNumber>
                        <NumberSubtext>appointments managed</NumberSubtext>
                    </NumberDisplay>
                )}
                {hoveredTile === 'offHours' && !isMetricTilesLoading && healthcareMetrics !== null && isInteractiveFeaturesReady && (
                    <ChartTooltip
                        title="After-Hours Performance"
                        value={offHoursSchedulingEventsFromEngagements.count}
                        subtitle="completed outside business hours"
                        apps={offHoursSchedulingEventsFromEngagements.apps.map(app => ({
                            ...app,
                            appName: (appNameMap && appNameMap[app.appName]) || app.appName
                        }))}
                        valueLabel="appointments managed"
                        showLanguageBreakdown={isMultiLingual}
                        spanishOnly={isSpanishOnlyFilter}
                        clickHint={true}
                        compact={isMobile}
                        overlay={isMobile}
                    />
                )}
            </SmallNumberTile>

            {/* New Patient Appointments Booked */}
            <SmallNumberTile
                onMouseEnter={() => setHoveredTile('newPatient')}
                onMouseLeave={() => setHoveredTile(null)}
                onClick={() => {
                    if (!isMetricTilesLoading && healthcareMetrics !== null) {
                        setChartDetailTitle('New Patient Appointments Booked');
                        setChartDetailValueLabel('conversations');
                        setChartDetailCountPrefix('');
                        setChartDetailSummaryLabel('New Patient Appointments Booked');
                        setChartDetailShowIndividualTotals(false);
                        setSelectedFunnelData([{
                            title: 'New Patient Appointments Booked',
                            value: healthcareMetrics.newPatientBookings,
                            subtitle: `${healthcareMetrics.newPatientBookings.toLocaleString()} new patients scheduled`,
                            apps: healthcareMetrics?.newPatientBookingsApps?.map(app => ({
                                appName: (appNameMap && appNameMap[app.appName]) || app.appName,
                                count: app.count,
                                english: app.english,
                                spanish: app.spanish
                            })) || []
                        }]);
                        // Build transcripts from new patient records
                        // Categorize as "Booking" if they have scheduling success tags, otherwise "Attempt"
                        const schedulingSuccessTags = ['ACTION_COMPLETED', 'APPOINTMENT_RESCHEDULED', 'APPOINTMENT_CANCELLED', 'CHECK_NEXT_APPOINTMENT_COMPLETE'];
                        setChartDetailTranscripts(callRecordsToTranscripts(newPatientCallDataRecords, (r) =>
                            r.tags.some(tag => schedulingSuccessTags.includes(tag)) ? 'Booking' : 'Attempt'
                        ));
                        setShowTranscriptsTab(true);
                        setChartDetailEventLabel('booking');
                        // Count only ACTION_COMPLETED for new patient bookings (not reschedules/cancellations)
                        setChartDetailEventCountTags(['ACTION_COMPLETED']);
                        setChartDetailExportAllCategories(true);
                        setChartDetailSuccessCategoryDisplayName('Booking');
                        setIsChartDetailModalOpen(true);
                    }
                }}
                style={{ cursor: isMetricTilesLoading || healthcareMetrics === null ? 'default' : 'pointer' }}
            >
                <TileHeader>
                    <TileTitle small>New Patient Appointments Booked</TileTitle>
                </TileHeader>
                {isMetricTilesLoading || healthcareMetrics === null ? (
                    <NumberLoadingWrapper>
                        <SecondaryLoader className="grey" />
                    </NumberLoadingWrapper>
                ) : (
                    <NumberDisplay>
                        <BigNumber>{newPatientBookingsData.count.toLocaleString()}</BigNumber>
                        <NumberSubtext>bookings</NumberSubtext>
                    </NumberDisplay>
                )}
                {hoveredTile === 'newPatient' && !isMetricTilesLoading && healthcareMetrics !== null && isInteractiveFeaturesReady && (
                    <ChartTooltip
                        title="New Patient Appointments Booked"
                        value={healthcareMetrics.newPatientBookings}
                        subtitle="new patients scheduled"
                        apps={newPatientBookingsData.apps}
                        valueLabel="bookings"
                        showLanguageBreakdown={isMultiLingual}
                        spanishOnly={isSpanishOnlyFilter}
                        clickHint={true}
                        compact={isMobile}
                        overlay={isMobile}
                    />
                )}
            </SmallNumberTile>

            {/* Call Volume Histogram */}
            <WideChartTile>
                {isHistogramChartLoading && (
                    <ChartLoadingOverlay style={{ boxShadow: 'none', background: 'white' }}>
                        <SecondaryLoader className="grey" />
                    </ChartLoadingOverlay>
                )}
                {!isHistogramChartLoading && currentHistogramData !== null && currentHistogramData.length === 0 && (
                    <EmptyStateMessage>
                        {isTodayDateRange
                            ? "Data may still be loading for today. Check back later — tomorrow will have all of today's data."
                            : "We couldn't find any call volume data for the selected date range and assistants."}
                    </EmptyStateMessage>
                )}
                <HistogramChartCard
                    title={isSpanishOnlyFilter ? "Spanish Calls" : "Call Volume"}
                    titleStyles={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: text_dark,
                        margin: '0 0 2px 0',
                        letterSpacing: '-0.2px',
                        textTransform: 'none'
                    }}
                    subtitle=""
                    data={[]}
                    secondaryData={[]}
                    showSecondary={false}
                    showPrimary={false}
                    isLoading={isHistogramChartLoading}
                    graphPadding={36}
                    graphBottomPadding={16}
                    scale={0.92}
                    showViewDropdown={!isSpanishOnlyFilter}
                    selectedView={selectedView}
                    onViewChange={(view) => !isSpanishOnlyFilter && engagementHistogramContainer.setSelectedView(view as EngagementHistogramView)}
                    disableViewDropdown={isSpanishOnlyFilter || isHistogramChartLoading}
                    hideSpanishCallsOption={!isMultiLingual}
                    hideConcurrentCallsOption={!isSingleAppSelected}
                    hideOrderCallsOption={true}
                    multiLineData={currentHistogramData || []}
                    onExportClick={() => {
                        if (isInteractiveFeaturesReady) {
                            if (isSpanishOnlyFilter) {
                                setIsSpanishCallsModalOpen(true);
                            } else if (selectedView === 'Containable Conversations') {
                                setIsContainableCallsModalOpen(true);
                            } else {
                                setIsCallDataModalOpen(true);
                            }
                        }
                    }}
                    exportButtonText="View Details"
                    legendTooltips={{
                        'Containable Conversations': 'CONTAINABLE_CALLS_HEALTHCARE',
                        'Contained Conversations': 'CONTAINED_CALLS_HEALTHCARE',
                        'All Conversations': 'ALL_CALLS_HEALTHCARE',
                        'All Spanish Calls': 'ALL_CALLS_HEALTHCARE',
                        'Spanish Containable Conversations': 'CONTAINABLE_CALLS_HEALTHCARE',
                        'Spanish Contained Conversations': 'CONTAINED_CALLS_HEALTHCARE',
                        'Max Concurrent Calls': 'MAX_CONCURRENT_CALLS'
                    }}
                    legendCounts={{
                        'All Conversations': allConversationsCount
                    }}
                    viewOptionLabels={{
                        'Concurrent Calls': 'Max Concurrent Calls'
                    }}
                    renderLegendTooltip={(lineId, metricKey) => (
                        <MetricInfoTooltip metricKey={metricKey} iconSize={11} showCalculation={false} />
                    )}
                />
            </WideChartTile>

            {/* Call Data Modal */}
            <HideOnMobile>
                <ModalLayout isVisible={isCallDataModalOpen} onClose={() => { clearAllFilters(); setAllCallsActiveTab('calls'); setIsCallDataModalOpen(false); }} zIndex={1000}>
                    <CallDataModalContent>
                        <CallDataModalHeader>
                            <CallDataModalTitleSection>
                                <CallDataModalTitle>All Conversations (includes no-interaction)</CallDataModalTitle>
                                <CallDataDateRange>{moment(startDate).format('MMM D, YYYY')} - {moment(endDate).format('MMM D, YYYY')}</CallDataDateRange>
                            </CallDataModalTitleSection>
                            <CallDataModalCloseButton onClick={() => { clearAllFilters(); setAllCallsActiveTab('calls'); setIsCallDataModalOpen(false); }}>×</CallDataModalCloseButton>
                        </CallDataModalHeader>

                        <ModalTabNavigation>
                            <ModalTabButton
                                isActive={allCallsActiveTab === 'calls'}
                                onClick={() => setAllCallsActiveTab('calls')}
                            >
                                Calls
                            </ModalTabButton>
                            <ModalTabButton
                                isActive={allCallsActiveTab === 'byLocation'}
                                onClick={() => setAllCallsActiveTab('byLocation')}
                            >
                                Inbound
                            </ModalTabButton>
                            {isOutboundCallApp && (
                                <ModalTabButton
                                    isActive={allCallsActiveTab === 'outbound'}
                                    onClick={() => setAllCallsActiveTab('outbound')}
                                >
                                    Outbound
                                </ModalTabButton>
                            )}
                        </ModalTabNavigation>

                        {allCallsActiveTab === 'calls' && (
                            <ModalTabContent>
                                <SimpleFilterControlsContainer>
                                    <SimpleFilterRow>
                                        <CallSearchFilters>
                                            <SearchInputWrapper>
                                                <FilterSearchInput
                                                    type="text"
                                                    placeholder="Search all fields..."
                                                    value={callSearchQuery}
                                                    onChange={(e) => setCallSearchQuery(e.target.value)}
                                                />
                                                {callSearchQuery && <ClearSearchButton onClick={() => setCallSearchQuery('')}>×</ClearSearchButton>}
                                            </SearchInputWrapper>
                                            <ContainmentFilterPill
                                                type="button"
                                                isActive={showNonContainedContainableOnly}
                                                aria-pressed={showNonContainedContainableOnly}
                                                aria-label="Show only containable calls that were not contained"
                                                title="Show only containable calls that were not contained"
                                                onClick={() => setShowNonContainedContainableOnly(prev => !prev)}
                                            >
                                                <img src={filterIcon} alt="" aria-hidden="true" />
                                                Not contained
                                            </ContainmentFilterPill>
                                        </CallSearchFilters>
                                        <ResultsInfo style={{ margin: 0 }}>
                                            Showing <strong>{filteredCallData.length}</strong> of <strong>{callDataRecords.length}</strong> calls
                                            {hasActiveFilters && <> (filtered) <ClearFilterLink onClick={clearAllFilters}>Clear all</ClearFilterLink></>}
                                        </ResultsInfo>
                                    </SimpleFilterRow>
                                </SimpleFilterControlsContainer>

                                <CallDataScrollContainer>
                                    <CallDataTable>
                                        <thead>
                                            <tr>
                                                <FilterableColumnHeader hasActiveFilter={sortConfig?.key === 'date'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Date</ColumnHeaderText>
                                                        <SortIcon isActive={sortConfig?.key === 'date'} onClick={() => handleSort('date')} title="Sort">
                                                            {sortConfig?.key === 'date' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={sortConfig?.key === 'time'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Time</ColumnHeaderText>
                                                        <SortIcon isActive={sortConfig?.key === 'time'} onClick={() => handleSort('time')} title="Sort">
                                                            {sortConfig?.key === 'time' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={selectedPhones.size > 0 || sortConfig?.key === 'phone'} tabIndex={-1} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpenColumnFilter(null); }}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Phone (Last 4)</ColumnHeaderText>
                                                        <SortIcon isActive={sortConfig?.key === 'phone'} onClick={() => handleSort('phone')} title="Sort">
                                                            {sortConfig?.key === 'phone' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                        <ColumnFilterIcon isActive={selectedPhones.size > 0} isOpen={openColumnFilter === 'phone'} onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === 'phone' ? null : 'phone'); }} title="Filter">
                                                            <img src={filterIcon} alt="Filter" />
                                                        </ColumnFilterIcon>
                                                    </ColumnHeaderContent>
                                                    {openColumnFilter === 'phone' && (
                                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                                            <ColumnFilterHeader>
                                                                <span>Filter Phone Numbers</span>
                                                                <FilterActionsContainer>
                                                                    <ClearFilterLink onClick={() => setSelectedPhones(new Set(uniquePhones))}>Select All</ClearFilterLink>
                                                                    <ClearFilterLink onClick={() => setSelectedPhones(new Set())}>Clear</ClearFilterLink>
                                                                </FilterActionsContainer>
                                                            </ColumnFilterHeader>
                                                            <PhoneFilterSection>
                                                                <PhoneFilterModeToggle>
                                                                    <PhoneFilterModeButton isActive={phoneFilterMode === 'include'} mode="include" onClick={() => setPhoneFilterMode('include')}>Include</PhoneFilterModeButton>
                                                                    <PhoneFilterModeButton isActive={phoneFilterMode === 'exclude'} mode="exclude" onClick={() => setPhoneFilterMode('exclude')}>Exclude</PhoneFilterModeButton>
                                                                </PhoneFilterModeToggle>
                                                                {selectedPhones.size > 0 && (
                                                                    <PhoneFilterList>
                                                                        {[...selectedPhones].map(phone => (
                                                                            <PhoneFilterTag key={phone} isExclude={phoneFilterMode === 'exclude'}>
                                                                                ***{phone}
                                                                                <PhoneFilterTagRemove onClick={() => handleRemovePhoneFilter(phone)}>×</PhoneFilterTagRemove>
                                                                            </PhoneFilterTag>
                                                                        ))}
                                                                    </PhoneFilterList>
                                                                )}
                                                            </PhoneFilterSection>
                                                            <ColumnFilterContent>
                                                                {uniquePhones.map(phone => (
                                                                    <ColumnFilterItem key={phone} onClick={() => { const newSet = new Set(selectedPhones); if (newSet.has(phone)) newSet.delete(phone); else newSet.add(phone); setSelectedPhones(newSet); }}>
                                                                        <ColumnFilterCheckbox state={selectedPhones.has(phone) ? 'checked' : 'unchecked'} />
                                                                        <span>***{phone}</span>
                                                                    </ColumnFilterItem>
                                                                ))}
                                                            </ColumnFilterContent>
                                                        </ColumnFilterDropdown>
                                                    )}
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={selectedLocations.size > 0 || sortConfig?.key === 'location'} tabIndex={-1} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpenColumnFilter(null); }}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Location</ColumnHeaderText>
                                                        <SortIcon isActive={sortConfig?.key === 'location'} onClick={() => handleSort('location')} title="Sort">
                                                            {sortConfig?.key === 'location' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                        <ColumnFilterIcon isActive={selectedLocations.size > 0} isOpen={openColumnFilter === 'location'} onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === 'location' ? null : 'location'); }} title="Filter">
                                                            <img src={filterIcon} alt="Filter" />
                                                        </ColumnFilterIcon>
                                                    </ColumnHeaderContent>
                                                    {openColumnFilter === 'location' && (
                                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                                            <ColumnFilterHeader>
                                                                <span>Filter by Location</span>
                                                                <FilterActionsContainer>
                                                                    <ClearFilterLink onClick={() => setSelectedLocations(new Set(uniqueLocations))}>Select All</ClearFilterLink>
                                                                    <ClearFilterLink onClick={() => setSelectedLocations(new Set())}>Clear</ClearFilterLink>
                                                                </FilterActionsContainer>
                                                            </ColumnFilterHeader>
                                                            <ColumnFilterContent>
                                                                {uniqueLocations.map(location => (
                                                                    <ColumnFilterItem key={location} onClick={() => { const newSet = new Set(selectedLocations); if (newSet.has(location)) newSet.delete(location); else newSet.add(location); setSelectedLocations(newSet); }}>
                                                                        <ColumnFilterCheckbox state={selectedLocations.has(location) ? 'checked' : 'unchecked'} />
                                                                        <span>{location}</span>
                                                                    </ColumnFilterItem>
                                                                ))}
                                                            </ColumnFilterContent>
                                                        </ColumnFilterDropdown>
                                                    )}
                                                </FilterableColumnHeader>
                                                <CallDataTh>Transcript</CallDataTh>
                                                <FilterableColumnHeader hasActiveFilter={hasCustomCategoryFilter || sortConfig?.key === 'category'} tabIndex={-1} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpenColumnFilter(null); }}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Category</ColumnHeaderText>
                                                        <SortIcon isActive={sortConfig?.key === 'category'} onClick={() => handleSort('category')} title="Sort">
                                                            {sortConfig?.key === 'category' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                        <ColumnFilterIcon isActive={hasCustomCategoryFilter} isOpen={openColumnFilter === 'category'} onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === 'category' ? null : 'category'); }} title="Filter">
                                                            <img src={filterIcon} alt="Filter" />
                                                        </ColumnFilterIcon>
                                                    </ColumnHeaderContent>
                                                    {openColumnFilter === 'category' && (
                                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                                            <ColumnFilterHeader>
                                                                <span>Filter by Category</span>
                                                                <FilterActionsContainer>
                                                                    <ClearFilterLink onClick={() => setSelectedCategories(new Set(uniqueCallCategories))}>Select All</ClearFilterLink>
                                                                    <ClearFilterLink onClick={() => setSelectedCategories(new Set())}>Clear</ClearFilterLink>
                                                                </FilterActionsContainer>
                                                            </ColumnFilterHeader>
                                                            <ColumnFilterContent>
                                                                {uniqueCallCategories.map(category => (
                                                                    <ColumnFilterItem key={category} onClick={() => { const newSet = new Set(selectedCategories); if (newSet.has(category)) newSet.delete(category); else newSet.add(category); setSelectedCategories(newSet); }}>
                                                                        <ColumnFilterCheckbox state={selectedCategories.has(category) ? 'checked' : 'unchecked'} />
                                                                        <span>{getCategoryName(category, 'HEALTHCARE')}</span>
                                                                    </ColumnFilterItem>
                                                                ))}
                                                            </ColumnFilterContent>
                                                        </ColumnFilterDropdown>
                                                    )}
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={selectedTags.size > 0 || excludedTags.size > 0} tabIndex={-1} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpenColumnFilter(null); }} style={{ minWidth: '250px' }}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Tags</ColumnHeaderText>
                                                        <ColumnFilterIcon isActive={selectedTags.size > 0 || excludedTags.size > 0} isOpen={openColumnFilter === 'tags'} onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === 'tags' ? null : 'tags'); }} title="Filter">
                                                            <img src={filterIcon} alt="Filter" />
                                                        </ColumnFilterIcon>
                                                    </ColumnHeaderContent>
                                                    {openColumnFilter === 'tags' && (
                                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                                            <ColumnFilterHeader>
                                                                <span>Filter by Tags</span>
                                                                <FilterActionsContainer>
                                                                    <ClearFilterLink onClick={() => setSelectedTags(new Set(callModalTagSet))}>Select All</ClearFilterLink>
                                                                    <ClearFilterLink onClick={() => { setSelectedTags(new Set()); setExcludedTags(new Set()); }}>Clear</ClearFilterLink>
                                                                </FilterActionsContainer>
                                                            </ColumnFilterHeader>
                                                            <TagFilterModeToggle>
                                                                <TagFilterModeButton isActive={tagFilterMode === 'any'} onClick={() => setTagFilterMode('any')}>Any</TagFilterModeButton>
                                                                <TagFilterModeButton isActive={tagFilterMode === 'all'} onClick={() => setTagFilterMode('all')}>All</TagFilterModeButton>
                                                            </TagFilterModeToggle>
                                                            <ColumnFilterContent>
                                                                {callModalTagSet.map(tag => (
                                                                    <ColumnFilterItem key={tag} onClick={() => {
                                                                        if (excludedTags.has(tag)) {
                                                                            const newExcluded = new Set(excludedTags); newExcluded.delete(tag); setExcludedTags(newExcluded);
                                                                        } else if (selectedTags.has(tag)) {
                                                                            const newSelected = new Set(selectedTags); newSelected.delete(tag); setSelectedTags(newSelected);
                                                                            const newExcluded = new Set(excludedTags); newExcluded.add(tag); setExcludedTags(newExcluded);
                                                                        } else {
                                                                            const newSelected = new Set(selectedTags); newSelected.add(tag); setSelectedTags(newSelected);
                                                                        }
                                                                    }}>
                                                                        <ColumnFilterCheckbox state={excludedTags.has(tag) ? 'excluded' : selectedTags.has(tag) ? 'checked' : 'unchecked'} />
                                                                        <span>{getTagName(tag, 'HEALTHCARE')}</span>
                                                                    </ColumnFilterItem>
                                                                ))}
                                                            </ColumnFilterContent>
                                                        </ColumnFilterDropdown>
                                                    )}
                                                </FilterableColumnHeader>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedCallData.map((call) => (
                                                <CallDataRow key={call.id}>
                                                    <CallDataTd>{call.date}</CallDataTd>
                                                    <CallDataTd>{call.time}</CallDataTd>
                                                    <CallDataTd>***{call.phoneLastFour}</CallDataTd>
                                                    <CallDataTd><TruncatedLocationText title={call.location}>{call.location}</TruncatedLocationText></CallDataTd>
                                                    <CallDataTd>
                                                        <TranscriptLink href={`/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`} target="_blank" rel="noopener noreferrer">
                                                            View Transcript
                                                        </TranscriptLink>
                                                    </CallDataTd>
                                                    <CallDataTd>{getCategoryName(call.category, 'HEALTHCARE')}</CallDataTd>
                                                    <CallDataTd>
                                                        <DeduplicatedTagsList tags={call.tags} domain="HEALTHCARE" />
                                                    </CallDataTd>
                                                </CallDataRow>
                                            ))}
                                        </tbody>
                                    </CallDataTable>
                                    {filteredCallData.length === 0 && (
                                        <CallDataTableEmptyOverlay>
                                            No calls match your current filters.
                                        </CallDataTableEmptyOverlay>
                                    )}
                                </CallDataScrollContainer>
                                <CallDataModalFooter>
                                    {totalCallPages > 1 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px' }}>
                                            <button
                                                onClick={() => setCallDataPage(p => Math.max(0, p - 1))}
                                                disabled={callDataPage === 0}
                                                style={{ padding: '4px 8px', cursor: callDataPage === 0 ? 'not-allowed' : 'pointer', opacity: callDataPage === 0 ? 0.5 : 1 }}
                                            >
                                                ← Prev
                                            </button>
                                            <span style={{ fontSize: '13px', color: color_shades_dark }}>
                                                Page {callDataPage + 1} of {totalCallPages}
                                            </span>
                                            <button
                                                onClick={() => setCallDataPage(p => Math.min(totalCallPages - 1, p + 1))}
                                                disabled={callDataPage >= totalCallPages - 1}
                                                style={{ padding: '4px 8px', cursor: callDataPage >= totalCallPages - 1 ? 'not-allowed' : 'pointer', opacity: callDataPage >= totalCallPages - 1 ? 0.5 : 1 }}
                                            >
                                                Next →
                                            </button>
                                        </div>
                                    )}
                                    <ExportInfoText>{isExportFiltered ? `Exporting ${filteredCallData.length} filtered results` : `Exporting all ${callDataRecords.length} results`}</ExportInfoText>
                                    <ExportButton onClick={handleExportCSV} disabled={isExportingCallData || callDataRecords.length === 0}>
                                        {isExportingCallData && <ButtonSpinner />}
                                        {isExportingCallData ? 'Exporting...' : 'Export to CSV'}
                                    </ExportButton>
                                </CallDataModalFooter>
                            </ModalTabContent>
                        )}

                        {allCallsActiveTab === 'byLocation' && (
                            <ModalTabContent>
                                <CallDataScrollContainer>
                                    <CallDataTable>
                                        <thead>
                                            <tr>
                                                <StickyLocationHeader hasActiveFilter={byLocationSortConfig?.key === 'location'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Location</ColumnHeaderText>
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'location'} onClick={() => handleByLocationSort('location')} title="Sort">
                                                            {byLocationSortConfig?.key === 'location' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </StickyLocationHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'engagements'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Engagements</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="ENGAGEMENTS_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'engagements'} onClick={() => handleByLocationSort('engagements')} title="Sort">
                                                            {byLocationSortConfig?.key === 'engagements' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'scheduled'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Scheduled</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="SCHEDULED_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'scheduled'} onClick={() => handleByLocationSort('scheduled')} title="Sort">
                                                            {byLocationSortConfig?.key === 'scheduled' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'rescheduled'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Rescheduled</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="RESCHEDULED_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'rescheduled'} onClick={() => handleByLocationSort('rescheduled')} title="Sort">
                                                            {byLocationSortConfig?.key === 'rescheduled' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'cancelled'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Cancelled</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="CANCELLED_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'cancelled'} onClick={() => handleByLocationSort('cancelled')} title="Sort">
                                                            {byLocationSortConfig?.key === 'cancelled' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'checkedNext'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Checked Next</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="CHECKED_NEXT_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'checkedNext'} onClick={() => handleByLocationSort('checkedNext')} title="Sort">
                                                            {byLocationSortConfig?.key === 'checkedNext' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'inboundTransfers'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Inbound Transfers</ColumnHeaderText>
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'inboundTransfers'} onClick={() => handleByLocationSort('inboundTransfers')} title="Sort">
                                                            {byLocationSortConfig?.key === 'inboundTransfers' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'answeredFaqs'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Answered FAQs</ColumnHeaderText>
                                                        <MetricInfoTooltip
                                                            metricKey="ANSWERED_FAQS_HEALTHCARE"
                                                            customDefinition={{
                                                                name: 'Answered FAQs',
                                                                description: 'All inbound FAQs that were handled by the assistant',
                                                                calculation: 'All engagements categorized as QUESTION_ANSWERED that do not have an outbound tag.'
                                                            }}
                                                            iconSize={11}
                                                            showCalculation={false}
                                                        />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'answeredFaqs'} onClick={() => handleByLocationSort('answeredFaqs')} title="Sort">
                                                            {byLocationSortConfig?.key === 'answeredFaqs' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'noInteraction'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>No Interaction</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="NO_INTERACTION_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'noInteraction'} onClick={() => handleByLocationSort('noInteraction')} title="Sort">
                                                            {byLocationSortConfig?.key === 'noInteraction' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'assistantHandledCalls'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Assistant Handled Calls</ColumnHeaderText>
                                                        <MetricInfoTooltip
                                                            metricKey="ASSISTANT_HANDLED_CALLS_HEALTHCARE"
                                                            customDefinition={{
                                                                name: 'Assistant Handled Calls',
                                                                description: 'Inbound calls handled by the assistant.',
                                                                calculation: 'Successful bookings + successful reschedules + successful cancelations + successful check next + no interaction + answered FAQ calls.'
                                                            }}
                                                            iconSize={11}
                                                            showCalculation={true}
                                                        />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'assistantHandledCalls'} onClick={() => handleByLocationSort('assistantHandledCalls')} title="Sort">
                                                            {byLocationSortConfig?.key === 'assistantHandledCalls' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'totalManagedCalls'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Total Managed Calls</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="MANAGED_CALLS_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'totalManagedCalls'} onClick={() => handleByLocationSort('totalManagedCalls')} title="Sort">
                                                            {byLocationSortConfig?.key === 'totalManagedCalls' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'supportedCallsPercent'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>% Supported Calls</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="SUPPORTED_CALLS_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'supportedCallsPercent'} onClick={() => handleByLocationSort('supportedCallsPercent')} title="Sort">
                                                            {byLocationSortConfig?.key === 'supportedCallsPercent' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'totalManageableCalls'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Total Manageable Calls</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="MANAGEABLE_CALLS_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'totalManageableCalls'} onClick={() => handleByLocationSort('totalManageableCalls')} title="Sort">
                                                            {byLocationSortConfig?.key === 'totalManageableCalls' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'attempts'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Attempts</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="ATTEMPTS_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'attempts'} onClick={() => handleByLocationSort('attempts')} title="Sort">
                                                            {byLocationSortConfig?.key === 'attempts' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'rawAttempts'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Raw Attempts</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="RAW_ATTEMPTS_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'rawAttempts'} onClick={() => handleByLocationSort('rawAttempts')} title="Sort">
                                                            {byLocationSortConfig?.key === 'rawAttempts' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                                <FilterableColumnHeader hasActiveFilter={byLocationSortConfig?.key === 'nonContainable'}>
                                                    <ColumnHeaderContent>
                                                        <ColumnHeaderText>Non-Containable</ColumnHeaderText>
                                                        <MetricInfoTooltip metricKey="NON_CONTAINABLE_HEALTHCARE" iconSize={11} showCalculation={true} />
                                                        <SortIcon isActive={byLocationSortConfig?.key === 'nonContainable'} onClick={() => handleByLocationSort('nonContainable')} title="Sort">
                                                            {byLocationSortConfig?.key === 'nonContainable' ? (byLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                        </SortIcon>
                                                    </ColumnHeaderContent>
                                                </FilterableColumnHeader>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedLocationSummaryData.length === 0 ? (
                                                <tr>
                                                    <CallDataTd colSpan={16} style={{ textAlign: 'center', padding: '32px', color: color_shades_dark }}>
                                                        No location data available
                                                    </CallDataTd>
                                                </tr>
                                            ) : (
                                                sortedLocationSummaryData.map((loc, index) => (
                                                    <CallDataRow key={loc.location}>
                                                        <StickyLocationCell>{loc.location}</StickyLocationCell>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.engagements.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.scheduled.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.rescheduled.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.cancelled.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.checkedNext.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.inboundTransfers.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.answeredFaqs.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.noInteraction.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.assistantHandledCalls.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.totalManagedCalls.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.supportedCallsPercent.toFixed(1)}%</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.totalManageableCalls.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.attempts.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.rawAttempts.toLocaleString()}</CallDataTd>
                                                        <CallDataTd style={{ textAlign: 'right' }}>{loc.nonContainable.toLocaleString()}</CallDataTd>
                                                    </CallDataRow>
                                                ))
                                            )}
                                        </tbody>
                                    </CallDataTable>
                                </CallDataScrollContainer>
                                <CallDataModalFooter>
                                    <ExportInfoText>Exporting {sortedLocationSummaryData.length} locations</ExportInfoText>
                                    <ExportButton onClick={handleExportByLocationCSV} disabled={isExportingByLocation || sortedLocationSummaryData.length === 0}>
                                        {isExportingByLocation && <ButtonSpinner />}
                                        {isExportingByLocation ? 'Exporting...' : 'Export to CSV'}
                                    </ExportButton>
                                </CallDataModalFooter>
                            </ModalTabContent>
                        )}

                        {allCallsActiveTab === 'outbound' && isOutboundCallApp && (
                            <ModalTabContent>
                                {shouldShowOutboundMetricsWarning && (
                                    <div
                                        style={{
                                            minHeight: '420px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '24px',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <div
                                            style={{
                                                maxWidth: '720px',
                                                padding: '24px 28px',
                                                borderRadius: '14px',
                                                border: `1px solid ${voicify_spark_light}`,
                                                background: '#F4FAFD',
                                                color: color_shades_darker,
                                                fontSize: '20px',
                                                fontWeight: 600,
                                                lineHeight: 1.6
                                            }}
                                        >
                                            We&apos;re improving our outbound metrics. Forward-looking data will be available later this month.
                                        </div>
                                    </div>
                                )}
                                {!shouldShowOutboundMetricsWarning && (
                                    <>
                                        <CallDataScrollContainer>
                                            <CallDataTable>
                                                <thead>
                                                    <tr>
                                                        <StickyLocationHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'practiceLocation'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Practice (Location)</ColumnHeaderText>
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'practiceLocation'} onClick={() => handleOutboundByLocationSort('practiceLocation')} title="Sort">
                                                                    {outboundByLocationSortConfig?.key === 'practiceLocation' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                                </SortIcon>
                                                            </ColumnHeaderContent>
                                                        </StickyLocationHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundCalls'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Calls</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_CALLS_BY_LOCATION_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Calls',
                                                                        description: 'Total outbound calls placed by the assistant.',
                                                                        calculation: 'Count of engagements tagged as outbound calls.'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundCalls'} onClick={() => handleOutboundByLocationSort('outboundCalls')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundCalls' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundApptConfirmed'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Appt Confirmed</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_APPT_CONFIRMED_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Appointment Confirmed',
                                                                        description: 'Number of outbound calls where an appointment was confirmed.',
                                                                        calculation: 'Count of outbound appointment confirmation tags.'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundApptConfirmed'} onClick={() => handleOutboundByLocationSort('outboundApptConfirmed')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundApptConfirmed' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundApptRescheduled'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Appt Rescheduled</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_APPT_RESCHEDULED_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Appointment Rescheduled',
                                                                        description: 'Number of outbound calls where an appointment was rescheduled.',
                                                                        calculation: 'Count of outbound appointment reschedule tags.'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundApptRescheduled'} onClick={() => handleOutboundByLocationSort('outboundApptRescheduled')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundApptRescheduled' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundApptCancelled'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Appt Cancelled</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_APPT_CANCELLED_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Appointment Cancelled',
                                                                        description: 'Number of outbound calls where an appointment was cancelled.',
                                                                        calculation: 'Count of outbound appointment cancellation tags.'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundApptCancelled'} onClick={() => handleOutboundByLocationSort('outboundApptCancelled')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundApptCancelled' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundMessagesLeft'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Messages Left</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_MESSAGES_LEFT_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Messages Left',
                                                                        description: 'Number of outbound calls where the assistant left a message.',
                                                                        calculation: 'Count of outbound left message tags.'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundMessagesLeft'} onClick={() => handleOutboundByLocationSort('outboundMessagesLeft')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundMessagesLeft' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundTransferredCalls'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Transferred Calls</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_TRANSFERS_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Transfers',
                                                                        description: 'Number of outbounds calls that resulted in a transfer',
                                                                        calculation: 'Number of outbounds calls that resulted in a transfer'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundTransferredCalls'} onClick={() => handleOutboundByLocationSort('outboundTransferredCalls')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundTransferredCalls' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundRecalls'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Recalls</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_RECALLS_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Recalls',
                                                                        description: 'Number of outbound calls that were for a reactivation',
                                                                        calculation: 'Number of outbound calls that were for a reactivation'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundRecalls'} onClick={() => handleOutboundByLocationSort('outboundRecalls')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundRecalls' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundRecallsBooked'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Recalls Booked</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_RECALLS_BOOKED_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Recalls Booked',
                                                                        description: 'Number of reactivation calls that resulted in a booked appoint',
                                                                        calculation: 'Number of reactivation calls that resulted in a booked appoint'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundRecallsBooked'} onClick={() => handleOutboundByLocationSort('outboundRecallsBooked')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundRecallsBooked' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundMissedAppointmentCalls'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Missed Appointment Calls</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_MISSED_APPOINTMENTS_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Missed Appointments',
                                                                        description: 'Number of outbound calls that were calling back for a missed appointment',
                                                                        calculation: 'Number of outbound calls that were calling back for a missed appointment'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundMissedAppointmentCalls'} onClick={() => handleOutboundByLocationSort('outboundMissedAppointmentCalls')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundMissedAppointmentCalls' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'outboundMissedAppointmentBookings'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Outbound Missed Appointment Bookings</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="OUTBOUND_MISSED_APPOINTMENTS_BOOKED_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Outbound Missed Appointments Booked',
                                                                        description: 'Number of missed appointment calls that resulted in a booking',
                                                                        calculation: 'Number of missed appointment calls that resulted in a booking'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={false}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'outboundMissedAppointmentBookings'} onClick={() => handleOutboundByLocationSort('outboundMissedAppointmentBookings')} title="Sort">{outboundByLocationSortConfig?.key === 'outboundMissedAppointmentBookings' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                        <FilterableColumnHeader hasActiveFilter={outboundByLocationSortConfig?.key === 'totalOutboundActions'}>
                                                            <ColumnHeaderContent>
                                                                <ColumnHeaderText>Total Outbound Actions</ColumnHeaderText>
                                                                <MetricInfoTooltip
                                                                    metricKey="TOTAL_OUTBOUND_ACTIONS_HEALTHCARE"
                                                                    customDefinition={{
                                                                        name: 'Total Outbound Actions',
                                                                        description: 'Total completed outbound appointment outcomes, bookings, and messages left.',
                                                                        calculation: 'Outbound appointment confirmations + outbound appointment reschedules + outbound appointment cancellations + outbound appointment bookings + outbound messages left.'
                                                                    }}
                                                                    iconSize={11}
                                                                    showCalculation={true}
                                                                />
                                                                <SortIcon isActive={outboundByLocationSortConfig?.key === 'totalOutboundActions'} onClick={() => handleOutboundByLocationSort('totalOutboundActions')} title="Sort">{outboundByLocationSortConfig?.key === 'totalOutboundActions' ? (outboundByLocationSortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</SortIcon>
                                                            </ColumnHeaderContent>
                                                        </FilterableColumnHeader>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sortedOutboundLocationSummaryData.length === 0 ? (
                                                        <tr>
                                                            <CallDataTd colSpan={12} style={{ textAlign: 'center', padding: '32px', color: color_shades_dark }}>
                                                                No outbound location data available
                                                            </CallDataTd>
                                                        </tr>
                                                    ) : (
                                                        sortedOutboundLocationSummaryData.map((loc) => (
                                                            <CallDataRow key={loc.practiceLocation}>
                                                                <StickyLocationCell>{loc.practiceLocation}</StickyLocationCell>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundCalls.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundApptConfirmed.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundApptRescheduled.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundApptCancelled.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundMessagesLeft.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundTransferredCalls.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundRecalls.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundRecallsBooked.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundMissedAppointmentCalls.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.outboundMissedAppointmentBookings.toLocaleString()}</CallDataTd>
                                                                <CallDataTd style={{ textAlign: 'right' }}>{loc.totalOutboundActions.toLocaleString()}</CallDataTd>
                                                            </CallDataRow>
                                                        ))
                                                    )}
                                                </tbody>
                                            </CallDataTable>
                                        </CallDataScrollContainer>
                                        <CallDataModalFooter>
                                            <ExportInfoText>Exporting {sortedOutboundLocationSummaryData.length} locations</ExportInfoText>
                                            <ExportButton onClick={handleExportOutboundByLocationCSV} disabled={isExportingOutboundByLocation || sortedOutboundLocationSummaryData.length === 0}>
                                                {isExportingOutboundByLocation && <ButtonSpinner />}
                                                {isExportingOutboundByLocation ? 'Exporting...' : 'Export to CSV'}
                                            </ExportButton>
                                        </CallDataModalFooter>
                                    </>
                                )}
                            </ModalTabContent>
                        )}
                    </CallDataModalContent>
                </ModalLayout>
            </HideOnMobile>

            <AppointmentSummaryModal
                isVisible={isAppointmentSummaryModalOpen}
                onClose={() => setIsAppointmentSummaryModalOpen(false)}
                summary={appointmentSummary}
                startDate={startDate}
                endDate={endDate}
                isLoading={isLoadingAppointmentSummary}
                hasFailedBatches={appointmentSummaryHasFailedBatches}
            />

            {/* Transfers Modal */}
            <HideOnMobile>
                <TransferDataModal
                    isVisible={isTransfersModalOpen}
                    onClose={() => setIsTransfersModalOpen(false)}
                    title="Transfers"
                    startDate={startDate}
                    endDate={endDate}
                    records={transferDataRecords}
                    exportFileName="transfers"
                    spanishOnlyFilter={isSpanishOnlyFilter}
                    defaultExcludedReasons={[TransferReason.IMMEDIATE_TRANSFER]}
                />
            </HideOnMobile>

            {/* All Conversations Modal */}
            <HideOnMobile>
                <CallDataModal
                    isVisible={isSchedulingEventsModalOpen}
                    onClose={() => setIsSchedulingEventsModalOpen(false)}
                    title={isOutboundCallApp ? `Appointment Action Attempts (Inbound & Outbound)` : `Appointment Action Attempts`}
                    startDate={startDate}
                    endDate={endDate}
                    records={schedulingEventsModalRecords}
                    domain="HEALTHCARE"
                    exportFileName="scheduling-events"
                    spanishOnlyFilter={isSpanishOnlyFilter}
                    useRecordTagsOnly={true}
                />
            </HideOnMobile>

            {/* Containable Conversations Modal */}
            <HideOnMobile>
                <CallDataModal
                    isVisible={isContainableCallsModalOpen}
                    onClose={() => setIsContainableCallsModalOpen(false)}
                    title="Containable Conversations"
                    startDate={startDate}
                    endDate={endDate}
                    records={containableCallDataRecords}
                    domain="HEALTHCARE"
                    exportFileName="containable-calls"
                />
            </HideOnMobile>

            {/* Spanish Calls Modal */}
            <HideOnMobile>
                <CallDataModal
                    isVisible={isSpanishCallsModalOpen}
                    onClose={() => setIsSpanishCallsModalOpen(false)}
                    title="Spanish Calls"
                    startDate={startDate}
                    endDate={endDate}
                    records={spanishCallDataRecords}
                    domain="HEALTHCARE"
                    exportFileName="spanish-calls"
                    spanishOnlyFilter={true}
                />
            </HideOnMobile>

            {/* Chart Detail Modal */}
            <HideOnMobile>
                <ChartDetailModal
                    isVisible={isChartDetailModalOpen}
                    onClose={() => {
                        setIsChartDetailModalOpen(false);
                        setSelectedFunnelData(null);
                        setChartDetailTitle('');
                        setChartDetailValueLabel('conversations');
                        setChartDetailCountPrefix('');
                        setChartDetailSummaryLabel(undefined);
                        setChartDetailShowIndividualTotals(false);
                        setChartDetailTranscripts([]);
                        setShowTranscriptsTab(false);
                        setChartDetailEventLabel(undefined);
                        setChartDetailEventCountTags(undefined);
                        setChartDetailExportAllCategories(false);
                        setChartDetailSuccessCategoryDisplayName(undefined);
                        setChartDetailHeaderInfo(undefined);
                    }}
                    title={chartDetailTitle}
                    dateRange={{ startDate, endDate }}
                    data={selectedFunnelData}
                    valueLabel={chartDetailValueLabel}
                    countPrefix={chartDetailCountPrefix}
                    showLanguageBreakdown={isMultiLingual}
                    showPercentOfTotal={selectedEntityCount > 1}
                    summaryLabel={chartDetailSummaryLabel}
                    showIndividualTotals={chartDetailShowIndividualTotals}
                    transcriptRecords={chartDetailTranscripts}
                    showTranscriptsTab={showTranscriptsTab}
                    spanishOnly={isSpanishOnlyFilter}
                    hideSummary={true}
                    noSummaryText="Summary unavailable"
                    domain="HEALTHCARE"
                    eventLabel={chartDetailEventLabel}
                    eventCountTags={chartDetailEventCountTags}
                    exportAllCategories={chartDetailExportAllCategories}
                    successCategoryDisplayName={chartDetailSuccessCategoryDisplayName}
                    headerInfoText={chartDetailHeaderInfo}
                />
            </HideOnMobile>
        </DashboardGrid>
    );
};

// Wrap with providers
export const DentalSchedulingDashboardWithProvider: React.FC<DentalSchedulingDashboardProps> = (props) => {
    return (
        <HistogramContainer.Provider>
            <EngagementHistogramContainer.Provider>
                <DentalSchedulingDashboard {...props} />
            </EngagementHistogramContainer.Provider>
        </HistogramContainer.Provider>
    );
};

export default DentalSchedulingDashboard;
